const NF_CITY_COMMERCE = (() => {
  'use strict';

  const CONTRACT_VERSION = 'nf-city-commerce/2.0.0';
  const PRICE_CONTRACT = globalThis.NF_CITY_PRICE_CONTRACT;
  if (PRICE_CONTRACT?.contract_version !== 'nf-city-pricing/2026-09-22') {
    throw new Error('City Builder-priskontrakten mangler eller har forkert version');
  }
  const PRICE_CONTRACT_VERSION = PRICE_CONTRACT.contract_version;
  /* Det fysiske produkt er ét 22 x 22 cm kort. Alt stoerre er flere af dem i
     et sammenhaengende gitter: 44 x 44 cm er fire kort i 2 x 2, ikke én stor
     plade. Derfor er der praecis én stoerrelse, nye designs kan bestilles i.

     Varianten "44 x 44 cm" bliver staaende i butikken og valideres her, hvis
     den findes -- gamle ordrer peger paa den, og produktionen skal kunne laese
     dem. Men den er markeret ``legacy``: den vaelges aldrig til et nyt design,
     og forsvinder den, lukker checkout ikke. */
  const MODULE_SIZE_CM = 22;
  /* Modulets variantpris maa vaere én af to kendte opsaetninger, og intet
     andet -- ellers ved vi ikke, hvad kunden faktisk bliver opkraevet:

       59900  butikken som den er i dag: modulet baerer sin egen pris, og
              prisstigen laegger resten oveni. Virker kun, naar den godkendte
              total ligger over antallet gange 599 kr.
       0      baereropsaetningen: modullinjen findes for geografien og
              produktionen, og hele beloebet ligger paa prisstigen. Saadan kan
              maengdekurven opkraeves praecist ved ethvert antal.

     Et hvilket som helst andet beloeb er en fejl, ingen har besluttet, og saa
     lukker kurven. */
  const MODULE_VARIANT_PRICES_MINOR = Object.freeze([0, 59900]);
  const MAP_CONTRACT = Object.freeze({
    22: Object.freeze({ title: '22 × 22 cm', priceMinor: 59900, legacy: false }),
    44: Object.freeze({ title: '44 × 44 cm', priceMinor: 159900, legacy: true })
  });

  /* ==========================================================================
     PRISEN — ÉT STED

     Kunden koeber ét motiv fordelt paa N kort. Prisen paa selve kortene er en
     maengdekurve, ikke N gange stykprisen: den flader ud, fordi det er det
     samme motiv og den samme opsaetning, uanset hvor mange plader det bliver
     til. Kurven er Rahins, og den staar her -- ikke i UI'et, ikke i kurven og
     ikke i en Liquid-indstilling -- saa der er ét sted at rette den.

     Knaekpunkterne: 1-4 er faste beloeb, derefter en fast pris pr. ekstra kort
     i tre trin. Alt regnes i oere som heltal; kroner med decimaler i et
     regnestykke, der skal ramme en Shopify-total praecist, er en fejlkilde. */
  const MAP_BUNDLE_BASE_MINOR = Object.freeze([49900, 74900, 94900, 109900]);
  const MAP_BUNDLE_TRIN = Object.freeze([
    { tilOgMed: 16, stigningMinor: 20000 },
    { tilOgMed: 36, stigningMinor: 15000 },
    { tilOgMed: 100, stigningMinor: 10000 }
  ]);
  const MAP_BUNDLE_MAKS = 100;

  function mapBundlePriceMinor(count) {
    const antal = Number(count);
    if (!Number.isSafeInteger(antal) || antal < 1 || antal > MAP_BUNDLE_MAKS) {
      throw new Error(`Antallet af kort skal være 1-${MAP_BUNDLE_MAKS}`);
    }
    if (antal <= MAP_BUNDLE_BASE_MINOR.length) return MAP_BUNDLE_BASE_MINOR[antal - 1];
    let pris = MAP_BUNDLE_BASE_MINOR[MAP_BUNDLE_BASE_MINOR.length - 1];
    let fra = MAP_BUNDLE_BASE_MINOR.length;
    for (const trin of MAP_BUNDLE_TRIN) {
      if (antal <= fra) break;
      const iTrin = Math.min(antal, trin.tilOgMed) - fra;
      if (iTrin > 0) pris += iTrin * trin.stigningMinor;
      fra = Math.max(fra, trin.tilOgMed);
    }
    return pris;
  }

  /* Udfoerelsen er ét tilvalg paa motivet, ikke en egenskab ved den enkelte
     plade: kunden vaelger farve ét sted og faar det paa hele opsaetningen.
     Derfor ganges tillaegget ikke med antallet af kort. */
  const MAP_STYLE_SURCHARGE_MINOR = Object.freeze({ hvid: 0, farve: 12900 });

  /* Referencerammen: Standard/Glat om ét 22 x 22 cm kort. Beloebet er Rahins
     og gaelder pr. fysisk kort. Rammemotorens egen formel bruges fortsat --
     men til at regne forskellen mellem denne reference og et dyrere udtryk,
     ikke til at saette grundprisen. */
  const FRAME_REFERENCE_MINOR = 19900;

  function normalizeVariantTitle(value) {
    return String(value || '').normalize('NFKC').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
  }

  /* Varianttitlen er ikke en streng, vi ejer -- den skrives i Shopify Admin, og
     der er ingen der garanterer, at gangetegnet bliver det samme. Butikken har
     i dag "22 × 22 cm" med U+00D7, mens storefrontens JSON kan komme igennem
     som "22 x 22 cm" med et almindeligt x. En streng-sammenligning ville lade
     de to vaere forskellige stoerrelser, og saa kan bykortet pludselig ikke
     bestilles, fordi nogen skrev et bogstav.

     Derfor sammenlignes maalene som tal. Alt der laeser som "22 x 22 cm" er
     22 x 22 cm -- uanset hvilket gangetegn, hvilken afstand og hvilket
     bogstavstore der staar imellem. */
  const GANGETEGN = /[x\u00d7\u2715\u2716\u00b7\u2217*]/i;
  function parseVariantDimension(value) {
    const tekst = normalizeVariantTitle(value);
    const traef = tekst.match(
      new RegExp(`^(\\d+(?:[.,]\\d+)?)\\s*${GANGETEGN.source}\\s*(\\d+(?:[.,]\\d+)?)\\s*cm$`, 'i')
    );
    if (!traef) return null;
    const bredde = Number(String(traef[1]).replace(',', '.'));
    const hoejde = Number(String(traef[2]).replace(',', '.'));
    if (!Number.isFinite(bredde) || !Number.isFinite(hoejde)) return null;
    if (bredde <= 0 || hoejde <= 0) return null;
    return { bredde, hoejde };
  }

  /* Kvadratiske kort er de eneste, City Builder saelger. Er varianten ikke
     kvadratisk, hoerer den ikke til modellen, og saa siger vi ikke noget om
     den -- i stedet for at gaette hvilken side der var maalet. */
  function variantSizeCm(value) {
    const maal = parseVariantDimension(value);
    if (!maal || maal.bredde !== maal.hoejde) return null;
    return maal.bredde;
  }

  function resolveProductVariants(variants) {
    const matches = new Map(Object.keys(MAP_CONTRACT).map(size => [Number(size), []]));
    for (const variant of Array.isArray(variants) ? variants : []) {
      const size = variantSizeCm(variant?.title);
      if (size !== null && matches.has(size)) matches.get(size).push(variant);
    }

    const resolved = {};
    const errors = [];
    for (const [sizeText, contract] of Object.entries(MAP_CONTRACT)) {
      const size = Number(sizeText);
      const candidates = matches.get(size);
      if (candidates.length !== 1) {
        errors.push(candidates.length ? `${contract.title} findes flere gange` : `${contract.title} mangler`);
        continue;
      }
      const variant = candidates[0];
      const priceMinor = Number(variant.price);
      if (variant.available === false) errors.push(`${contract.title} er ikke tilgængelig`);
      if (!variant.id) errors.push(`${contract.title} mangler variant-ID`);
      const godkendtePriser = size === MODULE_SIZE_CM
        ? MODULE_VARIANT_PRICES_MINOR : [contract.priceMinor];
      if (!Number.isSafeInteger(priceMinor) || priceMinor < 0) errors.push(`${contract.title} har ugyldig pris`);
      else if (!godkendtePriser.includes(priceMinor)) {
        errors.push(`${contract.title} matcher ikke en godkendt pris `
          + `(${godkendtePriser.map(value => value / 100).join(' eller ')} DKK)`);
      }
      if (!errors.some(message => message.startsWith(contract.title))) {
        resolved[size] = { id: variant.id, priceMinor, title: contract.title };
      }
    }
    /* Kun modulet spaerrer checkout. En arvet variant, der er fjernet eller
       omprissat, er ikke kundens problem -- den bruges ikke til nye designs. */
    const legacyOnly = errors.every(message => Object.values(MAP_CONTRACT)
      .some(contract => contract.legacy && message.startsWith(contract.title)));
    return {
      ok: Boolean(resolved[MODULE_SIZE_CM]) && legacyOnly,
      resolved,
      errors,
      blockingErrors: errors.filter(message => !Object.values(MAP_CONTRACT)
        .some(contract => contract.legacy && message.startsWith(contract.title)))
    };
  }

  return Object.freeze({
    CONTRACT_VERSION, PRICE_CONTRACT_VERSION, PRICE_CONTRACT, MODULE_SIZE_CM, MODULE_VARIANT_PRICES_MINOR,
    MAP_CONTRACT, normalizeVariantTitle, parseVariantDimension, variantSizeCm,
    resolveProductVariants,
    MAP_BUNDLE_MAKS, MAP_STYLE_SURCHARGE_MINOR, FRAME_REFERENCE_MINOR, mapBundlePriceMinor
  });
})();

(() => {
  'use strict';

  const root = document.querySelector('[data-nf-city-builder]');
  if (!root) return;

  const $ = selector => root.querySelector(selector);
  const $$ = selector => [...root.querySelectorAll(selector)];
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const money = value => `${Number(value).toLocaleString('da-DK')} kr.`;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, Number(value)));

  const sizeComparisonDialog = $('[data-size-comparison-dialog]');
  const sizeComparisonOpen = $('[data-size-comparison-open]');
  const sizeComparisonClose = $('[data-size-comparison-close]');
  let sizeComparisonReturnFocus = null;

  function openSizeComparison() {
    if (!sizeComparisonDialog) return;
    sizeComparisonReturnFocus = document.activeElement;
    if (typeof sizeComparisonDialog.showModal === 'function') sizeComparisonDialog.showModal();
    else sizeComparisonDialog.setAttribute('open', '');
  }

  function closeSizeComparison() {
    if (!sizeComparisonDialog?.hasAttribute('open')) return;
    if (typeof sizeComparisonDialog.close === 'function') sizeComparisonDialog.close();
    else {
      sizeComparisonDialog.removeAttribute('open');
      sizeComparisonReturnFocus?.focus?.({ preventScroll: true });
    }
  }

  sizeComparisonOpen?.addEventListener('click', openSizeComparison);
  sizeComparisonClose?.addEventListener('click', closeSizeComparison);
  sizeComparisonDialog?.addEventListener('click', event => {
    if (event.target === sizeComparisonDialog) closeSizeComparison();
  });
  sizeComparisonDialog?.addEventListener('close', () => {
    sizeComparisonReturnFocus?.focus?.({ preventScroll: true });
  });
  sizeComparisonDialog?.addEventListener('keydown', event => {
    if (event.key === 'Escape' && typeof sizeComparisonDialog.close !== 'function') {
      event.preventDefault();
      closeSizeComparison();
    }
  });
  /* Produktionen bruger fortsat 22 cm-moduler. Kunden ser kun de to færdige
     formater; 44 cm mappes internt til 2 x 2 uden at udstille feltlogikken. */
  const MODUL_CM = NF_CITY_COMMERCE.MODULE_SIZE_CM;
  const publicSize = value => Number(value) === MODUL_CM;
  const FIXED_MODULE_COVERAGE_M = 1400;
  const PUBLIC_SIZE_CHOICES = Object.freeze({
    22: Object.freeze({ sizeCm: 22, areaM: FIXED_MODULE_COVERAGE_M, columns: 1, rows: 1 }),
    44: Object.freeze({ sizeCm: 44, areaM: FIXED_MODULE_COVERAGE_M, columns: 2, rows: 2 })
  });
  const productSizeFor = value => {
    const panorama = value?.panorama || {};
    return Number(panorama.columns) === 2 && Number(panorama.rows) === 2 ? 44 : 22;
  };
  /* Gamle designs og ordrer baerer stadig 44. De skal kunne laeses, men aldrig
     skrives. */
  const LEGACY_SIZE_CM = Object.freeze([44]);
  const legacySize = value => LEGACY_SIZE_CM.includes(Number(value));

  /* Panorama: ét sammenhaengende omraade sat sammen af flere individuelle
     22 x 22 cm moduler. Et modul daekker fortsat ``areaM`` meter paa hver led
     -- det er dét, ét kort viser -- saa 1 x 1 er ordret det, siden altid har
     lavet: samme udsnit, samme kortdata, samme ordre. Er der flere moduler,
     vokser omraadet med hele moduler ad gangen, og forholdet mellem siderne
     bliver columns/rows af sig selv. */
  const PANORAMA_MIN = 1;
  const PANORAMA_MAX = 10;
  const PANORAMA_SCHEMA = 'nf-city-panorama/1.0.0';
  const internalProduction = root.dataset.productionCenter === 'true';
  const designApiBase = String(root.dataset.designApiUrl || '').trim();
  const READINESS_SCHEMA = 'nf-frame-surface-readiness/1.0.0';
  const surfaceReadiness = {
    valid: false,
    pattern: new Set(),
    proMaterial: new Set(),
    reason: 'Production-readiness er ikke valideret endnu.'
  };

  function newRequestId() {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = [...bytes].map(value => value.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  /* Ingen prisfallback: Shopify-varianten er autoritativ, og checkout forbliver
     lukket, indtil modulets variant findes til den godkendte pris. */
  let prices = { [MODUL_CM]: null };
  let pricesMinor = { [MODUL_CM]: null };
  const varianter = {};                     // stoerrelse i cm -> variant-id
  let commerceReadinessError = '';
  /* Hvid er grundudfoerelsen og koster ingenting. Farve er et tilvalg paa
     motivet -- se MAP_STYLE_SURCHARGE_MINOR. Raekkefoelgen her er den, kunden
     moeder. */
  const styleNames = { hvid: 'Hvid', farve: 'Farve' };
  /* Farve er City Builderens standardvalg. De seks faste bykort har deres
     egen storefront-guard og forbliver Hvid-only. */
  const STANDARD_STIL = 'farve';
  const MAP_PALETTE = Object.freeze({
    ground: '#FFFFFF',
    road: '#FFFFFF',
    building: '#E8DBB7',
    water: '#A3D8E1',
    green: '#61C680'
  });
  /* Hvid er et egentligt monokromt produktvalg. Alle kortlag er derfor hvide;
     relief, lys, skygger og materialernes ruhed bevarer stadig den fysiske
     dybde. Farve-paletten ovenfor er uændret. */
  const WHITE_MAP_PALETTE = Object.freeze({
    ground: '#FFFFFF', road: '#FFFFFF', building: '#FFFFFF',
    water: '#FFFFFF', green: '#FFFFFF'
  });
  const previewMapPalette = () => state.style === 'hvid' ? WHITE_MAP_PALETTE : MAP_PALETTE;
  const previewLayerDefaults = new WeakMap();
  const frameNames = { ingen: 'Uden ramme', med: 'Med ramme' };

  /* Det godkendte udsnit -- det previewet faktisk viser. Kunden kan flytte og
     dreje firkanten frit; foerst naar hun trykker "Vælg kort", flytter det sig
     herind, og foerst da hentes kortdata. To vaerdier, fordi de to ting ikke er
     det samme: hvad kunden er ved at vaelge, og hvad hun har set. */
  let godkendt = null;

  /* Rammens tilstand hoerer til bykortet og bor derfor her. baseColor og
     overflade er bevidst adskilt: et Pro Material erstatter farven paa
     skaermen, men maa ikke slette kundens farvevalg -- gaar hun tilbage til
     Glat eller Moenster, skal det staa der endnu. Moenster og Pro Material
     huskes hver for sig af samme grund. */
  const ramme = {
    farve: 'sort',
    // City Builder bruger altid Standard internt; kunden skal ikke vælge en
    // teknisk rammeprofil i dette flow.
    stil: 'standard',
    overflade: 'solid',
    /* Klassisk er fast ensfarvet. Pro kan stadig vælges som den særskilte,
       eksisterende materialefamilie. */
    klassiskOverflade: 'solid',
    moenster: '',
    proMateriale: '',
    /* Hvor kunden staar i materialebrowseren. Hver overflade husker sin egen
       kategori, saa et skift frem og tilbage ikke smider stedet vaek. */
    moensterKategori: '',
    proKategori: '',
    proSoegning: '',
    browserAaben: false
  };

  /* Bykortet bruger altid billedrammeprofilen og ingen bagplade. Vaerdierne er
     produktionens og staar her, fordi de skal med paa ordren -- ikke fordi
     kunden skal tage stilling til dem. */
  const RAMME_PROFIL = 'picture-frame';
  const RAMME_BAGPLADE = 'none';

  const PRIS_PRODUKTER = [
    'specialramme', 'specialramme-251-500-kr',
    'specialramme-501-750-kr', 'specialramme-751-1000-kr'
  ];
  let prisvarianter = [];

  const motor = () => window.nfRammeMotor;

  let state = {
    place: 'København',
    lat: 55.6786,
    lon: 12.5919,
    areaM: FIXED_MODULE_COVERAGE_M,
    style: STANDARD_STIL,
    frame: 'med',
    mapMode: '2d',
    rotationDeg: 0,
    panorama: { columns: 1, rows: 1 }
  };

  const FUNNEL_VERSION = 'nf-city-funnel/1.0.0';
  let cropSelectionTracked = false;

  /* Shopifys Customer Events er den eksisterende integrationsflade. Vi
     publicerer kun aggregerede valg og aldrig sted, koordinater, bbox,
     design-/konfigurations-ID eller kundedata. Uden en tilkoblet pixel er
     kaldet bevidst en no-op; temaet maa ikke indfoere en ny tracker. */
  function publishFunnelEvent(name, extra = {}) {
    if (internalProduction) return;
    const aggregate = {};
    const sources = new Set([
      'search_result', 'coordinates', 'current_view', 'center',
      'move', 'resize', 'rotate', 'keyboard_move', 'keyboard_rotate'
    ]);
    if (sources.has(extra.source)) aggregate.source = extra.source;
    if (['HIT', 'MISS', 'STALE', 'LOCAL', 'UNKNOWN'].includes(extra.cache)) aggregate.cache = extra.cache;
    if (extra.reason === 'map_data_unavailable') aggregate.reason = extra.reason;
    if (typeof extra.preview_ready === 'boolean') aggregate.preview_ready = extra.preview_ready;
    const payload = Object.freeze({
      funnel_version: FUNNEL_VERSION,
      size_cm: productSizeFor(state),
      frame: state.frame,
      surface: state.frame === 'med' ? ramme.overflade : 'none',
      ...aggregate
    });
    const detail = Object.freeze({ name, payload });
    if (localCartHarness) {
      root.__nfCityFunnelEvents.push(detail);
      root.dataset.funnelDebug = JSON.stringify(root.__nfCityFunnelEvents);
    }
    root.dispatchEvent(new CustomEvent('nf:city-funnel', { detail }));
    try {
      const publication = window.Shopify?.analytics?.publish?.(name, payload);
      publication?.catch?.(() => {});
    } catch (error) {
      // Analytics maa aldrig kunne blokere konfiguratoren.
    }
  }

  function publishCropSelected(source) {
    if (cropSelectionTracked) return;
    cropSelectionTracked = true;
    publishFunnelEvent('nf_city_crop_selected', { source });
  }

  let map = null;
  let mapReady = false;
  let resultMap = null;
  let resultMapReady = false;
  let productCaptureMap = null;
  let productCaptureReady = null;
  let productCaptureRequest = 0;
  let productCaptureFingerprint = '';
  let resultPreviewFrame = 0;
  let resultSnapshotPending = false;
  let last3dCamera = { pitch: 54, bearing: -18 };
  let selectionLocked = false;
  let selectionToolMode = 'move';
  let selectionDrag = null;
  let selectionInteractionsBound = false;
  let ignoreMapSync = 0;
  const CROP_VIEW_FRACTION = 0.58;
  let approvedDesign = null;
  let approvedDesignFingerprint = '';
  let currentDesignId = '';
  let currentDesignRefs = [];
  /* Ét verificerbart design-record pr. fysisk modul. Configuration-ID'et
     binder dem til samme motiv; V3 kan derfor sende alle fire referencer på
     én Shopify-linje uden at miste modulernes egne identiteter. */
  let panoramaGroupId = '';
  let currentDesignFingerprint = '';
  let designRequestId = newRequestId();
  let designSavePromise = null;
  const localCartHarness = root.dataset.localCartHarness === 'true';
  if (root.hasAttribute('data-local-cart-harness')) root.dataset.cartHarnessReady = String(localCartHarness);
  if (localCartHarness) {
    root.__nfCityFunnelEvents = [];
    root.dataset.funnelDebug = '[]';
    root.__nfCityCartDebug = {
      approvedSnapshot: null,
      lastCartPayload: null,
      lastCartError: ''
    };
    root.dataset.cartDebug = JSON.stringify(root.__nfCityCartDebug);
  }
  const syncLocalCartDebug = () => {
    if (localCartHarness) root.dataset.cartDebug = JSON.stringify(root.__nfCityCartDebug);
  };
  /* Bygninger og tekstur er det eneste i previewet, der kommer fra kortet.
     Plade, maalestok, udfoerelse og ramme kommer fra kundens valg og har
     ingenting med kortets kamera at goere. Var hele udsendelsen spaerret bag
     resultMapReady, ville et skift af stoerrelse, udfoerelse eller ramme
     foerst naa previewet, naar kortet tilfaeldigvis blev idle igen -- og
     aldrig, hvis kortet ikke blev klar. Den sidst indsamlede geometri holdes
     derfor her, saa valgene kan opdatere previewet alene. */
  let productGeometry = { buildings: [], textureDataUrl: '' };

  function normalize(next) {
    const clean = { ...state, ...next };
    clean.lat = clamp(clean.lat, -85, 85);
    clean.lon = clamp(clean.lon, -180, 180);
    /* Dækningen følger produktformatet og kan ikke længere ændres med zoom. */
    clean.areaM = FIXED_MODULE_COVERAGE_M;
    clean.style = Object.hasOwn(styleNames, clean.style) ? clean.style : STANDARD_STIL;
    clean.frame = Object.hasOwn(frameNames, clean.frame) ? clean.frame : 'med';
    clean.mapMode = '2d';
    const rotation = Number(clean.rotationDeg);
    clean.rotationDeg = Number.isFinite(rotation) ? ((rotation % 360) + 360) % 360 : 0;
    clean.place = String(clean.place || 'Valgt kortområde').trim().slice(0, 90);
    const panorama = clean.panorama || {};
    clean.panorama = {
      columns: clamp(Math.round(Number(panorama.columns) || 1), PANORAMA_MIN, PANORAMA_MAX),
      rows: clamp(Math.round(Number(panorama.rows) || 1), PANORAMA_MIN, PANORAMA_MAX)
    };
    return clean;
  }

  const produktHandle = String(root.dataset.productHandle || '').trim();
  const singleLine44ProductHandle = String(root.dataset.singleLineProductHandle || '').trim();
  const kurvOensket = String(root.dataset.cartEnabled || '') === 'true';
  let kurvKlar = false;
  let singleLine44Resolution = null;
  let singleLine44ReadinessError = '';
  const isSingleLine44 = snapshot => Number(snapshot?.panorama?.columns) === 2
    && Number(snapshot?.panorama?.rows) === 2 && Number(snapshot?.panorama?.count) === 4;

  /* Henter produktet og bruger dets varianter. Fejler det -- produktet er ikke
     udgivet, handlen er forkert -- bliver kurven staaende slaaet fra, og
     kontaktformularen overtager. Det er bedre end en knap, der foerst fejler,
     naar kunden trykker. */
  async function hentProdukt() {
    if (!produktHandle) return;
    try {
      const r = await fetch(`/products/${encodeURIComponent(produktHandle)}.js`, {
        headers: { Accept: 'application/json' }
      });
      if (!r.ok) throw new Error(`produktet svarede ${r.status}`);
      const produkt = await r.json();
      const resolution = NF_CITY_COMMERCE.resolveProductVariants(produkt.variants);
      Object.keys(varianter).forEach(size => delete varianter[size]);
      prices = { [MODUL_CM]: null };
      pricesMinor = { [MODUL_CM]: null };
      if (!resolution.ok) {
        throw new Error(`commercial readiness: ${resolution.blockingErrors.join('; ')}`);
      }
      const variant = resolution.resolved[MODUL_CM];
      varianter[MODUL_CM] = variant.id;
      pricesMinor[MODUL_CM] = variant.priceMinor;
      // Shopify er fortsat den kundevendte runtime-priskilde.
      prices[MODUL_CM] = variant.priceMinor / 100;
      commerceReadinessError = '';
      kurvKlar = kurvOensket && Boolean(varianter[MODUL_CM])
        && NF_CITY_COMMERCE.MODULE_VARIANT_PRICES_MINOR.includes(pricesMinor[MODUL_CM]);
    } catch (error) {
      console.warn('[bykort] kurven er ikke klar:', error.message);
      commerceReadinessError = String(error.message || 'Produktets commerce-kontrakt er ikke klar');
      kurvKlar = false;
    }
    try {
      if (!globalThis.NF_CITY_SINGLE_LINE) throw new Error('V3 commerce-helperen mangler');
      if (!singleLine44ProductHandle) throw new Error('44 × 44-produktets handle mangler');
      const r = await fetch(`/products/${encodeURIComponent(singleLine44ProductHandle)}.js`, {
        headers: { Accept: 'application/json' }
      });
      if (!r.ok) throw new Error(`44 × 44-produktet svarede ${r.status}`);
      singleLine44Resolution = globalThis.NF_CITY_SINGLE_LINE.resolveProduct(await r.json());
      if (!singleLine44Resolution.ok) throw new Error(singleLine44Resolution.errors.join('; '));
      singleLine44ReadinessError = '';
    } catch (error) {
      singleLine44Resolution = null;
      singleLine44ReadinessError = String(error.message || '44 × 44-produktet er ikke klart');
      console.warn('[bykort] 44 × 44 single-line er fail-closed:', singleLine44ReadinessError);
    }
    render();
  }

  /* Mindste maengde kortdata, foer et udsnit er vaerd at printe. Tallet er
     lavt med vilje: en kystby eller en landsby er et fuldt gyldigt motiv, og
     kun det helt tomme skal stoppes -- aabent hav, oerken, midt i en mark.

     Det her er den ene ting, der ellers gik tabt ved at skifte fra manuel
     kontrol til kurv. Foer saa Rahin hver ordre, inden den gik i produktion;
     nu betaler kunden foerst. Saa maa maskinen sige fra, hvor mennesket foer
     gjorde det. */
  const MINDSTE_INDHOLD = 25;
  let cartBusy = false;

  const cartButtons = () => $$('[data-add-to-cart]');
  const cartStatuses = () => $$('[data-cart-status]');

  function setCartStatus(message, readiness = false) {
    cartStatuses().forEach(status => {
      status.textContent = message;
      if (readiness) status.dataset.readiness = 'true';
      else delete status.dataset.readiness;
    });
  }

  function purchaseReadinessReason() {
    if (!approvedDesign || !godkendt) {
      return 'Vælg kortet, før du lægger designet i kurven.';
    }
    const mapdataReason = approvedMapdataReason();
    if (mapdataReason) return mapdataReason;
    /* Et godkendt design fra en tidligere version kan baere 44. Det kan ikke
       bestilles paa ny -- kunden skal opdatere previewet, saa designet bliver
       til 2 x 2 moduler. */
    if (!publicSize(approvedDesign.sizeCm)) {
      return legacySize(approvedDesign.sizeCm)
        ? 'Dit gemte design bruger den gamle 44 × 44-model. Opdater forhåndsvisningen, så det bliver til fire kort.'
        : 'Dit gemte design har en størrelse, der ikke længere kan bestilles. Opdater forhåndsvisningen.';
    }
    if (commerceReadinessError) {
      return 'Bestilling er midlertidigt lukket, fordi produktets varianter eller priser ikke matcher.';
    }
    if (isSingleLine44(approvedDesign) && singleLine44ReadinessError) {
      return '44 × 44 kan ikke bestilles endnu, fordi produktets seks prisvarianter ikke matcher.';
    }
    if (!kurvKlar || !varianter[MODUL_CM] || !Number.isFinite(prices[MODUL_CM])) {
      return 'Bykortet kan ikke bestilles endnu.';
    }
    /* Et synligt gammelt preview giver ikke adgang til at købe et nyt udsnit. */
    if (state.frame === 'med' && ramme.overflade === 'proMaterial' && !ramme.proMateriale) {
      return 'Vælg et Pro Material, før du lægger designet i kurven.';
    }
    if (state.frame === 'med' && !surfaceChoiceReady(ramme.overflade, overfladeId())) {
      return 'Det valgte rammeudtryk er endnu ikke frigivet til produktion. Vælg Ensfarvet.';
    }
    /* Kurven maa aldrig sende kunden videre til et andet beloeb, end hun har
       set. Kan butikkens varianter ikke laegges sammen til den godkendte pris,
       staar knappen stille -- og der staar hvorfor. */
    const blokering = checkoutBlokering(approvedOrderSnapshot());
    if (blokering) return `Bestilling er midlertidigt lukket (${blokering}).`;
    return '';
  }

  function syncPurchaseReadiness() {
    const reason = purchaseReadinessReason();
    const disabled = cartBusy || Boolean(reason);
    cartButtons().forEach(button => {
      button.disabled = disabled;
      button.setAttribute('aria-disabled', String(disabled));
    });
    if (reason) {
      setCartStatus(reason, true);
    } else {
      cartStatuses().forEach(status => {
        if (status.dataset.readiness !== 'true') return;
        status.textContent = '';
        delete status.dataset.readiness;
      });
    }
  }

  function udsnitIndhold() {
    if (!osmLayers || osmFingerprint !== surfaceLayersFingerprint(godkendtUdsnit(), state.style)) return null;
    return (osmLayers.bygninger?.length || 0) + (osmLayers.vand?.length || 0)
      + (osmLayers.groen?.length || 0) + (osmLayers.veje?.length || 0);
  }

  async function laegIKurv() {
    if (internalProduction) throw new Error('Brug produktionscenteret');
    const mapdataReason = approvedMapdataReason();
    if (mapdataReason) { setCartStatus(mapdataReason); return; }
    publishFunnelEvent('nf_city_cart_attempted', {
      preview_ready: Boolean(approvedDesign)
    });
    if (!approvedDesign) {
      setCartStatus('Vælg kortet, før du lægger designet i kurven.');
      return;
    }
    if (!publicSize(approvedDesign.sizeCm)) {
      setCartStatus(legacySize(approvedDesign.sizeCm)
        ? 'Dit gemte design bruger den gamle 44 × 44-model. Opdater forhåndsvisningen, så det bliver til fire kort.'
        : 'Dit gemte design har en størrelse, der ikke længere kan bestilles. Opdater forhåndsvisningen.');
      return;
    }
    const snapshot = approvedOrderSnapshot();
    if (snapshot.frame === 'med' && snapshot.frameConfig.surface === 'proMaterial'
      && !snapshot.frameConfig.proMaterialId) {
      setCartStatus('Vælg et Pro Material, før du lægger designet i kurven.');
      return;
    }
    const variantId = isSingleLine44(snapshot)
      ? globalThis.NF_CITY_SINGLE_LINE?.selectedVariant(singleLine44Resolution, snapshot).id
      : varianter[snapshot.sizeCm];
    if (!variantId) {
      setCartStatus('Størrelsen kan ikke bestilles lige nu.');
      return;
    }
    const indhold = udsnitIndhold();
    /* Kurvknappen maa aldrig hente produktionsdata skjult. Mangler data til et
       ellers godkendt snapshot, skal kunden eksplicit opdatere previewet. */
    if (indhold === null) {
      setCartStatus('Kortdata mangler i forhåndsvisningen. Opdater forhåndsvisningen og prøv igen.');
      return;
    }
    if (indhold !== null && indhold < MINDSTE_INDHOLD) {
      setCartStatus('Der er næsten ingenting at se i dette udsnit. '
        + 'Flyt kortet ind over et sted med bygninger, vand eller natur, før du bestiller.');
      return;
    }

    cartBusy = true;
    syncPurchaseReadiness();
    setCartStatus('Gemmer dit design sikkert …');
    try {
      /* Design-ID'et er producentflowets kobling til det godkendte kort. Cart
         add er derfor fail-closed: uden et gemt, valideret design sendes ingen
         Shopify-request og der kan ikke opstå en halv konfiguration. */
      const configurationId = currentConfigurationId();
      const commerce = commerceQuote(snapshot, variantId);
      const designRefs = await saveDesigns(snapshot, commerce);
      // Selection may change while the design API is saving. Recheck at the write boundary.
      const changedMap = approvedMapdataReason();
      if (changedMap || designFingerprint(snapshot) !== designFingerprint(approvedDesign)) {
        throw new Error(changedMap || 'Designet blev ændret. Vælg kortet igen.');
      }
      const linjer = buildCartLines(snapshot, designRefs, configurationId, variantId, indhold, commerce);
      const payload = { items: linjer };
      if (localCartHarness) {
        root.__nfCityCartDebug.lastCartPayload = JSON.parse(JSON.stringify(payload));
        syncLocalCartDebug();
      }

      setCartStatus('Lægger i kurv …');
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!r.ok) {
        const fejl = await r.json().catch(() => ({}));
        throw new Error(fejl.description || `kurven svarede ${r.status}`);
      }
      publishFunnelEvent('nf_city_cart_succeeded');
      if (localCartHarness) {
        cartBusy = false;
        syncPurchaseReadiness();
        setCartStatus('Lagt i den lokale testkurv.');
        return;
      }
      setCartStatus('Lagt i kurven. Sender dig videre …');
      window.location.href = '/cart';
    } catch (error) {
      if (localCartHarness) {
        root.__nfCityCartDebug.lastCartError = String(error?.message || error);
        syncLocalCartDebug();
      }
      cartBusy = false;
      syncPurchaseReadiness();
      setCartStatus(`Kunne ikke lægge i kurv: ${error.message}`);
    }
  }

  function invalidateDesignApproval() {
    approvedDesignFingerprint = '';
    if (previewRequestBusy) {
      previewRequestGeneration += 1;
      productCaptureRequest += 1;
    }
    previewRequestBusy = false;
    previewRequestState = 'idle';
    previewRequestPhase = 'idle';
  }

  function update(patch, options = {}) {
    const before = JSON.stringify({ state, frame: framePreviewContract() });
    state = normalize({ ...state, ...patch });
    const aendret = JSON.stringify({ state, frame: framePreviewContract() }) !== before;
    const selectionChanged = Object.keys(patch)
      .some(key => ['place', 'lat', 'lon', 'areaM', 'rotationDeg'].includes(key));
    const panoramaChanged = Object.hasOwn(patch, 'panorama');
    const frameOnly = Object.keys(patch).length === 1 && Object.hasOwn(patch, 'frame');
    const styleChanged = aendret && Object.hasOwn(patch, 'style');
    const refreshedFrame = aendret && frameOnly && refreshApprovedProductChoices();
    if (aendret && !refreshedFrame) invalidateDesignApproval();
    /* Et nyt layout er en ændring af selve kortproduktet. Det må derfor ikke
       opdatere resultatkort, væg eller 3D, før kunden trykker "Vælg kort".
       Tidligere blev panoramaet skrevet direkte ind i approvedDesign her, så
       CTA'en fejlagtigt blev stående som "Kort valgt" efter fx 2 x 2 -> 3 x 2. */
    if (aendret && panoramaChanged) {
      vaegBilledeFingerprint = '';
    }
    /* Geometriændringer afbryder også kortdatahentningen. Produktvalg afbryder
       godkendelsen ovenfor, men kan genbruge kortdata ved næste godkendelse. */
    if (aendret && (selectionChanged || panoramaChanged)) {
      previewRequestGeneration += 1;
      osmRequest += 1;
      abortActiveMapdata();
      productCaptureRequest += 1;
      if (previewRequestBusy) {
        previewRequestBusy = false;
        previewRequestState = 'idle';
        previewRequestPhase = 'idle';
      }
    }
    render({ frameOnly, selectionOnly: selectionChanged });
    if (options.selection) updateSelection(Boolean(options.fit));
    /* Previewet foelger valgene direkte. Det gjorde det ikke foer: eneste vej
       derhen gik gennem kortets ``idle``, saa stoerrelse, udfoerelse og ramme
       naaede foerst frem efter en kamerabevaegelse de intet havde med at goere
       -- og designfingeraftrykket her daekker praecis de felter, previewet
       tegner efter. */
    if (aendret) {
      /* Med/uden ramme aendrer kun rammen. Stoerrelse og kortudfoerelse
         fortsaetter gennem det fulde snapshot, fordi de ogsaa aendrer selve
         produktet. */
      if (frameOnly) publishFrameUpdate();
      else if (!selectionChanged && !styleChanged && (!panoramaChanged || !resultMapReady)) publishProductSnapshot();
    }
    /* Kortdata hentes ikke laengere af sig selv. Foer skete det ved hver eneste
       aendring af udsnittet, altsaa ogsaa midt i et trak, og kunden saa et
       preview der hele tiden var undervejs. Nu venter hentningen paa "Vælg
       kort". Stoerrelse, udfoerelse og ramme kostede aldrig en hentning og gaar
       stadig direkte igennem til previewet. */
    return state;
  }

  /* Det geografiske omraade, kunden faktisk har valgt. Ved panorama er det
     hele motivet -- modulet gange antallet af kolonner og raekker. */
  function areaText(value = state) {
    const { columns, rows } = panoramaOf(value);
    const tal = meter => (meter / 1000).toLocaleString('da-DK', { maximumFractionDigits: 1 });
    return `${tal(value.areaM * columns)} × ${tal(value.areaM * rows)} km`;
  }

  function summary() {
    const sizeCm = productSizeFor(state);
    const grund = {
      Sted: state.place,
      Område: areaText(),
      Størrelse: `${sizeCm} × ${sizeCm} cm`,
      Udførelse: styleNames[state.style],
      Retning: `${Math.round(state.rotationDeg)}°`
    };
    if (!medRamme()) return { ...grund, Ramme: 'Uden ramme' };
    return { ...grund, Ramme: rammeUdtryk() };
  }

  const medRamme = () => state.frame !== 'ingen';
  const proValgt = () => ramme.overflade === 'proMaterial';
  const moensterValgt = () => ramme.overflade === 'pattern';
  const aktivtMateriale = () => motor()?.proMaterialer().find(m => m.id === ramme.proMateriale) || null;
  const aktivtMoenster = () => motor()?.moenstre().find(m => m.id === ramme.moenster) || null;

  function currentConfigurationId() {
    return `NF-CFG-${designRequestId}`;
  }

  function surfaceChoiceReady(mode, designId) {
    if (mode === 'solid') return designId === (motor()?.GLAT_ID || 'glat');
    if (!surfaceReadiness.valid || !['pattern', 'proMaterial'].includes(mode)) return false;
    return surfaceReadiness[mode].has(String(designId || ''));
  }

  async function validateSurfaceReadiness() {
    await window.nfCityFrameConfigurator.validateReadiness(surfaceReadiness, motor);
    if (ramme.overflade !== 'solid' && !surfaceChoiceReady(ramme.overflade, overfladeId())) {
      ramme.overflade = 'solid';
    }
    render({ frameOnly: true });
    publishFrameUpdate();
  }

  /* Ensfarvet er ikke et design, det er fravaeret af et. Produktionen kender
     det som profilfamiliens "glat", saa det er det, ordren baerer. */
  function overfladeId() {
    if (proValgt()) return aktivtMateriale()?.id || motor()?.GLAT_ID || 'glat';
    if (moensterValgt()) return aktivtMoenster()?.id || motor()?.GLAT_ID || 'glat';
    return motor()?.GLAT_ID || 'glat';
  }

  /* Viewer-kontrakten er kun en afledning af den eksisterende rammestate.
     Den opretter ikke endnu en tilstand, som kan drive vaek fra kundens valg.
     Designet er katalogets stabile id; navnet hoerer kun hjemme i UI'et. */
  function framePreviewContract() {
    const withFrame = medRamme();
    const selectedSurface = proValgt()
      ? (aktivtMateriale() ? 'proMaterial' : 'solid')
      : (moensterValgt() ? (aktivtMoenster() ? 'pattern' : 'solid') : 'solid');
    return {
      frame: state.frame,
      previewFrameColor: withFrame ? ramme.farve : '',
      previewFrameSurface: withFrame ? selectedSurface : '',
      previewFrameStyle: withFrame ? 'standard' : '',
      previewFrameDesign: withFrame ? overfladeId() : ''
    };
  }

  function publishFrameUpdate() {
    const detail = framePreviewContract();
    root.__nfCityFramePreview = detail;
    root.dispatchEvent(new CustomEvent('nf:city-frame-update', { detail }));
  }

  /* Rammens udtryk i kundens sprog. Et Pro Material staar alene, fordi det
     erstatter farven. Ensfarvet og moenster staar med farven foerst, saa det
     kan ses, at farven bliver. */
  function rammeUdtryk() {
    const farve = motor()?.farve(ramme.farve)?.navn || 'Sort';
    // Aabner kunden Pro Material uden at have valgt ét, staar der ikke et
    // materialenavn -- for der er ikke valgt noget, og prisen er uaendret.
    if (proValgt()) {
      return aktivtMateriale() ? `${aktivtMateriale().navn} · Pro Material` : 'Vælg et Pro Material';
    }
    if (moensterValgt() && aktivtMoenster()) return `${farve} · ${aktivtMoenster().navn}`;
    return `${farve} · Ensfarvet`;
  }

  function deepFreeze(value) {
    if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
    Object.values(value).forEach(deepFreeze);
    return Object.freeze(value);
  }

  function frameSnapshot() {
    const style = motor()?.stilarter().find(item => item.id === 'standard');
    const color = motor()?.farve(ramme.farve);
    const pattern = aktivtMoenster();
    const proMaterial = aktivtMateriale();
    return {
      style: 'standard',
      styleLabel: style?.navn || 'Standard',
      baseColor: ramme.farve,
      baseColorLabel: color?.navn || ramme.farve,
      surface: ramme.overflade,
      patternId: ramme.moenster || '',
      patternLabel: pattern?.navn || '',
      proMaterialId: ramme.proMateriale || '',
      proMaterialLabel: proMaterial?.navn || '',
      designId: overfladeId(),
      profile: RAMME_PROFIL,
      backing: RAMME_BAGPLADE
    };
  }

  /* Det godkendte preview er ordregrundlaget. Kortudsnit, størrelse, udtryk
     og ramme bindes samlet af den endelige knap "Vælg kort". */
  function captureApprovedDesign(selectionState = godkendtUdsnit(), mapSnapshot = null) {
    /* ``corners`` er modulets fire hjoerner og ikke panoramaets. Det er dem,
       design-API'et validerer mod centrum, areaM og rotation, og ved 1 x 1 er
       de to ting den samme firkant. Hele omraadet ligger i ``panorama``. */
    const selection = makeModuleSelection(selectionState);
    const panoramaState = { ...selectionState, panorama: panoramaOf(selectionState) };
    const opsummering = panoramaOpsummering(panoramaState);
    const totalCorners = makeSelection(panoramaState).corners;
    return deepFreeze({
      mapSnapshot: mapSnapshot || mapdataLocalCache.get(JSON.stringify(mapdataContract(selectionState)))?.value.snapshot || null,
      place: String(selectionState.place || state.place),
      center: { lat: Number(selectionState.lat), lon: Number(selectionState.lon) },
      corners: selection.corners.map(corner => [...corner]),
      areaM: Number(selectionState.areaM),
      rotationDeg: Number(selectionState.rotationDeg),
      panorama: {
        schema: PANORAMA_SCHEMA,
        enabled: opsummering.count > 1,
        columns: opsummering.columns,
        rows: opsummering.rows,
        count: opsummering.count,
        moduleCm: MODUL_CM,
        widthCm: opsummering.widthCm,
        heightCm: opsummering.heightCm,
        totalCorners: totalCorners.map(corner => [...corner]),
        tiles: calculatePanoramaTiles(panoramaState)
      },
      sizeCm: MODUL_CM,
      style: state.style,
      styleLabel: styleNames[state.style],
      frame: state.frame,
      frameConfig: frameSnapshot(),
      production: {
        profile: state.frame === 'med' ? RAMME_PROFIL : 'none',
        backing: state.frame === 'med' ? RAMME_BAGPLADE : 'none',
        frameCount: state.frame === 'med' ? 1 : 0,
        frameScope: state.frame === 'med' ? 'combined_artwork' : 'none',
        frameWidthCm: state.frame === 'med' ? opsummering.widthCm + 5 : null,
        frameHeightCm: state.frame === 'med' ? opsummering.heightCm + 5 : null
      }
    });
  }

  const PRODUCT_FINGERPRINT_CONTRACT = 'nf-city-product-fingerprint/1.0.0';
  const SURFACE_LAYERS_FINGERPRINT_CONTRACT = 'nf-city-surface-layers-fingerprint/1.0.0';

  /* Fingerprints er persisted identity, ikke almindelig debug-JSON. Sortér
     derfor alle object keys rekursivt, så den samme tilstand altid får samme
     byte-streng uanset hvordan objektet blev bygget eller serialiseret. */
  function stableFingerprintValue(value) {
    if (Array.isArray(value)) return value.map(stableFingerprintValue);
    if (value && typeof value === 'object') {
      return Object.keys(value).sort().reduce((result, key) => {
        if (value[key] !== undefined) result[key] = stableFingerprintValue(value[key]);
        return result;
      }, {});
    }
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    return value;
  }

  function canonicalFingerprint(contract, value) {
    return JSON.stringify(stableFingerprintValue({ contract, value }));
  }

  function semanticSurfaceIdentity(productStyle) {
    return {
      version: MAP_RULES.semantic_surface_version,
      options: SEMANTIC_SURFACE.optionsForProduct(productStyle)
    };
  }

  /* Denne ene kontrakt bruges både ved "Vælg kort", før design-save og ved
     kurvvalideringen. Et produktvalg kan derfor ikke være godkendt under én
     normalisering og blive sendt under en anden. */
  function designFingerprint(value) {
    return canonicalFingerprint(PRODUCT_FINGERPRINT_CONTRACT, {
      mapSnapshotDigest: value.mapSnapshot?.digest || '',
      place: String(value.place || ''),
      center: [Number(value.center.lat.toFixed(8)), Number(value.center.lon.toFixed(8))],
      corners: value.corners.map(([lon, lat]) => [Number(lon.toFixed(8)), Number(lat.toFixed(8))]),
      areaM: Number(value.areaM),
      rotationDeg: Number(value.rotationDeg.toFixed(4)),
      panorama: [Number(value.panorama?.columns || 1), Number(value.panorama?.rows || 1)],
      sizeCm: Number(value.sizeCm),
      style: String(value.style || ''),
      mapRuleVersion: MAP_RULES.map_rule_version,
      semanticSurface: semanticSurfaceIdentity(value.style),
      frame: String(value.frame || ''),
      frameConfig: value.frame === 'med' ? {
        style: value.frameConfig.style,
        baseColor: value.frameConfig.baseColor,
        surface: value.frameConfig.surface,
        patternId: value.frameConfig.patternId,
        proMaterialId: value.frameConfig.proMaterialId,
        designId: value.frameConfig.designId,
        profile: value.frameConfig.profile,
        backing: value.frameConfig.backing
      } : null
    });
  }

  function setApprovedDesign(next) {
    const fingerprint = designFingerprint(next);
    if (fingerprint !== approvedDesignFingerprint) {
      currentDesignId = '';
      currentDesignRefs = [];
      currentDesignFingerprint = '';
      designRequestId = newRequestId();
      panoramaGroupId = `NF-PAN-${newRequestId().replace(/-/g, '').slice(0, 12).toUpperCase()}`;
    }
    approvedDesign = next;
    approvedDesignFingerprint = fingerprint;
    if (localCartHarness) {
      root.__nfCityCartDebug.approvedSnapshot = JSON.parse(JSON.stringify(next));
      syncLocalCartDebug();
    }
  }

  /* Rammen vælges efter selve bykortet. Et rammeskift ændrer derfor den
     godkendte ordre-/previewtilstand, men genbruger det samme friske mapdata-
     snapshot og kræver ikke endnu et klik på “Vælg kort”. Kortudsnit, udtryk
     og størrelse går fortsat gennem den fulde invalidation ovenfor. */
  function refreshApprovedProductChoices() {
    if (previewRequestBusy || !approvedDesign || !godkendt) return false;
    const snapshot = approvedDesign.mapSnapshot;
    const contract = mapdataContract(state);
    if (!validMapSnapshot(snapshot, contract)
      || mapdataIdentity(mapdataContract(godkendt)) !== mapdataIdentity(contract)) return false;
    setApprovedDesign(captureApprovedDesign(state, snapshot));
    return true;
  }

  function approvedOrderSnapshot() {
    const mapdataReason = approvedMapdataReason();
    if (mapdataReason) throw new Error(mapdataReason);
    if (!approvedDesign) throw new Error('Forhåndsvisningen er ikke godkendt endnu');
    if (!publicSize(approvedDesign.sizeCm)) throw new Error('Størrelsen kan ikke bestilles som et nyt bykort');
    return deepFreeze({
      ...JSON.parse(JSON.stringify(approvedDesign)),
      panoramaGroupId,
      previewDataUrl: root.__nfCityProductSnapshot?.textureDataUrl || null
    });
  }

  function frameExpression(snapshot) {
    const config = snapshot.frameConfig;
    if (config.surface === 'proMaterial') return `${config.proMaterialLabel} · Pro Material`;
    if (config.surface === 'pattern') return `${config.baseColorLabel} · ${config.patternLabel}`;
    return `${config.baseColorLabel} · Ensfarvet`;
  }

  function customerProperties(snapshot, tile = null) {
    const p = snapshot.panorama;
    const combined = Boolean(p?.enabled);
    const sizeCm = combined ? p.widthCm : snapshot.sizeCm;
    const properties = {
      'Sted': snapshot.place,
      'Størrelse': `${sizeCm} × ${sizeCm} cm`,
      'Kort': snapshot.styleLabel,
      'Ramme': snapshot.frame === 'med'
        ? 'Med ramme'
        : 'Uden ramme'
    };
    if (snapshot.frame === 'med') properties['Udtryk'] = frameExpression(snapshot);
    /* Produktionsopdelingen ligger kun i underscore-felterne nedenfor. */
    return properties;
  }

  function internalProperties(snapshot, designId, configurationId, calculation, selectionContent, tile = null) {
    const frameConfig = snapshot.frameConfig;
    /* Ved panorama beskriver linjen sit eget modul: centrum og hjoerner er
       modulets, ikke hele omraadets. Uden det kunne to felter af samme
       panorama ikke skelnes efter ordren. */
    const geo = tile || { center: snapshot.center, corners: snapshot.corners };
    const properties = {
      '_nf_configuration_id': configurationId,
      '_nf_design_id': designId,
      '_nf_center': `${geo.center.lat.toFixed(6)},${geo.center.lon.toFixed(6)}`,
      '_nf_corners': geo.corners
        .map(([lon, lat]) => `${lat.toFixed(6)},${lon.toFixed(6)}`).join(' | '),
      '_nf_area_m': String(snapshot.areaM),
      '_nf_rotation_deg': String(Number(snapshot.rotationDeg.toFixed(2))),
      '_nf_finish': snapshot.style,
      '_nf_profil': snapshot.production.profile,
      '_nf_stil': snapshot.frame === 'med' ? frameConfig.style : 'none',
      '_nf_bagplade': snapshot.production.backing,
      '_nf_surface': snapshot.frame === 'med' ? frameConfig.surface : 'none',
      '_nf_material_id': snapshot.frame === 'med' ? frameConfig.designId : 'none',
      '_nf_base_color': snapshot.frame === 'med' ? frameConfig.baseColor : '',
      '_nf_rammepris_dkk': String(calculation.perKort.rammePris),
      '_nf_bykortpris_dkk': String(calculation.perKort.bykortPris),
      '_nf_total_dkk': String(calculation.perKort.total)
    };
    const frameCarrier = !snapshot.panorama?.enabled || tile?.index === 0;
    if (frameCarrier && snapshot.frame === 'med'
      && Number.isFinite(snapshot.production.frameWidthCm)
      && Number.isFinite(snapshot.production.frameHeightCm)) {
      const width = Number(snapshot.production.frameWidthCm).toLocaleString('da-DK', { maximumFractionDigits: 1 });
      const height = Number(snapshot.production.frameHeightCm).toLocaleString('da-DK', { maximumFractionDigits: 1 });
      properties['_nf_rammemaal_cm'] = `${width} × ${height} cm`;
      properties['_nf_ramme_antal'] = '1';
      properties['_nf_ramme_scope'] = 'samlet værk';
    }
    if (selectionContent === null) properties['_nf_udsnitskontrol'] = 'kortdata svarede ikke';
    /* Alt hvad produktionen skal bruge for at genskabe det enkelte modul og
       samle panoramaet igen. Skemaversionen staar med, saa ordrer fra denne
       version kan kendes fra senere. */
    if (snapshot.panorama?.enabled) {
      const p = snapshot.panorama;
      const bounds = p.totalCorners
        .map(([lon, lat]) => `${lat.toFixed(6)},${lon.toFixed(6)}`).join(' | ');
      Object.assign(properties, {
        '_nf_panorama_schema': p.schema,
        '_nf_panorama_group': snapshot.panoramaGroupId || '',
        '_nf_panorama_kolonner': String(p.columns),
        '_nf_panorama_raekker': String(p.rows),
        '_nf_panorama_antal': String(p.count),
        '_nf_panorama_modul_cm': String(p.moduleCm),
        '_nf_panorama_samlet_cm': `${p.widthCm} × ${p.heightCm} cm`,
        '_nf_panorama_hjoerner': bounds,
        /* Tomhedskontrollen koerer paa kortdata for centermodulet -- ikke for
           alle N moduler. At hente Overpass-data for op til 100 udsnit ved
           hvert kurvklik ville hverken kunden eller kilden holde til. Det skal
           staa paa ordren, saa den kan kontrolleres i haanden. */
        '_nf_udsnitskontrol_omfang': 'centermodul',
        '_nf_gruppe_total_dkk': String(calculation.total)
      });
      if (tile) Object.assign(properties, {
        '_nf_panorama_indeks': String(tile.index),
        '_nf_panorama_raekke': String(tile.row),
        '_nf_panorama_kolonne': String(tile.column),
        '_nf_tile_bbox': [tile.south, tile.west, tile.north, tile.east]
          .map(value => Number(value).toFixed(6)).join(',')
      });
    }
    return properties;
  }

  /* ==========================================================================
     FRA PRIS TIL KURVLINJER

     Kunden skal betale praecis det, der staar paa skaermen. Kurven kan kun
     laegge beloeb til: hver kurvlinje har en positiv variantpris, og der
     findes ingen negativ linje. Derfor er der et gulv -- modulernes egen
     variantpris gange antallet -- og alt derover daekkes af butikkens
     prisstige med faerrest mulige linjer.

     Ligger den godkendte total under det gulv, kan Shopify ikke opkraeve den,
     og saa siger vi det. Vi lukker hellere kurven end at sende kunden til en
     checkout med et andet beloeb, end hun har set. */
  function checkoutPlan(calculation) {
    const modulMinor = pricesMinor[MODUL_CM];
    if (!Number.isSafeInteger(modulMinor)) {
      throw new Error('Modulets Shopify-pris er ikke hentet');
    }
    const gulvMinor = modulMinor * calculation.antal;
    const restMinor = calculation.totalMinor - gulvMinor;
    if (restMinor < 0) {
      throw new Error(`checkout-gulv: ${calculation.antal} kort koster mindst `
        + `${gulvMinor / 100} kr i Shopify, men den godkendte pris er `
        + `${calculation.totalMinor / 100} kr`);
    }
    if (restMinor === 0) return { modulMinor, gulvMinor, restMinor, buckets: [] };
    if (!prisvarianter.length) throw new Error('prisstigen kunne ikke hentes — prøv igen om et øjeblik');
    const restKroner = restMinor / 100;
    if (!Number.isSafeInteger(restKroner)) {
      throw new Error('Beløbet kan ikke deles i hele kroner');
    }
    const resolved = motor().findVarianterTilPraecisTotal(restKroner, prisvarianter);
    if (!resolved.length) {
      throw new Error(`${restKroner} kr kan ikke sammensættes af butikkens prisvarianter`);
    }
    const buckets = resolved.map(line => {
      const source = prisvarianter.find(variant => String(variant.id) === String(line.id));
      const unitPriceDkk = Number(source?.price);
      if (!Number.isSafeInteger(unitPriceDkk) || unitPriceDkk <= 0) {
        throw new Error('En prisvariant mangler en gyldig Shopify-pris');
      }
      return {
        variant_id: String(line.id),
        quantity: Number(line.quantity),
        unit_price_minor: unitPriceDkk * 100
      };
    }).sort((left, right) => left.variant_id.localeCompare(right.variant_id));
    const bucketMinor = buckets.reduce((sum, line) => sum + line.unit_price_minor * line.quantity, 0);
    if (bucketMinor !== restMinor) {
      throw new Error('Prisstigens linjer rammer ikke det beløb, kunden har set');
    }
    return { modulMinor, gulvMinor, restMinor, buckets };
  }

  /* Kan den aktuelle konfiguration overhovedet opkraeves? Bruges til at slaa
     koebsknappen fra med en aarsag, i stedet for at fejle ved klik. */
  function checkoutBlokering(snapshot) {
    try {
      if (isSingleLine44(snapshot)) {
        const variant = globalThis.NF_CITY_SINGLE_LINE.selectedVariant(singleLine44Resolution, snapshot);
        if (variant.priceMinor !== beregningFor(snapshot).totalMinor) throw new Error('44 × 44-prisen matcher ikke den viste total');
      } else checkoutPlan(beregningFor(snapshot));
      return '';
    }
    catch (fejl) { return String(fejl?.message || fejl); }
  }

  function commerceQuote(snapshot, variantId) {
    if (isSingleLine44(snapshot)) {
      const single = globalThis.NF_CITY_SINGLE_LINE;
      const variant = single.selectedVariant(singleLine44Resolution, snapshot);
      const calculation = beregningFor(snapshot);
      if (String(variant.id) !== String(variantId) || variant.priceMinor !== calculation.totalMinor) {
        throw new Error('44 × 44-varianten matcher ikke den viste pris');
      }
      return Object.freeze({
        contract_version: single.CONTRACT_VERSION,
        price_contract_version: NF_CITY_COMMERCE.PRICE_CONTRACT_VERSION,
        main_variant_id: String(variant.id),
        variant_key: variant.key,
        map_price_minor: variant.priceMinor,
        panorama: Object.freeze({ schema: PANORAMA_SCHEMA, columns: 2, rows: 2, count: 4 }),
        map_count: 4,
        map_bundle_price_minor: calculation.kortMinor,
        map_style_surcharge_minor: calculation.udfoerelseMinor,
        map_total_price_minor: calculation.kortMinor + calculation.udfoerelseMinor,
        frame_enabled: snapshot.frame === 'med',
        frame_reference_per_map_minor: calculation.rammeReferenceMinor,
        frame_surcharge_per_map_minor: calculation.rammeTillaegPrKortMinor,
        frame_price_per_map_minor: calculation.rammePrKortMinor,
        frame_price_minor: calculation.rammeMinor,
        price_bucket_breakdown: Object.freeze([]),
        total_product_price_minor: calculation.totalMinor
      });
    }
    const size = Number(snapshot.sizeCm);
    if (!publicSize(size) || String(varianter[size]) !== String(variantId)) {
      throw new Error('Shopify-varianten matcher ikke den valgte størrelse');
    }
    const priceMinor = pricesMinor[size];
    if (!NF_CITY_COMMERCE.MODULE_VARIANT_PRICES_MINOR.includes(priceMinor)) {
      throw new Error('Shopify-prisen matcher ikke en godkendt City Builder-opsætning');
    }
    const calculation = beregningFor(snapshot);
    const plan = checkoutPlan(calculation);
    const quote = Object.freeze({
      contract_version: NF_CITY_COMMERCE.CONTRACT_VERSION,
      price_contract_version: NF_CITY_COMMERCE.PRICE_CONTRACT_VERSION,
      main_variant_id: String(variantId),
      map_price_minor: priceMinor,
      /* ``map_price_minor`` er modulets variantpris -- der ligger én hovedlinje
         pr. modul i kurven. Prisstigens linjer er derimod lagt sammen for hele
         bestillingen, saa 100 moduler ikke bliver til hundredvis af linjer. */
      panorama: Object.freeze({
        schema: PANORAMA_SCHEMA,
        columns: snapshot.panorama.columns,
        rows: snapshot.panorama.rows,
        count: snapshot.panorama.count
      }),
      map_count: calculation.antal,
      map_bundle_price_minor: calculation.kortMinor,
      map_style_surcharge_minor: calculation.udfoerelseMinor,
      map_total_price_minor: priceMinor * calculation.antal,
      frame_enabled: snapshot.frame === 'med',
      frame_reference_per_map_minor: calculation.rammeReferenceMinor,
      frame_surcharge_per_map_minor: calculation.rammeTillaegPrKortMinor,
      frame_price_per_map_minor: calculation.rammePrKortMinor,
      frame_price_minor: calculation.rammeMinor,
      price_bucket_breakdown: plan.buckets,
      total_product_price_minor: calculation.totalMinor
    });
    /* Sidste kontrol foer kurven: linjerne skal laegge sammen til totalen.
       Gjorde de ikke det, ville kunden betale noget andet end det, hun saa. */
    const linjeMinor = quote.map_total_price_minor
      + plan.buckets.reduce((sum, line) => sum + line.unit_price_minor * line.quantity, 0);
    if (linjeMinor !== calculation.totalMinor) {
      throw new Error('Kurvens linjer summer ikke til den viste pris');
    }
    return quote;
  }

  /* V2 bruger én hovedlinje pr. modul. Ikke quantity = N: modulerne daekker forskellige
     steder paa jorden, og en maengde paa én linje ville smide koordinaterne
     for de oevrige vaek. Rammens prisbuckets er derimod lagt sammen for hele
     panoramaet -- de baerer ingen geografi, kun beloeb, og 100 moduler ville
     ellers give flere hundrede kurvlinjer. V3-grenen nedenfor bærer i stedet
     de fire hashbundne modulreferencer på én 44 × 44-linje. */
  function buildCartLines(snapshot, designRefs, configurationId, variantId, selectionContent, commerce) {
    if (!publicSize(snapshot.sizeCm)) throw new Error('Størrelsen kan ikke lægges i kurven som et nyt bykort');
    const refs = Array.isArray(designRefs) ? designRefs : [designRefs];
    const ids = refs.map(ref => typeof ref === 'string' ? ref : ref?.design_id);
    const tiles = snapshot.panorama?.enabled ? snapshot.panorama.tiles : [null];
    if (ids.length !== tiles.length) throw new Error('Der mangler et design-ID til et af panoramaets felter');
    ids.forEach(designId => {
      if (!/^NF-CITY-\d{8}-\d{4}$/.test(String(designId || ''))) {
        throw new Error('Designet mangler et gyldigt design-ID');
      }
    });
    const calculation = beregningFor(snapshot);
    if (commerce?.contract_version === globalThis.NF_CITY_SINGLE_LINE?.CONTRACT_VERSION) {
      if (!isSingleLine44(snapshot) || refs.length !== 4 || commerce.price_bucket_breakdown.length !== 0) {
        throw new Error('V3 kræver ét 44 × 44-værk uden prislinjer');
      }
      const properties = {
        ...customerProperties(snapshot),
        ...internalProperties(snapshot, ids[0], configurationId, calculation, selectionContent),
        ...globalThis.NF_CITY_SINGLE_LINE.moduleProperties(refs),
        '_nf_line_role': 'main',
        '_nf_commerce_contract': commerce.contract_version
      };
      const lines = [{ id: variantId, quantity: 1, properties }];
      if (commerce.map_price_minor !== commerce.total_product_price_minor) {
        throw new Error('V3-linjen summer ikke til den viste pris');
      }
      return lines;
    }
    const lines = tiles.map((tile, index) => ({
      id: variantId,
      quantity: 1,
      properties: {
        ...customerProperties(snapshot, tile),
        ...internalProperties(snapshot, ids[index], configurationId, calculation, selectionContent, tile),
        '_nf_line_role': 'main'
      }
    }));
    if (!commerce) throw new Error('Den frosne commerce-plan mangler');
    /* Prisstigens linjer hoerer til hele bestillingen og faar derfor gruppens
       faellesdata -- ikke ét bestemt moduls hjoerner. De baerer baade rammerne
       og forskellen mellem modulernes variantpris og den godkendte total, saa
       de findes ogsaa uden ramme. */
    const groupInternal = internalProperties(snapshot, ids[0], configurationId, calculation, selectionContent);
    commerce.price_bucket_breakdown.forEach(line => {
      const source = prisvarianter.find(variant => String(variant.id) === line.variant_id);
      if (!source) throw new Error('En frozen prisvariant findes ikke længere i Shopify-kataloget');
      lines.push({
        id: source.id,
        quantity: line.quantity,
        properties: { ...groupInternal, '_nf_line_role': 'price_bucket' }
      });
    });
    /* Til sidst: linjerne skal laegge sammen til den pris, kunden har set.
       Det er den eneste kontrol, der faktisk daekker hele vejen fra skaerm til
       checkout, saa den staar her og ikke kun i commerceQuote. */
    const linjeMinor = lines.reduce((sum, line) => {
      const minor = String(line.id) === String(variantId)
        ? commerce.map_price_minor
        : (commerce.price_bucket_breakdown.find(b => b.variant_id === String(line.id))?.unit_price_minor || 0);
      return sum + minor * line.quantity;
    }, 0);
    if (linjeMinor !== commerce.total_product_price_minor) {
      throw new Error('Kurvens linjer summer ikke til den viste pris');
    }
    return lines;
  }

  /* Forskellen mellem det valgte rammeudtryk og referencerammen -- regnet af
     rammemotoren, ikke skrevet her. Standard/Glat er nul pr. definition; et
     Pro Material koster motorens eget tillaeg. Er forskellen negativ, er der
     noget galt i motoren, og vi giver hellere referenceprisen end en rabat,
     ingen har besluttet. */
  function approvedPriceBreakdown(snapshot) {
    const count = Math.max(1, Number(snapshot.panorama?.count) || 1);
    const size = isSingleLine44(snapshot) ? '44' : '22';
    if (size === '22' && count !== 1) {
      const frame = snapshot.frame === 'med' && Boolean(motor());
      const pro = frame && snapshot.frameConfig.surface === 'proMaterial' && snapshot.frameConfig.proMaterialId;
      const mapBaseMinor = NF_CITY_COMMERCE.mapBundlePriceMinor(count);
      const styleMinor = NF_CITY_COMMERCE.MAP_STYLE_SURCHARGE_MINOR[snapshot.style] ?? 0;
      const frameReferencePerMapMinor = frame ? NF_CITY_COMMERCE.FRAME_REFERENCE_MINOR : 0;
      const frameSurchargePerMapMinor = pro ? 2900 : 0;
      const framePerMapMinor = frameReferencePerMapMinor + frameSurchargePerMapMinor;
      const frameMinor = framePerMapMinor * count;
      return { count, mapBaseMinor, styleMinor, frameMinor, framePerMapMinor,
        frameReferencePerMapMinor, frameSurchargePerMapMinor,
        totalMinor: mapBaseMinor + styleMinor + frameMinor };
    }
    const contract = NF_CITY_COMMERCE.PRICE_CONTRACT.sizes[size];
    const style = snapshot.style === 'farve' ? 'farve' : 'hvid';
    const frame = snapshot.frame === 'med' && Boolean(motor());
    const pro = frame && snapshot.frameConfig.surface === 'proMaterial' && snapshot.frameConfig.proMaterialId;
    const finish = frame ? (pro ? 'pro' : 'klassisk') : 'uden';
    const key = `${style}|${frame ? 'med' : 'uden'}|${frame ? finish : 'ingen'}`;
    const mapBaseMinor = contract.base_minor.hvid;
    const styleMinor = contract.base_minor[style] - mapBaseMinor;
    const frameMinor = frame ? contract.frame_surcharge_minor[finish] : 0;
    const frameReferenceMinor = frame ? contract.frame_surcharge_minor.klassisk : 0;
    const totalMinor = mapBaseMinor + styleMinor + frameMinor;
    if (contract.totals_minor[key] !== totalMinor
      || frameMinor % count !== 0 || frameReferenceMinor % count !== 0) {
      throw new Error('Den godkendte priskontrakt er inkonsistent');
    }
    return {
      count,
      mapBaseMinor,
      styleMinor,
      frameMinor,
      framePerMapMinor: frameMinor / count,
      frameReferencePerMapMinor: frameReferenceMinor / count,
      frameSurchargePerMapMinor: (frameMinor - frameReferenceMinor) / count,
      totalMinor
    };
  }

  /* Totalen, samlet ét sted:

       kortene    -- maengdekurven for antallet, ikke stykpris gange antal
       udfoerelse -- Farve er ét tillaeg paa motivet, ikke pr. kort
       rammerne   -- referencerammen pr. kort plus motorens tillaeg pr. kort

     Alt i oere. Kroner regnes foerst, naar der skal staa noget paa skaermen. */
  function beregningFor(snapshot) {
    if (!publicSize(snapshot.sizeCm)) throw new Error('Størrelsen er ikke et offentligt City Builder-valg');
    const price = approvedPriceBreakdown(snapshot);
    const antal = price.count;
    const kortMinor = price.mapBaseMinor;
    const udfoerelseMinor = price.styleMinor;
    const rammePrKortMinor = price.framePerMapMinor;
    const rammeMinor = price.frameMinor;
    const totalMinor = price.totalMinor;
    return {
      antal,
      totalMinor,
      kortMinor,
      udfoerelseMinor,
      rammeMinor,
      rammePrKortMinor,
      rammeReferenceMinor: price.frameReferencePerMapMinor,
      rammeTillaegPrKortMinor: price.frameSurchargePerMapMinor,
      total: totalMinor / 100,
      bykortPris: (kortMinor + udfoerelseMinor) / 100,
      rammePris: rammeMinor / 100,
      perKort: {
        total: totalMinor / 100 / antal,
        bykortPris: (kortMinor + udfoerelseMinor) / 100 / antal,
        rammePris: rammePrKortMinor / 100
      }
    };
  }

  function beregning() {
    return beregningFor(captureApprovedDesign(state));
  }

  function totalPrice() {
    return beregning().total;
  }

  /* Kunden har allerede valgt rammen paa det tidspunkt, hun ser prisen, saa
     der staar et beloeb -- ikke "+ ramme". */
  function totalPriceLabel() {
    if (!Number.isFinite(prices[MODUL_CM])) return 'Pris afventer variant';
    return money(totalPrice());
  }

  /* ==========================================================================
     PANORAMAETS GEOMETRI

     Ét sted regnes moduler om til koordinater. Gitteret paa kortet, previewet,
     designene og kurvens linjer skal bruge de samme tal, og to regnestykker
     ville foer eller siden give to svar -- og saa ville kunden se ét omraade
     og faa printet et andet.
     ========================================================================== */

  const panoramaOf = value => {
    const source = (value && value.panorama) || state.panorama || {};
    return {
      columns: clamp(Math.round(Number(source.columns) || 1), PANORAMA_MIN, PANORAMA_MAX),
      rows: clamp(Math.round(Number(source.rows) || 1), PANORAMA_MIN, PANORAMA_MAX)
    };
  };
  const panoramaCount = (value = state) => {
    const { columns, rows } = panoramaOf(value);
    return columns * rows;
  };
  const panoramaAktiv = (value = state) => panoramaCount(value) > 1;

  /* Lokale meter -> lon/lat. Ordret samme regnestykke som ``expectedCorners``
     i produktionens validering, saa et design gemt herfra kan godkendes der.
     Afviger de to, afvises ordren. Testen efterregner det. */
  function localToLngLat(east, north, value) {
    const shared = panoramaAktiv(value);
    const lat = shared ? Number(value.lat.toFixed(8)) : value.lat;
    const lon = shared ? Number(value.lon.toFixed(8)) : value.lon;
    const rotation = shared ? Number(value.rotationDeg.toFixed(4)) : value.rotationDeg;
    const radians = rotation * Math.PI / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const meters = Math.PI * 6378137 / 180;
    const lonMeters = (shared ? meters : 111320) * Math.max(.01, Math.cos(lat * Math.PI / 180));
    return [
      lon + (east * cos - north * sin) / lonMeters,
      lat + (east * sin + north * cos) / (shared ? meters : 110574)
    ];
  }

  /* Hele det valgte omraade. Ved 1 x 1 er det praecis den kvadratiske firkant,
     siden altid har haft; ved 3 x 2 er det ét rektangel med forholdet 3:2. */
  function makeSelection(value = state) {
    const { columns, rows } = panoramaOf(value);
    const halfEast = columns * value.areaM / 2;
    const halfNorth = rows * value.areaM / 2;
    const corners = [
      [-halfEast, halfNorth],
      [halfEast, halfNorth],
      [halfEast, -halfNorth],
      [-halfEast, -halfNorth]
    ].map(([east, north]) => localToLngLat(east, north, value));
    return {
      corners,
      geojson: {
        type: 'FeatureCollection',
        features: [
          { type: 'Feature', properties: { role: 'area' }, geometry: { type: 'Polygon', coordinates: [[...corners, corners[0]]] } },
          ...corners.map((coordinates, index) => ({ type: 'Feature', properties: { role: 'handle', index }, geometry: { type: 'Point', coordinates } }))
        ]
      }
    };
  }

  function cropFrameRect() {
    const mapNode = $('[data-map]');
    if (!mapNode) return { left: 0, top: 0, width: 0, height: 0 };
    const { columns, rows } = panoramaOf(state);
    const width = mapNode.clientWidth;
    const height = mapNode.clientHeight;
    const aspect = Math.max(1, columns) / Math.max(1, rows);
    const maxW = width * CROP_VIEW_FRACTION;
    const maxH = height * CROP_VIEW_FRACTION;
    let frameW;
    let frameH;
    if (maxW / maxH > aspect) {
      frameH = maxH;
      frameW = frameH * aspect;
    } else {
      frameW = maxW;
      frameH = frameW / aspect;
    }
    return {
      left: (width - frameW) / 2,
      top: (height - frameH) / 2,
      width: frameW,
      height: frameH
    };
  }

  function layoutCropOverlay() {
    const frame = $('[data-crop-frame]');
    const grid = $('[data-crop-grid]');
    const tag = $('[data-crop-tag]');
    if (!frame) return;
    const rect = cropFrameRect();
    frame.style.left = `${rect.left}px`;
    frame.style.top = `${rect.top}px`;
    frame.style.width = `${rect.width}px`;
    frame.style.height = `${rect.height}px`;
    frame.style.transform = `rotate(${Number(state.rotationDeg) || 0}deg)`;
    const { columns, rows } = panoramaOf(state);
    if (grid) {
      grid.style.gridTemplateColumns = `repeat(${columns}, 1fr)`;
      grid.style.gridTemplateRows = `repeat(${rows}, 1fr)`;
      const moduleCount = columns * rows;
      grid.dataset.modules = String(moduleCount);
      if (grid.childElementCount !== moduleCount) {
        grid.replaceChildren(...Array.from({ length: moduleCount }, () => document.createElement('span')));
      }
    }
    if (tag) {
      const rotation = `${Math.round(state.rotationDeg)}°`;
      tag.textContent = `${areaText()} · ${rotation}`;
    }
  }

  function selectionFromCropFrame() {
    if (!map) return null;
    const rect = cropFrameRect();
    if (rect.width < 8 || rect.height < 8) return null;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const center = map.unproject([cx, cy]);
    return {
      lat: center.lat,
      lon: center.lng
    };
  }

  function beginIgnoreMapSync() {
    ignoreMapSync += 1;
  }

  function endIgnoreMapSync() {
    window.requestAnimationFrame(() => {
      ignoreMapSync = Math.max(0, ignoreMapSync - 1);
    });
  }

  function syncSelectionFromMap() {
    if (!mapReady || selectionLocked || ignoreMapSync) return;
    const next = selectionFromCropFrame();
    if (!next) return;
    if (Number(next.lat.toFixed(8)) === Number(state.lat.toFixed(8))
      && Number(next.lon.toFixed(8)) === Number(state.lon.toFixed(8))) return;
    update({ lat: next.lat, lon: next.lon }, { selection: true });
  }

  /* Modulet midt i omraadet -- ét 22 x 22 cm kort. Kortdata, teksturen og
     3D-modellen arbejder paa det og ikke paa hele panoramaet: et panorama paa
     10 x 10 ville ellers koste hundrede gange saa meget at hente og tegne,
     hver gang kunden roerte firkanten. Ved 1 x 1 er de to ting den samme
     firkant, saa for et enkelt kort aendrer intet sig overhovedet. */
  const previewTile = (value = state) => ({ ...value, panorama: { columns: 1, rows: 1 } });
  const makeModuleSelection = (value = state) => makeSelection(previewTile(value));

  /* Ét tile pr. modul, nummereret raekkevis fra nordvest (index 0 oeverst til
     venstre, foer rotation). north/south/east/west er modulets akseparallelle
     omskrevne kasse -- er udsnittet roteret, er den altsaa lidt stoerre end
     selve modulet, og ``corners`` er den praecise firkant. */
  function calculatePanoramaTiles(value = state) {
    const { columns, rows } = panoramaOf(value);
    const moduleM = Number(value.areaM);
    const half = moduleM / 2;
    const tiles = [];
    for (let row = 0; row < rows; row += 1) {
      for (let column = 0; column < columns; column += 1) {
        // Modulets centrum, maalt i meter fra panoramaets midte.
        const east = (column - (columns - 1) / 2) * moduleM;
        const north = ((rows - 1) / 2 - row) * moduleM;
        const [lon, lat] = localToLngLat(east, north, value);
        // Exact quadrants of one parent frame, including rotated layouts.
        const corners = [[-half, half], [half, half], [half, -half], [-half, -half]]
          .map(([tileEast, tileNorth]) => localToLngLat(east + tileEast, north + tileNorth, value));
        const latitudes = corners.map(point => point[1]);
        const longitudes = corners.map(point => point[0]);
        tiles.push({
          index: row * columns + column,
          row,
          column,
          center: { lat, lon },
          areaM: moduleM,
          rotationDeg: value.rotationDeg,
          corners,
          north: Math.max(...latitudes),
          south: Math.min(...latitudes),
          east: Math.max(...longitudes),
          west: Math.min(...longitudes)
        });
      }
    }
    return tiles;
  }

  /* Ét lukket felt pr. modul -- ikke loese linjer henover omraadet.

     Foerst var gitteret to saet ``LineString``. Paa arbejdskortet saa det rigtigt
     ud, fordi udsnittets egen kant lukkede felterne i yderkanten; paa
     resultatkortet, hvor der ingen kant er, stod stregerne og flagrede, og
     felterne saa aabne ud. Nu tegnes hvert modul som sin egen firkant, saa et
     3 x 2 er seks lukkede felter begge steder.

     ``mellemrum`` er andelen af modulet, der trækkes fra hele vejen rundt.
     Med lidt luft mellem felterne ligner de dét, kunden faktisk faar: flere
     selvstaendige indrammede kort, der tilsammen danner motivet.

     Selv ved 10 x 10 er det hundrede firkanter i én kilde -- ren geometri, ingen
     hentninger og ingen modeller -- saa +/- kan opdatere live. */
  function panoramaCellsGeojson(value = state, mellemrum = 0) {
    const { columns, rows } = panoramaOf(value);
    const moduleM = Number(value.areaM);
    const halfEast = columns * moduleM / 2;
    const halfNorth = rows * moduleM / 2;
    const kant = moduleM * clamp(mellemrum, 0, .2);
    const half = moduleM / 2 - kant / 2;
    const features = [];
    for (let row = 0; row < rows; row += 1) {
      for (let column = 0; column < columns; column += 1) {
        const east = -halfEast + (column + .5) * moduleM;
        const north = halfNorth - (row + .5) * moduleM;
        const ring = [[-half, half], [half, half], [half, -half], [-half, -half]]
          .map(([e, n]) => localToLngLat(east + e, north + n, value));
        features.push({
          type: 'Feature',
          properties: { role: 'cell', index: row * columns + column, row, column },
          geometry: { type: 'Polygon', coordinates: [[...ring, ring[0]]] }
        });
      }
    }
    return { type: 'FeatureCollection', features };
  }

  /* Kundevendte tal. Der staar ingen faste strenge nogen steder: 6 kort og
     66 x 44 cm er regnet ud af columns, rows og modulets 22 cm. */
  const tomGeojson = () => ({ type: 'FeatureCollection', features: [] });

  /* Resultatkortets motivfelter mødes uden luft. Den brede ramme tegnes af en
     separat ydre polygon; denne kilde leverer kun de indre delestreger. */
  const resultatCeller = () => (panoramaAktiv({ panorama: previewPanorama() })
    ? panoramaCellsGeojson({ ...godkendtUdsnit(), panorama: previewPanorama() }, 0)
    : tomGeojson());
  const resultatYderramme = () => {
    if (!panoramaAktiv({ panorama: previewPanorama() })) return tomGeojson();
    const corners = makeSelection({ ...godkendtUdsnit(), panorama: previewPanorama() }).corners;
    return {
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        properties: { role: 'outer-frame' },
        geometry: { type: 'Polygon', coordinates: [[...corners, corners[0]]] }
      }]
    };
  };

  function panoramaOpsummering(value = state) {
    const { columns, rows } = panoramaOf(value);
    const count = columns * rows;
    return {
      columns,
      rows,
      count,
      widthCm: columns * MODUL_CM,
      heightCm: rows * MODUL_CM,
      label: `${columns} × ${rows}`,
      sizeLabel: `${columns * MODUL_CM} × ${rows * MODUL_CM} cm`,
      countLabel: count === 1 ? '1 kort' : `${count} kort`,
      opbygningLabel: count === 1
        ? `1 kort · ${MODUL_CM} × ${MODUL_CM} cm`
        : `Ét samlet værk · ${columns} × ${rows} felter · ${columns * MODUL_CM} × ${rows * MODUL_CM} cm`,
      modulLabel: `${MODUL_CM} × ${MODUL_CM} cm pr. motivfelt`,
      fuldLabel: `Ét samlet værk · ${count} motivfelter · ${columns} × ${rows} · ${columns * MODUL_CM} × ${rows * MODUL_CM} cm`
    };
  }

  function designPayload(snapshot, commerce, tile = null, requestId = designRequestId) {
    if (!publicSize(snapshot.sizeCm)) throw new Error('Størrelsen kan ikke gemmes som et nyt bykort');
    const hasFrame = snapshot.frame === 'med';
    const frameConfig = snapshot.frameConfig;
    const frameType = frameConfig.style === 'minimal' ? 'slim' : frameConfig.style;
    const exteriorAddCm = frameType === 'slim' ? 3.8 : 5;
    return {
      schema_version: '2.1.0',
      client_request_id: requestId,
      configuration_id: currentConfigurationId(),
      place: snapshot.place,
      /* Udsnittet er altid ét modul -- det er dét, produktionens skema rummer,
         og det er dét, der bliver til én fysisk plade. Sammenhaengen til de
         oevrige moduler staar i ``panorama`` nedenfor. */
      selection: tile ? {
        center: { ...tile.center },
        corners: tile.corners.map(corner => [...corner]),
        area_m: tile.areaM,
        rotation_deg: tile.rotationDeg
      } : {
        center: { ...snapshot.center },
        corners: snapshot.corners.map(corner => [...corner]),
        area_m: snapshot.areaM,
        rotation_deg: snapshot.rotationDeg
      },
      panorama: snapshot.panorama?.enabled ? {
        schema: snapshot.panorama.schema,
        coordinate_system: 'nf-city-parent/1.0.0',
        group_id: snapshot.panoramaGroupId || null,
        columns: snapshot.panorama.columns,
        rows: snapshot.panorama.rows,
        count: snapshot.panorama.count,
        module_cm: snapshot.panorama.moduleCm,
        total_width_cm: snapshot.panorama.widthCm,
        total_height_cm: snapshot.panorama.heightCm,
        total_corners: snapshot.panorama.totalCorners.map(corner => [...corner]),
        center: { ...snapshot.center },
        tile: tile ? { index: tile.index, row: tile.row, column: tile.column,
          north: tile.north, south: tile.south, east: tile.east, west: tile.west } : null
      } : null,
      product: {
        size_cm: snapshot.sizeCm,
        style: snapshot.style,
        frame: snapshot.frame,
        frame_configuration: hasFrame ? {
          enabled: true,
          type: frameType,
          profile_id: frameConfig.profile,
          base_color: frameConfig.baseColor,
          surface_mode: frameConfig.surface,
          pattern_id: frameConfig.surface === 'pattern' ? (frameConfig.patternId || null) : null,
          material_id: frameConfig.surface === 'proMaterial' ? (frameConfig.proMaterialId || null) : null,
          design_id: frameConfig.designId,
          backing_id: frameConfig.backing,
          exterior_width_cm: snapshot.sizeCm + exteriorAddCm,
          exterior_height_cm: snapshot.sizeCm + exteriorAddCm
        } : null
      },
      commerce,
      layers: { bygninger: true, vand: true, groen: true, veje: true },
      preview_data_url: snapshot.previewDataUrl
    };
  }

  function setDesignSaveStatus(message, error = false) {
    const node = $('[data-design-save-status]');
    if (!node) return;
    node.textContent = message;
    if (error) node.dataset.error = 'true';
    else delete node.dataset.error;
  }

  /* Hvor mange design der gemmes samtidig. Serielt ville 100 moduler tage
     minutter; alle paa én gang ville ramme produktionsservicen med hundrede
     samtidige skrivninger. Fire ad gangen holder begge dele i skak. */
  const DESIGN_SAMTIDIGE = 4;

  async function gemEtDesign(snapshot, commerce, tile, requestId) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(new URL('/api/designs', designApiBase), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(designPayload(snapshot, commerce, tile, requestId)),
        signal: controller.signal
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        /* Produktionens egen afvisning skal kunne kendes fra temaets. Uden
           afsenderen ligner "det valgte materiale er ikke frigivet" en fejl i
           konfiguratoren, og saa leder man det forkerte sted -- og produktionen
           er den eneste, der kan aabne for et materiale. */
        const fejl = new Error(`Produktionen afviste designet: `
          + `${body.error || `svar ${response.status}`}`);
        fejl.kilde = 'design-api';
        fejl.status = response.status;
        fejl.serversvar = body.error || '';
        throw fejl;
      }
      if (!/^NF-CITY-\d{8}-\d{4}$/.test(String(body.design_id || ''))) {
        throw new Error('Design-API returnerede ikke et gyldigt design-ID');
      }
      if (!/^sha256:[0-9a-f]{64}$/.test(String(body.design_record_sha256 || ''))) {
        throw new Error('Design-API returnerede ikke et verificerbart record-hash');
      }
      return Object.freeze({
        design_id: body.design_id,
        design_record_sha256: body.design_record_sha256
      });
    } finally {
      window.clearTimeout(timeout);
    }
  }

  /* Ét design-record pr. modul. Produktionens skema rummer praecis ét udsnit
     pr. design -- centrum, fire hjoerner, areaM og rotation, som servicen selv
     efterregner -- saa et panorama kan ikke presses ned i ét record uden at
     aendre og udrulle den service. Det er ogsaa den rigtige model: ét design-ID
     er én fysisk plade i produktionen.

     Fejler ét modul, gaar intet i kurven. En halv panoramabestilling er vaerre
     end ingen: kunden ville betale for et motiv, der ikke kan samles. */
  async function saveDesigns(snapshot, commerce) {
    if (!designApiBase) throw new Error('Designlagring er ikke konfigureret endnu');
    const approvedFingerprint = designFingerprint(snapshot);
    const fingerprint = `${approvedFingerprint}|${JSON.stringify(commerce)}|${snapshot.panoramaGroupId || ''}`;
    if (currentDesignRefs.length && currentDesignFingerprint === fingerprint) return currentDesignRefs;
    if (designSavePromise) return await designSavePromise;

    const tiles = snapshot.panorama?.enabled ? snapshot.panorama.tiles : [null];
    const requestIds = tiles.map((tile, index) => (tiles.length === 1 ? designRequestId : newRequestId()));

    designSavePromise = (async () => {
      try {
        const resultater = new Array(tiles.length);
        let faerdige = 0;
        let naeste = 0;
        const fortael = () => setDesignSaveStatus(tiles.length === 1
          ? 'Gemmer dit præcise design sikkert…'
          : `Gemmer dine ${tiles.length} kort sikkert… ${faerdige} af ${tiles.length}`);
        fortael();

        async function arbejder() {
          for (let index = naeste++; index < tiles.length; index = naeste++) {
            resultater[index] = await gemEtDesign(snapshot, commerce, tiles[index], requestIds[index]);
            faerdige += 1;
            fortael();
          }
        }
        await Promise.all(Array.from(
          { length: Math.min(DESIGN_SAMTIDIGE, tiles.length) }, arbejder
        ));

        if (!approvedDesign || approvedDesignFingerprint !== approvedFingerprint) {
          throw new Error('Produktvalgene blev ændret under lagringen. Prøv igen.');
        }
        currentDesignRefs = resultater;
        currentDesignId = resultater[0].design_id;
        currentDesignFingerprint = fingerprint;
        setDesignSaveStatus(tiles.length === 1
          ? `Design ${currentDesignId} er gemt.`
          : `${tiles.length} design er gemt · første: ${currentDesignId}`);
        syncOrderFields(summary());
        return resultater;
      } catch (error) {
        const message = error?.name === 'AbortError'
          ? 'Designet kunne ikke gemmes inden for tidsgrænsen. Prøv igen.'
          : `Designet kunne ikke gemmes: ${error.message || 'ukendt fejl'}`;
        setDesignSaveStatus(message, true);
        throw error;
      }
    })();
    try { return await designSavePromise; }
    finally { designSavePromise = null; }
  }

  // Kontaktformularen bestiller ét kort ad gangen og vil kun have ét ID.
  async function saveDesign(snapshot, commerce) {
    return (await saveDesigns(snapshot, commerce))[0].design_id;
  }

  function selectionLocalMeters(lon, lat, reference = godkendtUdsnit()) {
    const meters = Math.PI * 6378137 / 180;
    const shared = panoramaAktiv(reference);
    const lat0 = shared ? Number(reference.lat.toFixed(8)) : reference.lat;
    const lon0 = shared ? Number(reference.lon.toFixed(8)) : reference.lon;
    const east = (Number(lon) - lon0) * (shared ? meters : 111320) * Math.max(.01, Math.cos(lat0 * Math.PI / 180));
    const north = (Number(lat) - lat0) * (shared ? meters : 110574);
    const radians = (shared ? Number(reference.rotationDeg.toFixed(4)) : reference.rotationDeg) * Math.PI / 180;
    return {
      east: east * Math.cos(radians) + north * Math.sin(radians),
      north: -east * Math.sin(radians) + north * Math.cos(radians)
    };
  }

  /* ==========================================================================
     KORTDATA TIL PRODUKTPREVIEWET

     Previewet tegnede foer efter CARTOs vektortiles og et skaermbillede af
     baggrundskortet. To ting var galt med det. Bygningshoejderne var CARTOs
     ``render_height`` og ikke generatorens, saa previewet og det printede emne
     kunne vise hver sin by; og vand, groent og veje var et *billede* af et
     kort med skrift og gadenavne, ikke fire flader i emnets egne farver.

     Her hentes de samme OSM-data, som produktionsgeneratoren bygger af, saa
     previewet viser det, kunden faktisk faar.
     ========================================================================== */

  /* Kortdata gaar gennem vores egen cachende proxy, ikke direkte til de
     offentlige kildeservere. Proxyen ligger i workers/nf-kortdata: den cacher
     svaret i 24 timer, falder tilbage paa tre kilder efter hinanden, serverer
     en udloebet kopi hvis alle fejler, og begraenser til 30 kald i minuttet pr.
     IP. Uden den ville hver kunde, der flytter udsnittet, ramme infrastruktur
     uden driftsgaranti -- maalt til mellem 3,7 og 13,5 sekunder, med 504'ere.

     Derfor er der heller ingen hedging her laengere: proxyen skifter selv
     kilde, og et enkelt kald er nok. */
  /* Kan peges et andet sted hen af sektionen. Bruges af den lokale
     fejlsoegningsserver, som ellers rammer proxyens CORS-allowlist. */
  const KORTDATA_URL = String(root.dataset.kortdataUrl || '').trim()
    || 'https://nf-kortdata.rahin1992.workers.dev';
  /* En delrequest kan bruge proxyens to bundne upstream-runder. Browseren skal
     vente paa proxyens endelige svar i stedet for selv at udstille en transient
     503 i konsollen. */
  const KORTDATA_GIVEUP_MS = 65000;
  const PRODUCT_CAPTURE_GIVEUP_MS = 35000;
  const MAP_RULES = window.NF_CITY_MAP_RULES;
  const SEMANTIC_SURFACE = window.NF_CITY_SEMANTIC_SURFACE;
  if (!MAP_RULES?.map_rule_version || !MAP_RULES?.semantic_surface_version
    || !MAP_RULES?.roads || !Array.isArray(MAP_RULES?.overpass_selectors)
    || typeof SEMANTIC_SURFACE?.classify !== 'function'
    || typeof SEMANTIC_SURFACE?.optionsForProduct !== 'function') {
    throw new Error('City Builders versionerede map-regler mangler');
  }

  // Faktiske koerebaner plus fortov, ikke kildedataenes ``width`` -- den mangler
  // paa de fleste veje, og et vejnet hvor halvdelen forsvinder ser oedelagt ud.
  const ROAD_W = MAP_RULES.roads;

  let osmLayers = null;
  let osmFingerprint = '';
  // Preview cache covers the whole work; production/cart retain the centre module.
  let productOsmLayers = null;
  let productOsmFingerprint = '';
  let productOsmRequest = 0;
  let osmRequest = 0;
  let osmFejl = false;              // kortdata svarede ikke paa sidste forsoeg
  let osmCacheStatus = '';
  let previewRequestBusy = false;
  let previewRequestState = 'idle';
  let previewRequestPhase = 'idle';
  let previewRequestFingerprint = '';
  let previewRequestGeneration = 0;

  const godkendtUdsnit = () => godkendt || state;
  const previewPanorama = () => panoramaOf(godkendt || state);
  const panoramaFingerprint = value => {
    const format = panoramaOf(value);
    return `${format.columns}x${format.rows}`;
  };
  const erGodkendt = () => !!godkendt
    && !!approvedDesign
    && mapdataIdentity(mapdataContract(godkendt)) === mapdataIdentity(mapdataContract(state))
    && Number(godkendt.rotationDeg.toFixed(4)) === Number(state.rotationDeg.toFixed(4))
    && godkendt.place === state.place
    && panoramaFingerprint(godkendt) === panoramaFingerprint(state)
    && designFingerprint(captureApprovedDesign(state, approvedDesign.mapSnapshot)) === approvedDesignFingerprint;

  function selectionFingerprint(value = state) {
    return JSON.stringify([
      Number(value.lat.toFixed(6)), Number(value.lon.toFixed(6)), value.areaM,
      Number((value.rotationDeg || 0).toFixed(4)), MAP_RULES.map_rule_version
    ]);
  }

  const selectionIntentFingerprint = (value = state) => JSON.stringify([
    selectionFingerprint(value), String(value.place || ''), panoramaFingerprint(value)
  ]);

  const productLayersFingerprint = (value = godkendtUdsnit(), panorama = previewPanorama(), productStyle = state.style) => {
    const format = panoramaOf({ panorama });
    return canonicalFingerprint(SURFACE_LAYERS_FINGERPRINT_CONTRACT, {
      selection: selectionFingerprint(value),
      panorama: [format.columns, format.rows],
      style: productStyle,
      semanticSurface: semanticSurfaceIdentity(productStyle)
    });
  };

  const surfaceLayersFingerprint = (value = godkendtUdsnit(), productStyle = state.style) =>
    productLayersFingerprint(value, { columns: 1, rows: 1 }, productStyle);

  function selectionBbox(value = state, fullPanorama = false) {
    /* Produktionskontrollen bruger fortsat centermodulet. Den særskilte
       3D-previewhentning kan bede om det samlede committed panorama. */
    const corners = (fullPanorama ? makeSelection(value) : makeModuleSelection(value)).corners;
    const latitudes = corners.map(point => Number(point[1]));
    const longitudes = corners.map(point => Number(point[0]));
    return {
      s: Math.min(...latitudes), w: Math.min(...longitudes),
      n: Math.max(...latitudes), e: Math.max(...longitudes)
    };
  }

  /* Browseren sender ét logisk preview. Proxyen genskaber de eksakte gamle
     ca. 700 m-delområder, så rate limit beskytter kundeoperationer i stedet for
     at tælle intern geometri som 24-45 separate previews. */
  const MAPDATA_PREVIEW_CONTRACT = 'nf-mapdata-preview/1';
  const MAPDATA_LOCAL_TTL_MS = 5 * 60 * 1000;
  const mapdataLocalCache = new Map();
  let activeMapdata = null;

  function mapdataContract(value) {
    const format = panoramaOf(value);
    return {
      contract: MAPDATA_PREVIEW_CONTRACT,
      mapRuleVersion: MAP_RULES.map_rule_version,
      selectors: MAP_RULES.overpass_selectors,
      selection: {
        lat: Number(value.lat.toFixed(8)),
        lon: Number(value.lon.toFixed(8)),
        areaM: value.areaM,
        rotationDeg: Number((value.rotationDeg || 0).toFixed(4)),
        columns: format.columns,
        rows: format.rows
      }
    };
  }

  function abortActiveMapdata() {
    activeMapdata?.controller.abort();
    activeMapdata = null;
  }

  function mapdataIdentity(value) {
    if (!value?.selection || !Array.isArray(value.selectors)) return '';
    const s = value.selection;
    return JSON.stringify([value.contract, value.mapRuleVersion, value.selectors,
      s.lat, s.lon, s.areaM, s.rotationDeg, s.columns, s.rows]);
  }

  function validMapSnapshot(snapshot, contract, now = Date.now()) {
    return Boolean(snapshot && mapdataIdentity(snapshot.identity) === mapdataIdentity(contract)
      && /^[a-f0-9]{64}$/.test(snapshot.digest || '')
      && Number.isSafeInteger(snapshot.createdAt) && snapshot.createdAt > 0
      && snapshot.createdAt <= now + 1000 && now - snapshot.createdAt <= 604800000
      && ['FRESH', 'STALE'].includes(snapshot.freshness));
  }

  function approvedMapdataReason() {
    const message = 'Opdater og vælg kortet igen, før du lægger designet i kurven.';
    if (!approvedDesign || !godkendt) return message;
    const contract = mapdataContract(state);
    const snapshot = approvedDesign.mapSnapshot;
    if (!validMapSnapshot(snapshot, contract)
      || mapdataIdentity(mapdataContract(godkendt)) !== mapdataIdentity(contract)
      || mapdataIdentity(mapdataContract({ ...approvedDesign,
        lat: approvedDesign.center.lat, lon: approvedDesign.center.lon })) !== mapdataIdentity(contract)) return message;
    if (designFingerprint(captureApprovedDesign(state, snapshot)) !== approvedDesignFingerprint) return message;
    // Display-only stale fallback is never a purchasable production snapshot.
    if (snapshot.purchaseAllowed !== true || snapshot.freshness !== 'FRESH'
      || Date.now() - snapshot.createdAt > 86400000) {
      return 'Kortet vises fra en ældre kopi. Hent et aktuelt preview før bestilling.';
    }
    return '';
  }

  async function fetchKortdata(snap) {
    const contract = mapdataContract(snap);
    const body = JSON.stringify(contract);
    const cached = mapdataLocalCache.get(body);
    if (cached && Date.now() - cached.savedAt < MAPDATA_LOCAL_TTL_MS) {
      return { ...cached.value, cacheStatus: 'LOCAL' };
    }
    if (activeMapdata?.key === body) return activeMapdata.promise;
    abortActiveMapdata();
    const controller = new AbortController();
    const headers = {
      'Content-Type': 'application/json',
      ...(internalProduction ? { 'X-NF-Action': 'dashboard' } : {})
    };
    const promise = (async () => {
      const deadline = window.setTimeout(() => controller.abort(), KORTDATA_GIVEUP_MS);
      try {
        const response = await fetch(KORTDATA_URL, { method: 'POST', headers, body, signal: controller.signal });
        if (!response.ok) throw new Error('kortdata svarede ' + response.status);
        const payload = await response.json();
        if (payload?.contract !== MAPDATA_PREVIEW_CONTRACT
          || payload.mapRuleVersion !== contract.mapRuleVersion
          || !validMapSnapshot(payload.snapshot, contract)
          || !Array.isArray(payload.center?.elements) || !Array.isArray(payload.product?.elements)) {
          throw new Error('kortdata var ufuldstændige');
        }
        const value = {
          snapshot: payload.snapshot,
          center: payload.center,
          product: payload.product,
          cacheStatus: response.headers.get('x-nf-cache') || 'UNKNOWN'
        };
        mapdataLocalCache.set(body, { savedAt: Date.now(), value });
        while (mapdataLocalCache.size > 6) mapdataLocalCache.delete(mapdataLocalCache.keys().next().value);
        return value;
      } finally {
        window.clearTimeout(deadline);
        /* En gammel aborteret request kan afslutte efter, at samme state er
           valgt igen. Sammenlign controller-identitet, så den aldrig rydder
           den nyere in-flight request med samme cache key. */
        if (activeMapdata?.controller === controller) activeMapdata = null;
      }
    })();
    activeMapdata = { key: body, controller, promise };
    return promise;
  }

  const asRing = geometry => geometry
    .filter(p => Number.isFinite(p?.lat) && Number.isFinite(p?.lon))
    .map(p => [p.lon, p.lat]);

  const samePoint = (a, b) => !!a && !!b && a[0] === b[0] && a[1] === b[1];

  function joinedMemberRings(element, role) {
    const pending = (element.members || [])
      .filter(member => member.type === 'way' && (member.role || 'outer') === role && Array.isArray(member.geometry))
      .map(member => asRing(member.geometry)).filter(ring => ring.length >= 2);
    const rings = [];
    while (pending.length) {
      let path = pending.shift().slice();
      let changed = true;
      while (changed && !samePoint(path[0], path.at(-1))) {
        changed = false;
        for (let index = 0; index < pending.length; index += 1) {
          const candidate = pending[index];
          if (samePoint(path.at(-1), candidate[0])) path.push(...candidate.slice(1));
          else if (samePoint(path.at(-1), candidate.at(-1))) path.push(...candidate.slice(0, -1).reverse());
          else if (samePoint(path[0], candidate.at(-1))) path.unshift(...candidate.slice(0, -1));
          else if (samePoint(path[0], candidate[0])) path.unshift(...candidate.slice(1).reverse());
          else continue;
          pending.splice(index, 1);
          changed = true;
          break;
        }
      }
      if (path.length >= 4 && samePoint(path[0], path.at(-1))) rings.push(path);
    }
    return rings;
  }

  function pointInRing(point, ring) {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const a = ring[i], b = ring[j];
      if (((a[1] > point[1]) !== (b[1] > point[1]))
        && point[0] < (b[0] - a[0]) * (point[1] - a[1]) / ((b[1] - a[1]) || 1e-12) + a[0]) inside = !inside;
    }
    return inside;
  }

  function osmAreas(element) {
    if (Array.isArray(element.geometry)) {
      const ring = asRing(element.geometry);
      return ring.length >= 3 ? [{ ring, holes: [] }] : [];
    }
    const outers = joinedMemberRings(element, 'outer');
    const inners = joinedMemberRings(element, 'inner');
    return outers.map(ring => ({
      ring,
      holes: inners.filter(hole => pointInRing(hole[0], ring))
    }));
  }

  function buildingIntersectsParent(polygons, reference, format = panoramaOf(reference)) {
    const half = [format.columns * reference.areaM / 2, format.rows * reference.areaM / 2];
    // Clip rings to the ONE shared parent rectangle for inclusion only. Keep
    // source polygons intact: the detail renderer owns clipping, holes and roofs.
    const clippedArea = ring => {
      let points = ring.map(([lon, lat]) => {
        const p = selectionLocalMeters(lon, lat, reference);
        return [p.east, p.north];
      });
      if (points.some(p => !p.every(Number.isFinite))) return 0;
      for (const axis of [0, 1]) for (const sign of [-1, 1]) {
        const output = [];
        for (let i = 0; i < points.length; i += 1) {
          const a = points[i], b = points[(i + 1) % points.length];
          const da = half[axis] - sign * a[axis], db = half[axis] - sign * b[axis];
          if (da >= 0) output.push(a);
          if ((da < 0 && db > 0) || (da > 0 && db < 0)) {
            const t = da / (da - db);
            output.push(a.map((value, j) => value + t * (b[j] - value)));
          }
        }
        points = output;
      }
      if (points.length < 3) return 0;
      // Translate before summing to avoid cancellation for small edge slivers.
      const [x, y] = points[0];
      return Math.abs(points.reduce((sum, a, i) => {
        const b = points[(i + 1) % points.length];
        return sum + (a[0] - x) * (b[1] - y) - (b[0] - x) * (a[1] - y);
      }, 0)) / 2;
    };
    return polygons.some(([outer, ...holes]) => outer?.length >= 3
      && clippedArea(outer) - holes.reduce((sum, ring) => sum + clippedArea(ring), 0) > 0);
  }

  /* OSM-hoejder, samme regler som kortdata.py i generatoren: ``height`` foerst,
     saa ``building:levels`` gange etagehoejden, ellers et gaet. */
  function buildingMetres(raw) {
    if (raw == null) return null;
    const match = String(raw).match(/^\s*([+-]?\d+(?:\.\d+)?)\s*(m|ft)?\s*$/);
    if (!match) return null;
    const value = Number(match[1]) * (match[2] === 'ft' ? .3048 : 1);
    return Number.isFinite(value) && value >= 0 ? value : null;
  }

  function buildingHeight(tags) {
    const raw = buildingMetres(tags.height) ?? buildingMetres(tags['building:height']);
    if (raw !== null) return raw;
    const levels = buildingMetres(tags['building:levels']) ?? buildingMetres(tags.levels);
    const roof = buildingMetres(tags['roof:height']) ?? ((buildingMetres(tags['roof:levels']) ?? 0) * MAP_RULES.buildings.level_height_m);
    if (levels !== null) return levels * MAP_RULES.buildings.level_height_m + roof;
    const defaults = MAP_RULES.buildings.height_by_type_m;
    if (defaults[tags.building]) return defaults[tags.building];
    for (const key of ['amenity', 'man_made', 'building:use']) {
      if (defaults[tags[key]]) return defaults[tags[key]];
    }
    return MAP_RULES.buildings.default_height_m;
  }

  function osmToLayers(json, previewState = null, surfaceOptions = {}) {
    const vand = [], groen = [], veje = [], bygninger = [], kyst = [];
    const seen = new Set(), parents = new Map();
    for (const group of json.elements || []) {
      if (group.tags?.type !== 'building') continue;
      const outlines = (group.members || []).filter(m => m.role === 'outline').map(m => m.type + '/' + m.ref);
      for (const part of (group.members || []).filter(m => m.role === 'part')) {
        const id = part.type + '/' + part.ref;
        parents.set(id, [...(parents.get(id) || []), ...outlines]);
      }
    }
    for (const element of json.elements || []) {
      const tags = element.tags || {}, osm_id = element.type + '/' + element.id;
      if (element.id != null && seen.has(osm_id)) continue;
      seen.add(osm_id);
      if (tags.type === 'building') continue;
      if (tags.natural === MAP_RULES.coastline.value && element.type === 'way' && Array.isArray(element.geometry)) {
        const pts = asRing(element.geometry);
        if (pts.length >= 2) kyst.push(pts);
        continue;
      }
      if (ROAD_W[tags.highway] && element.type === 'way' && Array.isArray(element.geometry)) {
        const pts = asRing(element.geometry);
        if (pts.length >= 2) veje.push({ pts, w: ROAD_W[tags.highway], highway: tags.highway });
        continue;
      }
      const isPart = tags['building:part'] && tags['building:part'] !== 'no';
      if ((tags.building && tags.building !== 'no') || isPart || tags.man_made === 'tower') {
        if (isPart && element.type === 'way') {
          const points = asRing(element.geometry || []);
          if (!samePoint(points[0], points.at(-1))) continue;
        }
        for (const area of osmAreas(element)) {
          bygninger.push({ ...area, height: buildingHeight(tags), tags: { ...tags }, osm_id, parent_ids: parents.get(osm_id) || [] });
        }
        continue;
      }
      const surface = SEMANTIC_SURFACE.classify(tags, surfaceOptions);
      if (surface !== 'WATER' && surface !== 'GREEN_LANDCOVER') continue;
      for (const area of osmAreas(element)) {
        (surface === 'WATER' ? vand : groen).push(area);
      }
    }
    // Store flader foerst: rammer loftet nedenfor, er det de smaa der ryger.
    const areal = area => Math.abs(area.ring.reduce((sum, p, i) => {
      const q = area.ring[(i + 1) % area.ring.length];
      return sum + (p[0] * q[1] - q[0] * p[1]);
    }, 0));
    vand.sort((a, b) => areal(b) - areal(a));
    groen.sort((a, b) => areal(b) - areal(a));
    bygninger.sort((a, b) => b.height - a.height);
    /* Et samlet panorama hentes i ét autoritativt bbox-kald. Fordel loftet
       ligeligt over felterne; ellers kan Overpass' rækkefølge fylde alle 700
       pladser med centrumbyen og efterlade yderfelterne uden relief. */
    let previewBuildings = bygninger;
    if (previewState && panoramaCount(previewState) > 1) {
      const format = panoramaOf(previewState);
      const buckets = Array.from({ length: format.columns * format.rows }, () => []);
      const halfWidth = format.columns * previewState.areaM / 2;
      const halfHeight = format.rows * previewState.areaM / 2;
      for (const building of bygninger) {
        const ring = building.ring || [];
        if (!ring.length) continue;
        const center = ring.reduce((sum, point) => ({ lon: sum.lon + point[0], lat: sum.lat + point[1] }), { lon: 0, lat: 0 });
        center.lon /= ring.length;
        center.lat /= ring.length;
        const local = selectionLocalMeters(center.lon, center.lat, previewState);
        if (!buildingIntersectsParent([[ring, ...(building.holes || [])]], previewState)) continue;
        // The centre chooses a single quota bucket; it never decides inclusion.
        const column = clamp(Math.floor((local.east + halfWidth) / previewState.areaM), 0, format.columns - 1);
        const row = clamp(Math.floor((halfHeight - local.north) / previewState.areaM), 0, format.rows - 1);
        buckets[row * format.columns + column].push(building);
      }
      previewBuildings = [];
      for (let offset = 0; previewBuildings.length < 7000; offset += 1) {
        let added = false;
        for (const bucket of buckets) {
          if (bucket[offset]) {
            previewBuildings.push(bucket[offset]);
            added = true;
            if (previewBuildings.length >= 7000) break;
          }
        }
        if (!added) break;
      }
    }
    return {
      vand: vand.slice(0, 120),
      groen: groen.slice(0, 200),
      veje: veje.slice(0, 700),
      bygninger: previewBuildings.slice(0, 7000),
      kyst,
      mapRuleVersion: MAP_RULES.map_rule_version
    };
  }

  if (localCartHarness) {
    root.__nfCityMapRulesDebug = Object.freeze({
      version: MAP_RULES.map_rule_version,
      selectionBbox: value => ({ ...selectionBbox(value) }),
      optionsForProduct: value => ({ ...SEMANTIC_SURFACE.optionsForProduct(value) }),
      osmToLayers: (value, options = {}) => osmToLayers(value, null, options)
    });
  }

  async function ensureOsmLayers(kilde = godkendtUdsnit()) {
    // Kilden gives eksplicit af previewknappen. Navigation og kurv maa ikke
    // kunne falde tilbage til state og starte et skjult produktionskald.
    const productStyle = state.style;
    const fingerprint = surfaceLayersFingerprint(kilde, productStyle);
    if (osmFingerprint === fingerprint && osmLayers) {
      osmFejl = false;
      osmCacheStatus = 'LOCAL';
      return osmLayers;
    }
    const id = ++osmRequest;
    const snap = { lat: kilde.lat, lon: kilde.lon, areaM: kilde.areaM,
      rotationDeg: kilde.rotationDeg || 0, panorama: { ...panoramaOf(kilde) } };
    osmFejl = false;
    try {
      const result = await fetchKortdata(snap);
      const surfaceOptions = SEMANTIC_SURFACE.optionsForProduct(productStyle);
      const layers = osmToLayers(result.center, null, surfaceOptions);
      const productLayers = osmToLayers(result.product, snap, surfaceOptions);
      // Et svar der kom for sent maa ikke overskrive et nyere udsnit.
      if (id !== osmRequest) return osmLayers;
      osmLayers = layers;
      osmFingerprint = fingerprint;
      productOsmLayers = productLayers;
      productOsmFingerprint = productLayersFingerprint(snap, panoramaOf(snap), productStyle);
      osmCacheStatus = result.cacheStatus;
      return layers;
    } catch (error) {
      if (id === osmRequest) {
        osmFejl = true;
        osmCacheStatus = '';
        console.warn('[bykort] kortdata til preview:', error.message);
      }
      return null;
    }
  }

  async function ensureProductOsmLayers(selection, centreLayers) {
    const productStyle = state.style;
    const fingerprint = productLayersFingerprint(selection, panoramaOf(selection), productStyle);
    const request = ++productOsmRequest;
    if (productOsmFingerprint === fingerprint && productOsmLayers) return productOsmLayers;
    try {
      const response = await fetchKortdata(selection);
      const combined = panoramaCount(selection) > 1;
      const layers = combined
        ? osmToLayers(response.product, selection, SEMANTIC_SURFACE.optionsForProduct(productStyle))
        : centreLayers;
      if (request !== productOsmRequest || !layers) return null;
      productOsmLayers = layers;
      productOsmFingerprint = fingerprint;
      return layers;
    } catch (error) {
      console.warn('[bykort] kortdata til hele værket:', error.message);
      return null;
    }
  }

  function collectResultBuildings(reference = godkendtUdsnit(), panorama = previewPanorama(),
    sourceMap = resultMap, buildingLayer = 'nf-result-buildings') {
    const currentProductLayers = productOsmFingerprint === productLayersFingerprint(reference, panorama)
      ? productOsmLayers : null;
    const cachedOsmBuildings = () => (currentProductLayers?.bygninger || []).slice(0, 7000).map((building, index) => ({
      ...building,
      id: `osm-${index}`,
      height: Number(building.height) || 12,
      polygons: [[building.ring, ...(building.holes || [])]]
    }));
    if (currentProductLayers) {
      const buildings = cachedOsmBuildings();
      const format = panoramaOf({ panorama });
      const counts = Array(format.columns * format.rows).fill(0);
      for (const building of buildings) {
        const ring = building.polygons[0][0];
        const center = ring.reduce((sum, p) => [sum[0] + p[0] / ring.length, sum[1] + p[1] / ring.length], [0, 0]);
        const local = selectionLocalMeters(center[0], center[1], reference);
        const column = clamp(Math.floor(local.east / reference.areaM + format.columns / 2), 0, format.columns - 1);
        const row = clamp(Math.floor(format.rows / 2 - local.north / reference.areaM), 0, format.rows - 1);
        counts[row * format.columns + column] += 1;
      }
      const stage = $('[data-result-stage]');
      if (stage && sourceMap === productCaptureMap) {
        stage.dataset.previewReliefFieldCounts = counts.join(',');
        stage.dataset.previewReliefSource = 'committed-full-bounds-osm';
        stage.dataset.previewReliefBounds = JSON.stringify(selectionBbox({ ...reference, panorama: format }, true));
      }
      return buildings;
    }
    if (!sourceMap?.getSource('carto')) return cachedOsmBuildings();
    let renderedFeatures = [];
    let sourceFeatures = [];
    try {
      /* ``queryRenderedFeatures`` er den stabile vej til de fliser, der faktisk
         dækker hele det indzoomede panoramaværk. ``querySourceFeatures`` kan
         kortvarigt være tom ved et kameraskift, selv om bygningerne allerede
         er tegnet; behold den som fallback for skjulte previewfaner. */
      if (sourceMap.getLayer(buildingLayer)) {
        renderedFeatures = sourceMap.queryRenderedFeatures(undefined, { layers: [buildingLayer] }) || [];
      }
      sourceFeatures = sourceMap.querySourceFeatures('carto', { sourceLayer: 'building' }) || [];
    } catch (error) {
      return cachedOsmBuildings();
    }
    const features = [...renderedFeatures, ...sourceFeatures];
    const resultStage = $('[data-result-stage]');
    if (resultStage) {
      const prefix = sourceMap === productCaptureMap ? 'Capture' : 'Result';
      resultStage.dataset[`preview${prefix}RenderedBuildingFeatures`] = String(renderedFeatures.length);
      resultStage.dataset[`preview${prefix}SourceBuildingFeatures`] = String(sourceFeatures.length);
    }
    const seen = new Set();
    const candidates = [];
    for (const feature of features) {
      const geometry = feature?.geometry;
      if (!geometry || !['Polygon', 'MultiPolygon'].includes(geometry.type)) continue;
      const polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
      const outer = polygons[0]?.[0];
      if (!Array.isArray(outer) || outer.length < 4) continue;
      const center = outer.reduce((sum, point) => ({ lon: sum.lon + Number(point[0]), lat: sum.lat + Number(point[1]) }), { lon: 0, lat: 0 });
      center.lon /= outer.length;
      center.lat /= outer.length;
      const local = selectionLocalMeters(center.lon, center.lat, reference);
      /* Ved multikort er resultMap fit'et til hele det committed værk. Bevar
         derfor bygninger i alle moduler, ikke kun centermodulets kvadrat. */
      const halfWidth = reference.areaM * Math.max(1, Number(panorama.columns) || 1) * .53;
      const halfHeight = reference.areaM * Math.max(1, Number(panorama.rows) || 1) * .53;
      if (panoramaCount({ panorama }) > 1) {
        if (!buildingIntersectsParent(polygons, reference, panorama)) continue;
      } else if (Math.abs(local.east) > halfWidth || Math.abs(local.north) > halfHeight) continue;
      const height = Number(feature.properties?.render_height || feature.properties?.height || 12);
      /* Vector-tile feature IDs er kun lokale for den enkelte flise. Den gamle
         nøgle brugte ID'et alene og slog derfor bygninger med fx id=17 i seks
         forskellige panoramafelter sammen til én. Geografien gør nøglen
         global for hele det committed værk. */
      const key = `${feature.id ?? 'feature'}:${center.lon.toFixed(6)}:${center.lat.toFixed(6)}:${Math.round(height)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      candidates.push({
        id: String(key),
        height: Number.isFinite(height) ? clamp(height, 2.5, 80) : 12,
        polygons,
        local
      });
    }
    const format = panoramaOf({ panorama });
    const buckets = Array.from({ length: format.columns * format.rows }, () => []);
    const halfWidth = reference.areaM * format.columns / 2;
    const halfHeight = reference.areaM * format.rows / 2;
    for (const building of candidates.sort((left, right) => right.height - left.height)) {
      const column = clamp(Math.floor((building.local.east + halfWidth) / reference.areaM), 0, format.columns - 1);
      const row = clamp(Math.floor((halfHeight - building.local.north) / reference.areaM), 0, format.rows - 1);
      buckets[row * format.columns + column].push(building);
    }
    if (resultStage && sourceMap === productCaptureMap) {
      resultStage.dataset.previewReliefFieldCounts = buckets.map(bucket => bucket.length).join(',');
    }
    const buildings = [];
    for (let offset = 0; buildings.length < 700; offset += 1) {
      let added = false;
      for (const bucket of buckets) {
        if (bucket[offset]) {
          const { local, ...building } = bucket[offset];
          buildings.push(building);
          added = true;
          if (buildings.length >= 700) break;
        }
      }
      if (!added) break;
    }
    /* Den godkendte OSM-geometri er et ægte relief og er allerede bundet til
       samme committed udsnit. Brug den som sidste sikkerhedsnet, hvis
       MapLibre endnu ikke har afleveret vector-tile features efter et hurtigt
       skift fra 1 × 1 til multikort. Derved bliver 3D aldrig en flad plakat. */
    return buildings.length ? buildings : cachedOsmBuildings();
  }

  function captureSelectionTexture(selection, sourceMap = resultMap) {
    if (!sourceMap) return '';
    const source = sourceMap.getCanvas();
    const cssWidth = source.clientWidth || 1;
    const pixelRatio = source.width / cssWidth;
    const points = selection.corners.map(point => sourceMap.project(point));
    const widthPx = Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y) * pixelRatio;
    const heightPx = Math.hypot(points[2].x - points[1].x, points[2].y - points[1].y) * pixelRatio;
    if (!Number.isFinite(widthPx) || !Number.isFinite(heightPx) || widthPx < 8 || heightPx < 8) return '';
    const center = points.reduce((sum, point) => ({ x: sum.x + point.x / 4, y: sum.y + point.y / 4 }), { x: 0, y: 0 });
    const angle = Math.atan2(points[1].y - points[0].y, points[1].x - points[0].x);
    const target = document.createElement('canvas');
    const longest = Math.max(widthPx, heightPx);
    target.width = Math.max(128, Math.round(768 * widthPx / longest));
    target.height = Math.max(128, Math.round(768 * heightPx / longest));
    const context = target.getContext('2d', { alpha: false });
    if (!context) return '';
    context.fillStyle = '#f2eee6';
    context.fillRect(0, 0, target.width, target.height);
    context.translate(target.width / 2, target.height / 2);
    context.rotate(-angle);
    const scale = target.width / widthPx;
    context.scale(scale, scale);
    try {
      context.drawImage(source, -center.x * pixelRatio, -center.y * pixelRatio);
      return target.toDataURL('image/jpeg', .82);
    } catch (error) {
      return '';
    }
  }

  function ensureProductCaptureMap() {
    if (productCaptureReady) return productCaptureReady;
    productCaptureReady = new Promise((resolve, reject) => {
      const container = document.createElement('div');
      container.dataset.productCaptureMap = '';
      container.setAttribute('aria-hidden', 'true');
      Object.assign(container.style, {
        position: 'fixed', left: '-10000px', top: '0',
        width: '512px', height: '512px', opacity: '0.01', pointerEvents: 'none'
      });
      document.body.append(container);
      productCaptureMap = new window.maplibregl.Map({
        container,
        style: 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json',
        center: [state.lon, state.lat],
        zoom: 14.2,
        pitch: 0,
        bearing: 0,
        interactive: false,
        preserveDrawingBuffer: true,
        attributionControl: false
      });
      productCaptureMap.once('load', () => {
        try {
          applyApprovedMapPalette(productCaptureMap);
          productCaptureMap.addLayer({
            id: 'nf-product-capture-buildings',
            type: 'fill-extrusion',
            source: 'carto',
            'source-layer': 'building',
            minzoom: 12,
            paint: {
              'fill-extrusion-color': previewMapPalette().building,
              'fill-extrusion-height': ['coalesce', ['get', 'render_height'], ['get', 'height'], 12],
              'fill-extrusion-base': ['coalesce', ['get', 'render_min_height'], 0],
              'fill-extrusion-opacity': .98
            }
          });
          resolve(productCaptureMap);
        } catch (error) { reject(error); }
      });
      productCaptureMap.on('error', event => {
        if (!productCaptureMap?.isStyleLoaded?.() && event?.error) reject(event.error);
      });
    });
    return productCaptureReady;
  }

  async function waitForProductCaptureData(captureMap, requestId) {
    /* ``idle`` er ikke en completion-kontrakt: én langsom eller fejlet tile
       kan forhindre eventen permanent, selv om alle tiles for det committed
       udsnit allerede er klar. Poll i stedet MapLibres tile-state. Kortet har
       fast størrelse og kamera fra committed bounds, så resultatet er
       uafhængigt af den synlige desktop-/mobilviewport. */
    const deadline = Date.now() + PRODUCT_CAPTURE_GIVEUP_MS;
    let renderObserved = false;
    let stableLoadedChecks = 0;
    captureMap.once('render', () => { renderObserved = true; });
    captureMap.triggerRepaint();
    while (Date.now() < deadline) {
      if (requestId !== productCaptureRequest) return 'stale';
      /* fitBounds kan kortvarigt efterlade ``areTilesLoaded`` paa true fra det
         forrige kamera, inden den foerste target-render har bestilt sine nye
         tiles. Kræv derfor en faktisk render og to stabile loaded-aflæsninger. */
      if (renderObserved && captureMap.areTilesLoaded?.()) {
        stableLoadedChecks += 1;
        if (stableLoadedChecks >= 2) return 'ready';
      } else {
        stableLoadedChecks = 0;
      }
      await new Promise(resolve => window.setTimeout(resolve, 120));
    }
    return requestId === productCaptureRequest ? 'timeout' : 'stale';
  }

  async function updateProductCapture(reference = godkendtUdsnit(), panorama = previewPanorama(), options = {}) {
    if (!window.maplibregl || !reference) return null;
    const id = ++productCaptureRequest;
    const format = panoramaOf({ panorama });
    const fingerprint = productLayersFingerprint(reference, format, state.style);
    try {
      const captureMap = await ensureProductCaptureMap();
      if (id !== productCaptureRequest) return null;
      /* Fast opløsning pr. 22 cm-felt holder kortets zoom og detaljeniveau
         stabilt. 3 × 2 får derfor 1260 × 840 px i stedet for at blive presset
         ned i det synlige kvadratiske 566 px-preview. */
      const modulePixels = 700;
      const width = Math.max(512, format.columns * modulePixels);
      const height = Math.max(512, format.rows * modulePixels);
      const container = captureMap.getContainer();
      container.style.width = `${width}px`;
      container.style.height = `${height}px`;
      captureMap.resize();
      applyApprovedMapPalette(captureMap);
      const selection = makeSelection({ ...reference, panorama: format });
      const bounds = selection.corners.reduce(
        (current, point) => current.extend(point),
        new window.maplibregl.LngLatBounds(selection.corners[0], selection.corners[0])
      );
      captureMap.fitBounds(bounds, {
        padding: 8,
        duration: 0,
        pitch: 0,
        bearing: reference.rotationDeg || 0
      });
      const captureState = await waitForProductCaptureData(captureMap, id);
      if (captureState === 'stale') return null;
      if (captureState !== 'ready') throw new Error('kortfliserne blev ikke klar inden for tidsgrænsen');
      productGeometry = {
        buildings: collectResultBuildings(reference, format, captureMap, 'nf-product-capture-buildings'),
        textureDataUrl: captureSelectionTexture(selection, captureMap)
      };
      if (!productGeometry.textureDataUrl) throw new Error('det godkendte kortbillede mangler');
      productCaptureFingerprint = fingerprint;
      const stage = $('[data-result-stage]');
      if (stage) {
        stage.dataset.previewCaptureWidth = String(width);
        stage.dataset.previewCaptureHeight = String(height);
      }
      if (options.publish !== false) publishProductSnapshot();
      return productGeometry;
    } catch (error) {
      console.warn('[bykort] højopløst product-capture:', error.message);
      return null;
    }
  }

  function publishProductSnapshot() {
    if ((previewRequestBusy && previewRequestPhase !== 'prepare') || previewRequestState === 'error') return;
    if (!erGodkendt()) return;
    const approvedSelection = godkendtUdsnit();
    const committedPanorama = previewPanorama();
    const panorama = panoramaOpsummering({ ...approvedSelection, panorama: committedPanorama });
    const combined = panorama.count > 1;
    /* 3D-viseren tegner hele det samlede værk. Ved 1 × 1 er denne firkant
       identisk med det hidtidige modul, så enkeltkortets geometri bevares. */
    const selection = combined
      ? makeSelection({ ...approvedSelection, panorama: committedPanorama })
      : makeModuleSelection(approvedSelection);
    const currentCaptureFingerprint = productLayersFingerprint(approvedSelection, committedPanorama, state.style);
    if (productCaptureFingerprint !== currentCaptureFingerprint || !productGeometry.textureDataUrl) return;
    const detail = {
      state: {
        place: approvedSelection.place || state.place,
        lat: approvedSelection.lat,
        lon: approvedSelection.lon,
        areaM: approvedSelection.areaM,
        sizeCm: MODUL_CM,
        panorama,
        style: state.style,
        frame: state.frame,
        rotationDeg: approvedSelection.rotationDeg
      },
      selection: { corners: selection.corners },
      /* The approved 3D view, 2D card and wall all share this capture. */
      buildings: productGeometry.buildings,
      /* De eksisterende OSM-fladelag er modulbaserede. Multikort bruger det
         autoritative samlede kortbillede plus de synlige bygninger som ægte
         ekstruderet geometri, så 3D aldrig falder tilbage til en flad plakat. */
      layers: !combined && osmFingerprint === surfaceLayersFingerprint(approvedSelection, state.style) ? osmLayers : null,
      textureDataUrl: productGeometry.textureDataUrl,
      captureSource: 'high-resolution-committed-map'
    };
    root.__nfCityProductSnapshot = detail;
    if (erGodkendt() && detail.textureDataUrl) {
      const artwork = $('[data-approved-map]');
      if (artwork) {
        if (artwork.src !== detail.textureDataUrl) artwork.src = detail.textureDataUrl;
        artwork.hidden = false;
        artwork.dataset.fingerprint = currentCaptureFingerprint;
      }
      if ($('[data-result-stage]')?.dataset.resultView === 'vaeg') opdaterVaeg();
    }
    root.dispatchEvent(new CustomEvent('nf:city-product-update', { detail }));
  }

  function setSelectionCursor(cursor = '') {
    if (map) map.getCanvas().style.cursor = cursor;
  }

  function selectionAngleFromCenter(lngLat, center = { lon: state.lon, lat: state.lat }) {
    const east = (lngLat.lng - center.lon) * 111320 * Math.max(.01, Math.cos(center.lat * Math.PI / 180));
    const north = (lngLat.lat - center.lat) * 110574;
    return Math.atan2(north, east) * 180 / Math.PI;
  }

  function setSelectionToolMode() {
    $('[data-selection-hint]').textContent = selectionLocked
      ? 'Udsnittet er låst. Lås op for at flytte kortet under rammen.'
      : 'Flyt kortet under den faste ramme · drej med skyderen';
    setSelectionCursor(selectionLocked ? '' : '');
  }

  function updateSelectionToolsPosition() {
    layoutCropOverlay();
  }

  function setSelectionInteraction(active) {
    root.querySelector('.nf-city-builder__map-wrap').classList.toggle('is-selection-dragging', active);
    if (!map) return;
    if (active) {
      map.dragPan.disable();
      map.touchZoomRotate.disable();
      setSelectionCursor('grabbing');
    } else {
      map.dragPan.enable();
      setSelectionCursor('');
    }
  }

  function startSelectionDrag(event) {
    if (selectionLocked || !event.lngLat) return;
    event.preventDefault?.();
    const center = { lon: state.lon, lat: state.lat };
    selectionDrag = selectionToolMode === 'rotate'
      ? {
          type: 'rotate',
          startAngle: selectionAngleFromCenter(event.lngLat, center),
          rotationDeg: state.rotationDeg,
          center
        }
      : {
          type: 'move',
          start: { lng: event.lngLat.lng, lat: event.lngLat.lat },
          center: { lng: state.lon, lat: state.lat }
        };
    setSelectionInteraction(true);
  }

  function startHandleDrag(event) {
    if (selectionLocked || !event.lngLat) return;
    const handleIndex = Number(event.features?.[0]?.properties?.index);
    if (!Number.isInteger(handleIndex)) return;
    event.preventDefault?.();
    const selection = makeSelection();
    selectionDrag = {
      type: 'resize',
      handleIndex,
      rotationDeg: state.rotationDeg,
      opposite: selection.corners[(handleIndex + 2) % 4]
    };
    setSelectionInteraction(true);
  }

  function moveSelectionDrag(event) {
    if (!selectionDrag || !event.lngLat) return;
    event.preventDefault?.();
    let patch;

    if (selectionDrag.type === 'move') {
      patch = {
        lon: selectionDrag.center.lng + event.lngLat.lng - selectionDrag.start.lng,
        lat: selectionDrag.center.lat + event.lngLat.lat - selectionDrag.start.lat
      };
    } else if (selectionDrag.type === 'rotate') {
      const angle = selectionAngleFromCenter(event.lngLat, selectionDrag.center);
      patch = { rotationDeg: selectionDrag.rotationDeg + angle - selectionDrag.startAngle };
    } else {
      const opposite = selectionDrag.opposite;
      const referenceLat = (event.lngLat.lat + opposite[1]) / 2;
      const lonMeters = 111320 * Math.max(.01, Math.cos(referenceLat * Math.PI / 180));
      const pointerEast = (event.lngLat.lng - opposite[0]) * lonMeters;
      const pointerNorth = (event.lngLat.lat - opposite[1]) * 110574;
      /* Traekket skalerer modulet, saa hele panoramaet vokser og skrumper i ét
         stykke og beholder forholdet columns:rows. Retningen er rektanglets
         egen diagonal -- ved 1 x 1 er den (±1, ±1)/√2 som foer, saa et enkelt
         kort opfoerer sig praecis som hidtil. */
      const { columns, rows } = panoramaOf(state);
      const diagonalUnits = Math.hypot(columns, rows);
      const signs = [[-1, 1], [1, 1], [1, -1], [-1, -1]][selectionDrag.handleIndex];
      const radians = selectionDrag.rotationDeg * Math.PI / 180;
      const unitEast = signs[0] * columns / diagonalUnits;
      const unitNorth = signs[1] * rows / diagonalUnits;
      const diagonalEast = unitEast * Math.cos(radians) - unitNorth * Math.sin(radians);
      const diagonalNorth = unitEast * Math.sin(radians) + unitNorth * Math.cos(radians);
      const diagonalM = Math.max(500 * diagonalUnits, pointerEast * diagonalEast + pointerNorth * diagonalNorth);
      const centerEast = diagonalEast * diagonalM / 2;
      const centerNorth = diagonalNorth * diagonalM / 2;
      patch = {
        lat: opposite[1] + centerNorth / 110574,
        lon: opposite[0] + centerEast / lonMeters,
        areaM: diagonalM / diagonalUnits
      };
    }

    state = normalize({ ...state, ...patch });
    /* Trækket ændrer kun editorens arbejdskopi. Resultatkort, produktviewer og
       ordregrundlag må stå urørt, indtil kunden trykker “Vælg kort”. */
    render({ selectionOnly: true });
    updateSelection(false);
  }

  function endSelectionDrag() {
    if (!selectionDrag) return;
    const action = selectionDrag.type;
    selectionDrag = null;
    setSelectionInteraction(false);
    update({ place: 'Valgt kortområde' }, { selection: true });
    $('[data-search-status]').textContent = action === 'resize'
      ? 'Kortområdets størrelse er ændret.'
      : action === 'rotate'
        ? `Kortområdet er roteret til ${Math.round(state.rotationDeg)}°.`
        : 'Kortområdet er flyttet. Dit nye udsnit er valgt.';
    publishCropSelected(action);
  }

  function bindSelectionInteractions() {
    if (selectionInteractionsBound || !map) return;
    selectionInteractionsBound = true;
    map.on('move', layoutCropOverlay);
    map.on('drag', syncSelectionFromMap);
    map.on('resize', () => {
      layoutCropOverlay();
    });
    map.on('moveend', event => {
      if (state.mapMode === '3d') {
        const pitch = Number(map.getPitch());
        const bearing = Number(map.getBearing());
        if (Number.isFinite(pitch) && pitch > 0) last3dCamera.pitch = pitch;
        if (Number.isFinite(bearing)) last3dCamera.bearing = bearing;
      }
      if (event?.originalEvent) syncSelectionFromMap();
    });
    map.on('zoomend', event => {
      if (event?.originalEvent) syncSelectionFromMap();
    });
  }

  const SELECTOR_BASEMAP = Object.freeze({
    tiles: Object.freeze(['https://tile.openstreetmap.org/{z}/{x}/{y}.png']),
    attribution: '© OpenStreetMap contributors',
    maxzoom: 19,
    tileSize: 256
  });

  function selectorBasemapStyle() {
    return {
      version: 8,
      name: 'nordform-selector-osm-standard',
      sources: {
        'osm-standard': {
          type: 'raster',
          tiles: SELECTOR_BASEMAP.tiles.slice(),
          tileSize: SELECTOR_BASEMAP.tileSize,
          attribution: SELECTOR_BASEMAP.attribution,
          maxzoom: SELECTOR_BASEMAP.maxzoom
        }
      },
      layers: [{ id: 'osm-standard', type: 'raster', source: 'osm-standard' }]
    };
  }

  function addMapLayers() {
    if (!map || !map.isStyleLoaded()) return;
    /* Venstre selector er OSM Carto Standard raster (samme kilde som
       Production Center /generer). Produktpaletten hoerer kun til hoejre
       preview og capture. Ingen prefetch: MapLibre henter kun viewport. */

    if (!map.getSource('nf-selection')) {
      map.addSource('nf-selection', { type: 'geojson', data: makeSelection().geojson });
      map.addLayer({
        id: 'nf-selection-fill', type: 'fill', source: 'nf-selection',
        filter: ['==', ['get', 'role'], 'area'],
        paint: { 'fill-color': '#b5965c', 'fill-opacity': 0 }
      });
      map.addLayer({
        id: 'nf-selection-line', type: 'line', source: 'nf-selection',
        filter: ['==', ['get', 'role'], 'area'],
        paint: { 'line-color': '#876d3f', 'line-width': 0 }
      });
      /* Felterne ligger mellem fladen og hjoernegrebene, saa de ses paa kortet
         uden at daekke det, kunden skal trykke paa. */
      map.addSource('nf-panorama-grid', { type: 'geojson', data: panoramaCellsGeojson() });
      map.addLayer({
        id: 'nf-panorama-grid', type: 'line', source: 'nf-panorama-grid',
        paint: { 'line-color': '#876d3f', 'line-width': 1.8, 'line-opacity': 0 }
      });
      map.addLayer({
        id: 'nf-selection-handles', type: 'circle', source: 'nf-selection',
        filter: ['==', ['get', 'role'], 'handle'],
        paint: { 'circle-radius': 0, 'circle-color': '#fffefa', 'circle-stroke-width': 0 }
      });
    }

    if (map.getSource('carto') && !map.getLayer('nf-buildings')) {
      map.addLayer({
        id: 'nf-buildings', type: 'fill-extrusion', source: 'carto', 'source-layer': 'building', minzoom: 14,
        layout: { visibility: 'none' },
        paint: {
          'fill-extrusion-color': previewMapPalette().building,
          'fill-extrusion-height': ['coalesce', ['get', 'render_height'], ['get', 'height'], 12],
          'fill-extrusion-base': ['coalesce', ['get', 'render_min_height'], 0],
          'fill-extrusion-opacity': .88
        }
      });
    }
    if (!map.getSource('nf-elevation')) {
      map.addSource('nf-elevation', {
        type: 'raster-dem',
        tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'],
        tileSize: 256,
        encoding: 'terrarium',
        maxzoom: 15
      });
    }
    if (!map.getLayer('nf-hillshade')) {
      map.addLayer({
        id: 'nf-hillshade', type: 'hillshade', source: 'nf-elevation',
        layout: { visibility: 'none' },
        paint: { 'hillshade-shadow-color': '#493f31', 'hillshade-highlight-color': '#fff8e9', 'hillshade-exaggeration': .2 }
      });
    }

    mapReady = true;
    bindSelectionInteractions();
    /* Indpas udsnittet ved foerste visning. Uden det stod firkanten og fyldte
       hele kortet, saa de fire hjoernegreb laa uden for billedet -- og saa er
       det ikke til at se, at feltet kan flyttes, drejes og gøres stoerre.
       Det er kundens foerste indtryk af, hvad siden overhovedet kan. */
    /* Indpasningen udskydes én frame og maaler containeren igen foerst.
       #map er hoejdesat i vh, og MapLibre maaler sin container én gang i
       konstruktoeren. Maales den foer layoutet er faerdigt, regner fitBounds
       paa et for lavt kort, og saa havner de oeverste hjoerner alligevel uden
       for billedet -- praecis den fejl vi ville af med. */
    window.requestAnimationFrame(() => {
      map.resize();
      updateSelection(true);
    });
    applyMapMode(state.mapMode, false);
  }

  function initMap() {
    const status = $('[data-map-status]');
    if (!window.maplibregl) {
      status.textContent = 'Kortet kunne ikke indlæses. Prøv at genindlæse siden.';
      return;
    }
    map = new window.maplibregl.Map({
      container: $('[data-map]'),
      /* Samme OSM Standard raster som Production Center. URL kan skiftes i
         SELECTOR_BASEMAP uden at røre crop eller preview. */
      style: selectorBasemapStyle(),
      center: [state.lon, state.lat],
      zoom: 15.1,
      pitch: 0,
      bearing: 0,
      attributionControl: true
    });
    /* Rammen har fast geografisk dækning. Kun panorering er kundevendt;
       programmatisk fitBounds bruges fortsat ved søgning og størrelsesskift. */
    map.scrollZoom.disable();
    map.touchZoomRotate.disable();
    map.doubleClickZoom.disable();
    map.boxZoom.disable();
    map.dragRotate.disable();
    map.keyboard.disable();
    map.touchPitch?.disable();
    map.on('load', addMapLayers);
    map.on('style.load', addMapLayers);
    map.on('moveend', () => {
      if (state.mapMode !== '3d') return;
      const pitch = Number(map.getPitch());
      const bearing = Number(map.getBearing());
      if (Number.isFinite(pitch) && pitch > 0) last3dCamera.pitch = pitch;
      if (Number.isFinite(bearing)) last3dCamera.bearing = bearing;
    });
    map.on('error', event => {
      if (!event?.error) return;
      status.textContent = 'En del af kortet kunne ikke indlæses. Dine valg er stadig gemt.';
    });
  }

  function setMapLayerVisibility(layerId, visible) {
    if (mapReady && map.getLayer(layerId)) map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
  }

  /* Én kilde til den owner-godkendte kartografi. Kun CARTO-baggrundens
     autoritative source-lag ændres; vores udsnitsramme, labels og UI-lag
     forbliver urørte. Hvert paint-kald er isoleret, så et tredjeparts-lag,
     der ændrer type, ikke kan afbryde resten af kortopsætningen. */
  function applyApprovedMapPalette(target) {
    if (!target) return;
    const palette = previewMapPalette();
    const isWhite = state.style === 'hvid';
    if (!previewLayerDefaults.has(target)) previewLayerDefaults.set(target, new Map());
    const defaults = previewLayerDefaults.get(target);
    const layers = target?.getStyle?.()?.layers || [];
    for (const layer of layers) {
      let property = '';
      let color = '';
      const sourceLayer = String(layer['source-layer'] || '');
      const id = String(layer.id || '');
      if (layer.source === 'carto' && layer.type === 'symbol') {
        const key = `${id}|visibility`;
        if (!defaults.has(key)) defaults.set(key, layer.layout?.visibility || 'visible');
        target.setLayoutProperty(id, 'visibility', isWhite ? 'none' : defaults.get(key));
        continue;
      }
      if (layer.type === 'background' && id === 'background') {
        property = 'background-color';
        color = palette.ground;
      } else if (layer.source === 'carto' && layer.type === 'fill') {
        property = 'fill-color';
        if (sourceLayer === 'building') color = palette.building;
        else if (sourceLayer === 'water') color = palette.water;
        else if (sourceLayer === 'park' || sourceLayer === 'landcover'
          || (sourceLayer === 'landuse' && id !== 'landuse_residential')) color = palette.green;
        else if (id === 'landuse_residential') color = palette.ground;
      } else if (layer.source === 'carto' && layer.type === 'line') {
        property = 'line-color';
        if (sourceLayer === 'waterway') color = palette.water;
        else if (sourceLayer === 'transportation') color = palette.road;
      } else if (layer.source === 'carto' && layer.type === 'fill-extrusion') {
        property = 'fill-extrusion-color';
        if (sourceLayer === 'building') color = palette.building;
      }
      if (!property) continue;
      const key = `${id}|${property}`;
      if (!defaults.has(key)) defaults.set(key, layer.paint?.[property] ?? null);
      try {
        target.setPaintProperty(layer.id, property, isWhite ? '#FFFFFF' : (color || defaults.get(key)));
        if (property === 'fill-color') {
          const outlineKey = `${id}|fill-outline-color`;
          if (!defaults.has(outlineKey)) defaults.set(outlineKey, layer.paint?.['fill-outline-color'] ?? null);
          target.setPaintProperty(layer.id, 'fill-outline-color', isWhite ? '#FFFFFF' : defaults.get(outlineKey));
        }
      }
      catch (error) { console.warn('[bykort] kortpalette sprang lag over:', layer.id); }
    }
  }

  function applyMapMode(mode, animate = true) {
    const nextMode = mode === '3d' ? '3d' : '2d';
    if (state.mapMode === '3d' && nextMode === '2d' && map) {
      last3dCamera = { pitch: Number(map.getPitch()) || 54, bearing: Number(map.getBearing()) || -18 };
    }
    state = normalize({ ...state, mapMode: nextMode });
    $$('[data-map-mode]').forEach(button => {
      button.setAttribute('aria-pressed', String(button.dataset.mapMode === nextMode));
    });

    if (mapReady) {
      const is3d = nextMode === '3d';
      setMapLayerVisibility('nf-buildings', is3d);
      setMapLayerVisibility('nf-hillshade', is3d);
      map.setTerrain(is3d && map.getSource('nf-elevation') ? { source: 'nf-elevation', exaggeration: 1 } : null);
      const camera = is3d ? last3dCamera : { pitch: 0, bearing: 0 };
      const duration = animate && !reducedMotion() ? 420 : 0;
      if (duration) map.easeTo({ ...camera, duration, essential: false });
      else map.jumpTo(camera);
    }

    $('[data-map-status]').textContent = nextMode === '3d'
      ? '3D aktiv · bygninger og terræn ses i perspektiv'
      : '2D aktiv · kortet ses direkte ovenfra';
  }

  function setResultLayerVisibility(layerId, visible) {
    if (resultMapReady && resultMap.getLayer(layerId)) resultMap.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
  }

  function updateResultPreview(fit = true) {
    if (!resultMapReady || !resultMap) return;
    applyApprovedMapPalette(resultMap);
    /* MapLibre sender ogsaa idle efter rene container-resizes. Kun en
       eksplicit previewopdatering maa derfor aabne for et nyt produktsnapshot;
       ellers ville et breakpoint eller en rotation af telefonen genbygge hele
       city-geometrien uden at kundens valg havde aendret sig. */
    resultSnapshotPending = true;
    const isWhite = state.style === 'hvid';
    setResultLayerVisibility('nf-result-buildings', true);
    setResultLayerVisibility('nf-result-hillshade', true);
    if (resultMap.getLayer('nf-result-buildings')) {
      resultMap.setPaintProperty(
        'nf-result-buildings',
        'fill-extrusion-color',
        previewMapPalette().building
      );
      resultMap.setPaintProperty('nf-result-buildings', 'fill-extrusion-opacity', isWhite ? .98 : .92);
    }
    if (resultMap.getLayer('nf-result-hillshade')) {
      resultMap.setPaintProperty('nf-result-hillshade', 'hillshade-shadow-color', isWhite ? '#c4c4c1' : '#493f31');
      resultMap.setPaintProperty('nf-result-hillshade', 'hillshade-highlight-color', isWhite ? '#ffffff' : '#fff8e9');
      resultMap.setPaintProperty('nf-result-hillshade', 'hillshade-accent-color', isWhite ? '#e3e3e0' : '#8d7a5a');
      resultMap.setPaintProperty('nf-result-hillshade', 'hillshade-exaggeration', isWhite ? .34 : .2);
    }
    resultMap.setTerrain(null);
    resultMap.getSource('nf-panorama-grid')?.setData(resultatCeller());
    resultMap.getSource('nf-panorama-outline')?.setData(resultatYderramme());

    if (fit) {
      const { corners } = makeSelection({ ...godkendtUdsnit(), panorama: previewPanorama() });
      const bounds = corners.reduce(
        (current, point) => current.extend(point),
        new window.maplibregl.LngLatBounds(corners[0], corners[0])
      );
      resultMap.fitBounds(bounds, {
        /* Plads i bunden til billedteksten, naar den staar der. Uden det laa
           den hen over nederste raekke felter ved 10 x 10. */
        padding: panoramaAktiv({ panorama: previewPanorama() }) ? { top: 12, right: 12, bottom: 44, left: 12 } : 10,
        duration: reducedMotion() ? 0 : 420,
        pitch: 0,
        bearing: godkendtUdsnit().rotationDeg
      });
    } else window.requestAnimationFrame(publishProductSnapshot);
  }

  function addResultMapLayers() {
    if (!resultMap || !resultMap.isStyleLoaded()) return;
    applyApprovedMapPalette(resultMap);
    if (resultMap.getSource('carto') && !resultMap.getLayer('nf-result-buildings')) {
      resultMap.addLayer({
        id: 'nf-result-buildings', type: 'fill-extrusion', source: 'carto', 'source-layer': 'building', minzoom: 13,
        layout: { visibility: 'visible' },
        paint: {
          'fill-extrusion-color': previewMapPalette().building,
          'fill-extrusion-height': ['coalesce', ['get', 'render_height'], ['get', 'height'], 12],
          'fill-extrusion-base': ['coalesce', ['get', 'render_min_height'], 0],
          'fill-extrusion-opacity': .92
        }
      });
    }
    if (!resultMap.getSource('nf-result-elevation')) {
      resultMap.addSource('nf-result-elevation', {
        type: 'raster-dem',
        tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'],
        tileSize: 256,
        encoding: 'terrarium',
        maxzoom: 15
      });
    }
    if (!resultMap.getLayer('nf-result-hillshade')) {
      resultMap.addLayer({
        id: 'nf-result-hillshade', type: 'hillshade', source: 'nf-result-elevation',
        layout: { visibility: 'visible' },
        paint: { 'hillshade-shadow-color': '#493f31', 'hillshade-highlight-color': '#fff8e9', 'hillshade-accent-color': '#8d7a5a', 'hillshade-exaggeration': .2 }
      });
    }
    /* Resultatkortet viser hele panoramaet med gitteret. Det er den korrekte
       2D-visning af et flerdelt motiv -- se kommentaren ved ``updateResultPreview``
       om hvorfor 3D-modellen viser ét modul. */
    /* Panoramaet paa resultatkortet er ikke et gitter -- det er de indrammede
       kort, kunden koeber. Hvert modul faar sin egen moerke kant og lidt luft
       til naboen, saa et 3 x 2 laeses som seks selvstaendige billeder, der
       tilsammen danner ét motiv.

       To linjelag oven paa hinanden: et bredt moerkt, der er selve rammen, og
       et smalt lyst indeni, som giver kanten dybde. Ingen fyldfarve -- kortet
       skal kunne ses gennem felterne. */
    /* Vagten spoerger til lagene og ikke til kilden. Fejler et ``addLayer``,
       er kilden allerede oprettet, og en vagt paa kilden ville betyde, at
       lagene aldrig blev forsoegt igen. */
    if (!resultMap.getSource('nf-panorama-grid')) {
      resultMap.addSource('nf-panorama-grid', { type: 'geojson', data: resultatCeller() });
    }
    if (!resultMap.getSource('nf-panorama-outline')) {
      resultMap.addSource('nf-panorama-outline', { type: 'geojson', data: resultatYderramme() });
    }
    if (!resultMap.getLayer('nf-panorama-ramme')) {
      /* ``line-join`` hoerer i ``layout``, ikke i ``paint``. Stod den i paint,
         afviste MapLibre hele laget -- og fejlen gik til kortets ``error``-
         haendelse i stedet for konsollen, saa resultatkortet stod tomt uden at
         nogen sagde noget. Kilden var der; lagene var aldrig blevet oprettet. */
      resultMap.addLayer({
        id: 'nf-panorama-ramme', type: 'line', source: 'nf-panorama-outline',
        layout: { 'line-join': 'round' },
        paint: { 'line-color': '#2d2a22', 'line-width': 5, 'line-opacity': .92 }
      });
      resultMap.addLayer({
        id: 'nf-panorama-grid', type: 'line', source: 'nf-panorama-grid',
        layout: { 'line-join': 'round' },
        paint: { 'line-color': '#2d2a22', 'line-width': 1.2, 'line-opacity': .78 }
      });
    }
    resultMapReady = true;
    updateResultPreview(true);
  }

  function initResultMap() {
    if (resultMap || !window.maplibregl) return;
    resultMap = new window.maplibregl.Map({
      container: $('[data-result-map]'),
      /* Voyager frem for Positron. Positron er tegnet til at traede i
         baggrunden bag data, og her ER kortet opgaven: kunden skal kunne
         genkende sit sted og se hvad der kommer med paa pladen. Voyager er
         samme kilde og samme lagnavne, men med vand, groent og vejnet i
         farver man kan skelne. Foerste forsoeg var at overskrive Positrons
         enkelte lag efter indlaesning; det knaekkede udsnitsfirkanten, fordi
         et afvist setPaintProperty stopper resten af opsaetningen. */
      style: 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json',
      center: [state.lon, state.lat],
      zoom: 15.1,
      pitch: 0,
      bearing: 0,
      interactive: false,
      preserveDrawingBuffer: true,
      attributionControl: false
    });
    resultMap.on('load', addResultMapLayers);
    resultMap.on('style.load', addResultMapLayers);
    resultMap.on('idle', () => {
      if (!resultSnapshotPending) return;
      resultSnapshotPending = false;
      publishProductSnapshot();
      /* Kort og væg bruger det samme godkendte textureDataUrl som 3D. */
    });
    resultMap.on('error', event => {
      if (!event?.error) return;
      $('[data-result-status]').textContent = 'Forhåndsvisningen kunne ikke indlæses helt. Dine valg er stadig gemt.';
    });
  }

  function scheduleResultPreview() {
    if (resultPreviewFrame) window.cancelAnimationFrame(resultPreviewFrame);
    resultPreviewFrame = window.requestAnimationFrame(() => {
      resultPreviewFrame = 0;
      initResultMap();
      resultMap?.resize();
      updateResultPreview(true);
    });
  }

  async function waitForPreviewPaint() {
    await new Promise(resolve => window.requestAnimationFrame(resolve));
  }

  async function godkendUdsnit() {
    const candidate = {
      place: state.place,
      lat: state.lat,
      lon: state.lon,
      areaM: state.areaM,
      rotationDeg: state.rotationDeg,
      panorama: { ...panoramaOf(state) }
    };
    const requestId = ++previewRequestGeneration;
    const requestFingerprint = selectionIntentFingerprint(candidate);
    previewRequestBusy = true;
    previewRequestState = 'loading';
    previewRequestPhase = 'fetch';
    previewRequestFingerprint = requestFingerprint;
    const approvedMap = $('[data-approved-map]');
    if (approvedMap) approvedMap.hidden = true;
    const wallArtwork = $('[data-wall]');
    if (wallArtwork?.dataset && wallArtwork.style) {
      wallArtwork.dataset.harBillede = 'false';
      wallArtwork.style.removeProperty('--nf-vaeg-billede');
    }
    publishFunnelEvent('nf_city_preview_requested');
    render();

    const centreLayers = await ensureOsmLayers(candidate);
    if (requestId !== previewRequestGeneration || requestFingerprint !== selectionIntentFingerprint(state)) return false;
    previewRequestPhase = 'classify';
    render();
    await waitForPreviewPaint();
    const layers = centreLayers ? await ensureProductOsmLayers(candidate, centreLayers) : null;
    if (requestId !== previewRequestGeneration
      || requestFingerprint !== selectionIntentFingerprint(state)) return false;
    previewRequestPhase = 'assemble';
    render();
    await waitForPreviewPaint();
    previewRequestPhase = 'model';
    render();
    const geometry = layers
      ? await updateProductCapture(candidate, panoramaOf(candidate), { publish: false })
      : null;
    if (requestId !== previewRequestGeneration
      || requestFingerprint !== selectionIntentFingerprint(state)) return false;
    previewRequestPhase = 'materials';
    render();
    /* Et urbant centermodul med bygninger maa aldrig blive godkendt som en
       flad 0-bygningspreview. Et eksplicit vand-/naturudsnit uden bygninger er
       fortsat gyldigt og bevarer Round 5D's water-only-kontrakt. */
    const forventerBygninger = (layers?.bygninger || []).length > 0;
    if (!layers || !geometry || (forventerBygninger && !geometry.buildings.length)) {
      previewRequestBusy = false;
      previewRequestState = 'error';
      previewRequestPhase = 'error';
      publishFunnelEvent('nf_city_preview_failed', { reason: 'map_data_unavailable' });
      render();
      return false;
    }

    await waitForPreviewPaint();
    if (requestId !== previewRequestGeneration
      || requestFingerprint !== selectionIntentFingerprint(state)) return false;
    /* Bind først kundevalget efter materialetrinnets paint. Et valg, der
       ændres under arbejdet, må ikke nå at erstatte det senest godkendte
       snapshot, heller ikke i det korte vindue mellem to frames. */
    godkendt = candidate;
    setApprovedDesign(captureApprovedDesign(godkendt));
    previewRequestPhase = 'prepare';
    render();
    /* Geometrien er klar før committen, så dette snapshot er den første
       kundevendte tilstand og den samme kilde for 3D, Kort og På væggen. */
    publishProductSnapshot();
    saetResultatVisning('model');
    if (resultMapReady) updateResultPreview(true);
    await waitForPreviewPaint();
    if (requestId !== previewRequestGeneration
      || requestFingerprint !== selectionIntentFingerprint(state)) return false;
    previewRequestBusy = false;
    previewRequestState = 'success';
    previewRequestPhase = 'ready';
    publishFunnelEvent('nf_city_preview_succeeded', { cache: osmCacheStatus || 'UNKNOWN' });
    render();
    return true;
  }

  function updateSelection(fit = false) {
    const selection = makeSelection();
    if (!mapReady) return;
    map.getSource('nf-selection')?.setData(selection.geojson);
    map.getSource('nf-panorama-grid')?.setData(tomGeojson());
    layoutCropOverlay();
    if (fit) {
      const mapNode = $('[data-map]');
      const rect = cropFrameRect();
      const bounds = selection.corners.reduce(
        (current, point) => current.extend(point),
        new window.maplibregl.LngLatBounds(selection.corners[0], selection.corners[0])
      );
      // Sync follows camera motion, not tile/network idle. A no-op fit or
      // interrupted fit need not emit idle; waiting for it can strand the guard.
      map.stop();
      beginIgnoreMapSync();
      let released = false;
      const releaseFitSync = () => {
        if (released) return;
        released = true;
        map.off('moveend', releaseFitSync);
        endIgnoreMapSync();
      };
      map.once('moveend', releaseFitSync);
      try {
        map.fitBounds(bounds, {
        padding: {
          top: Math.max(8, rect.top),
          right: Math.max(8, mapNode.clientWidth - rect.left - rect.width),
          bottom: Math.max(8, mapNode.clientHeight - rect.top - rect.height),
          left: Math.max(8, rect.left)
        },
        duration: reducedMotion() ? 0 : 420,
        pitch: state.mapMode === '3d' ? last3dCamera.pitch : 0,
        bearing: 0
        });
      } catch (error) {
        releaseFitSync();
        throw error;
      }
      if (!map.isMoving()) releaseFitSync();
    }
  }

  function summaryMarkup(values) {
    return Object.entries(values)
      .filter(([label]) => !['Område', 'Retning'].includes(label))
      .map(([label, value]) => {
        const visibleLabel = label === 'Ramme' ? 'Billederamme' : label;
        const visibleValue = label === 'Ramme' && value === 'Uden ramme' ? 'Uden billederamme' : value;
        return `<div><dt>${escapeHtml(visibleLabel)}</dt><dd>${escapeHtml(visibleValue)}</dd></div>`;
      })
      .join('');
  }

  function escapeHtml(value) {
    const node = document.createElement('span');
    node.textContent = String(value);
    return node.innerHTML;
  }

  function syncOrderFields(values) {
    const g = godkendtUdsnit();
    const center = `${g.lat.toFixed(6)}, ${g.lon.toFixed(6)}`;
    const cornerNames = ['NV', 'NØ', 'SØ', 'SV'];
    const corners = makeSelection(g).corners
      .map(([lon, lat], index) => `${cornerNames[index]} ${lat.toFixed(6)}, ${lon.toFixed(6)}`)
      .join(' | ');
    const orderValues = {
      ...values,
      ...(currentDesignId ? { 'Design-ID': currentDesignId } : {}),
      'Centrum koordinater': center,
      Områdehjørner: corners
    };
    const fieldValues = {
      place: values.Sted,
      center,
      corners,
      area: values.Område,
      size: values.Størrelse,
      style: values.Udførelse,
      frame: values.Ramme,
      design: currentDesignId,
      message: Object.entries(orderValues).map(([label, value]) => `${label}: ${value}`).join('\n')
    };
    Object.entries(fieldValues).forEach(([key, value]) => {
      const field = $(`[data-order-field="${key}"]`);
      if (field) field.value = value;
    });
  }

  /* Moenstrene har ingen billeder i temaet -- de ligger i rammesidens
     sektionsindstillinger. Her tegnes de i stedet i kundens egen farve, hvilket
     ogsaa siger det, teksten ellers skulle: moenstret aendrer strukturen, ikke
     farven. */
  function moensterFlade(id, farve) {
    const linje = 'rgba(255,255,255,.32)';
    const flader = {
      rund: `radial-gradient(circle at 50% 0, ${linje} 0 2px, transparent 2px) 0 0/10px 10px`,
      trappet: `repeating-linear-gradient(90deg, ${linje} 0 2px, transparent 2px 7px)`,
      'indre-accent': `repeating-linear-gradient(180deg, ${linje} 0 1px, transparent 1px 9px)`,
      'fin-struktur': `repeating-linear-gradient(45deg, ${linje} 0 1px, transparent 1px 4px)`,
      aztec: `repeating-linear-gradient(135deg, ${linje} 0 3px, transparent 3px 9px)`,
      blomsterbort: `radial-gradient(circle at 50% 50%, ${linje} 0 3px, transparent 3px) 0 0/12px 12px`,
      reb: `repeating-linear-gradient(60deg, ${linje} 0 2px, transparent 2px 6px)`,
      hjerte: `radial-gradient(circle at 30% 40%, ${linje} 0 2px, transparent 2px) 0 0/9px 9px`
    };
    return `${flader[id] || flader.rund}, ${farve}`;
  }

  /* Kataloget grupperet, som rammesiden grupperer det. Familierne og deres
     navne kommer fra nf-ramme-katalog.liquid gennem motoren -- der opfindes
     ingen parallel inddeling her. */
  function rammeKategorier(mode) {
    const alle = (mode === 'pattern' ? motor().moenstre() : motor().proMaterialer())
      .map(item => ({ ...item, klar: surfaceReadiness[mode].has(item.id) }));
    const kategorier = window.nfFrameMaterialCategories;
    if (!kategorier) return [];
    return kategorier.group(alle).map(gruppe => ({
      id: gruppe.id,
      navn: gruppe.name,
      designs: gruppe.materials
    }));
  }

  const aktivKategori = () => (moensterValgt() ? ramme.moensterKategori : ramme.proKategori) || '';

  function saetKategori(id) {
    if (moensterValgt()) ramme.moensterKategori = id;
    else ramme.proKategori = id;
  }

  /* Har kunden allerede valgt et materiale i den aabne tilstand? Saa kan
     browseren foldes sammen til en linje. */
  const valgtDesign = () => {
    if (!medRamme()) return null;
    const id = overfladeId();
    if (!id || ramme.overflade === 'solid') return null;
    const liste = moensterValgt() ? motor().moenstre() : motor().proMaterialer();
    return liste.find(item => item.id === id) || null;
  };

  const sharedFrameControls = window.nfCityFrameConfigurator.create({
    root, ramme, surfaceReadiness, motor, medRamme, proValgt, rammeUdtryk,
    getSize: () => productSizeFor(state), surfaceChoiceReady, aktivtMateriale,
    opdaterRammeDetalje, escapeHtml
  });
  function renderRamme() { return sharedFrameControls.render(); }
  function renderProMaterials(...args) { return sharedFrameControls.renderProMaterials(...args); }
  function vaelgerBillede(...args) { return sharedFrameControls.vaelgerBillede(...args); }

  /* Rammens hjoerne, stort. Ét regnestykke, to steder: kortet under vaelgeren
     og indsatsen paa previewet. Laa de to hver sit sted, ville de kunne vise
     forskellige rammer paa samme skaerm.

     Rangfoelgen er den samme som alle andre steder i konfiguratoren:
     rammesidens hjoernefoto foerst -- der ER rammen -- ellers materialets egen
     flade eller moensterflisen ganget med kundens farve. */
  const HJOERNE_BREDDE = 480;
  function rammeHjoerne() {
    const m = motor();
    if (!m) return null;
    const farve = m.farve(ramme.farve);
    const mode = ramme.overflade;
    const design = valgtDesign();
    const kantPct = 28;
    const grund = {
      kantPct,
      designId: design?.id || '',
      farve: farve?.swatch || '#2d2a22',
      titel: design ? design.navn : 'Klassisk · Ensfarvet',
      note: '',
      flade: null,
      fladeType: 'solid',
      stoerrelse: 'cover',
      gentag: 'no-repeat'
    };
    if (!design) {
      return { ...grund, note: `Ensfarvet · ${farve?.navn || ''}`.trim() };
    }
    if (mode === 'pattern') {
      /* Præcis samme autoritative closeup-resolver som standalone-siden.
         Den tidligere parallelle SVG-prøve blev gentaget og ganget med farven;
         derfor kunne et mønster ligne et helt andet materiale her. */
      const closeup = m.materialeCloseup(design.id, HJOERNE_BREDDE);
      return {
        ...grund,
        flade: closeup.url,
        fladeType: closeup.url ? 'pattern' : 'solid',
        stoerrelse: closeup.size,
        gentag: closeup.repeat,
        blend: closeup.blend,
        kilde: closeup.source,
        note: `Mønster · ${design.familieNavn} · ${farve?.navn || ''}`
      };
    }
    const closeup = m.materialeCloseup(design.id, HJOERNE_BREDDE);
    return {
      ...grund,
      flade: closeup.url,
      fladeType: 'proMaterial',
      /* Baggrundsfarven er en bevidst fallback, hvis et eksternt materiale-
         thumbnail ikke kan indlæses. Så står valget aldrig som et tomt felt. */
      farve: grund.farve,
      stoerrelse: closeup.size,
      gentag: closeup.repeat,
      blend: closeup.blend,
      kilde: closeup.source,
      note: `Pro Material · ${design.familieNavn}`
    };
  }

  function saetHjoerne(node, detalje) {
    if (!node) return;
    node.style.setProperty('--nf-hjoerne-kant', `${detalje.kantPct}%`);
    node.style.setProperty('--nf-hjoerne-farve', detalje.farve);
    node.style.setProperty('--nf-hjoerne-flade', detalje.flade ? `url("${detalje.flade}")` : 'none');
    node.style.setProperty('--nf-hjoerne-stoerrelse', detalje.stoerrelse);
    node.style.setProperty('--nf-hjoerne-gentag', detalje.gentag);
    node.style.setProperty('--nf-hjoerne-blend', detalje.blend || 'normal');
    node.dataset.flade = detalje.fladeType;
    node.dataset.design = detalje.designId || '';
    node.dataset.closeupSource = detalje.kilde || '';
  }

  function opdaterRammeDetalje() {
    const kort = $('[data-frame-detail]');
    const indsats = $('[data-frame-inset]');
    const material = $('[data-frame-material-closeup]');
    const detalje = medRamme() ? rammeHjoerne() : null;
    if (kort) kort.hidden = !detalje;
    if (indsats) indsats.hidden = !detalje;
    if (material) material.hidden = !detalje?.flade;
    if (!detalje) return;
    saetHjoerne($('[data-frame-detail-corner]'), detalje);
    saetHjoerne($('[data-frame-inset-corner]'), detalje);
    saetHjoerne($('[data-frame-material-closeup-surface]'), detalje);
    $('[data-frame-detail-title]').textContent = detalje.titel;
    $('[data-frame-detail-note]').textContent = detalje.note;
    $('[data-frame-inset-label]').textContent = detalje.titel;
    $('[data-frame-material-closeup-title]').textContent = detalje.titel;
    $('[data-frame-material-closeup-note]').textContent = detalje.note;
    root.dataset.frameCloseupDesign = detalje.designId || '';
    root.dataset.frameCloseupSource = detalje.kilde || '';
  }

  function renderRammeBrowser() {
    const scopes = { pattern: {
      browser: $('[data-frame-pattern-browser]'),
      hoved: $('[data-frame-pattern-browser-head]'),
      kategorier: $('[data-frame-pattern-categories]'),
      materialer: $('[data-frame-pattern-materials]'),
      tilbage: $('[data-frame-pattern-back-label]'),
      antal: $('[data-frame-pattern-browser-count]'),
      valgt: $('[data-frame-pattern-chosen]')
    } };

    Object.values(scopes).forEach(scope => {
      scope.browser.hidden = true;
      scope.hoved.hidden = true;
      scope.kategorier.hidden = true;
      scope.materialer.hidden = true;
      scope.kategorier.innerHTML = '';
      scope.materialer.innerHTML = '';
      if (scope.valgt) scope.valgt.hidden = true;
    });

    const mode = ramme.overflade;
    if (mode !== 'pattern') return;
    const scope = scopes[mode];
    const design = valgtDesign();

    if (design && !ramme.browserAaben) {
      scope.valgt.hidden = false;
      const closeup = motor().materialeCloseup(design.id, 128);
      const flade = closeup.url;
      const thumb = $('[data-frame-pattern-chosen-thumb]');
      thumb.style.backgroundImage = flade ? `url("${flade}")` : 'none';
      thumb.style.backgroundColor = flade ? 'transparent' : (motor().farve(ramme.farve)?.swatch || '#2d2a22');
      thumb.dataset.flade = mode;
      thumb.dataset.closeupSource = closeup.source;
      $('[data-frame-pattern-chosen-title]').textContent =
        `${motor().stilarter().find(x => x.id === ramme.stil)?.navn || 'Standard'} · ${design.navn}`;
      $('[data-frame-pattern-chosen-note]').textContent =
        `Mønster · ${design.familieNavn} · ${motor().farve(ramme.farve)?.navn || ''}`;
      return;
    }
    scope.browser.hidden = false;
    const grupper = rammeKategorier(mode);
    const kategori = grupper.find(g => g.id === aktivKategori());

    if (!kategori) {
      scope.kategorier.hidden = false;
      scope.kategorier.innerHTML = grupper.map(g => {
        const forside = motor().familieForside(g.id, g.designs, false);
        const klar = g.designs.filter(item => item.klar).length;
        return `<button type="button" class="nf-frame-category" data-frame-category="${escapeHtml(g.id)}">`
          + `<span ${vaelgerBillede(forside, mode, 'nf-frame-category__thumb')}></span>`
          + `<strong>${escapeHtml(g.navn)}</strong><small>${klar} af ${g.designs.length} klar</small></button>`;
      }).join('');
      return;
    }

    scope.hoved.hidden = false;
    scope.tilbage.textContent = 'Alle mønstre';
    scope.antal.textContent = `${kategori.navn} · ${kategori.designs.length}`;
    scope.materialer.hidden = false;
    const attribut = 'pattern';
    scope.materialer.innerHTML = kategori.designs.map(materiale => {
      const aktiv = materiale.id === overfladeId();
      return `<button type="button" class="nf-frame-material${aktiv ? ' is-on' : ''}" data-frame-${attribut}="${escapeHtml(materiale.id)}"`
        + ` aria-pressed="${aktiv}"${materiale.klar ? '' : ' disabled aria-disabled="true"'}>`
        + `<span ${vaelgerBillede(materiale.id, mode)}></span>`
        + `<small>${escapeHtml(materiale.navn)}</small>`
        + (aktiv ? '<span class="nf-frame-choice-state">Valgt</span>' : '')
        + (!materiale.klar ? '<span class="nf-frame-choice-state nf-frame-choice-state--disabled">Ikke frigivet</span>' : '')
        + '</button>';
    }).join('');
  }

  function render(options = {}) {
    const values = summary();
    const total = totalPriceLabel();
    $('[data-area-label]').textContent = areaText();
    $('[data-selection-size]').textContent = areaText();
    $('[data-map-place]').textContent = state.place;
    $$(`[name^="nf-style-"]`).forEach(input => { input.checked = input.value === state.style; });
    /* Tillaegget skrives af kontrakten, ikke af markup'en. Aendrer beloebet
       sig ét sted, aendrer det sig ogsaa her. */
    $$('[data-style-price]').forEach(node => {
      const price22 = NF_CITY_COMMERCE.PRICE_CONTRACT.sizes['22'].base_minor;
      const tillaeg = (price22[node.dataset.stylePrice] ?? price22.hvid) - price22.hvid;
      node.textContent = tillaeg ? `+${(tillaeg / 100).toLocaleString('da-DK')} kr.` : 'Inkluderet';
    });
    $$(`[name^="nf-frame-"]`).forEach(input => { input.checked = input.value === state.frame; });
    $('[data-summary]').innerHTML = summaryMarkup(values);
    $$('[data-total-price]').forEach(node => { node.textContent = total; });
    $('[data-result-place]').textContent = approvedDesign?.place || 'Vælg dit udsnit';
    renderProductSize();
    $('[data-selection-rotation]').textContent = `${Math.round(state.rotationDeg)}°`;
    const rotationSlider = $('[data-selection-rotation-slider]');
    if (rotationSlider && Number(rotationSlider.value) !== Math.round(state.rotationDeg)) {
      rotationSlider.value = String(Math.round(state.rotationDeg));
    }
    layoutCropOverlay();
    $('[data-result-stage]').dataset.style = state.style;
    $('[data-result-stage]').dataset.frame = state.frame;
    /* Rammevalg maa gerne slaa igennem paa previewet med det samme -- det er
       kun kortudsnittet, der venter paa knappen. Egne attributnavne: hed de
       data-frame-color, ville previewet blive fanget af den vaelger, der finder
       farveknapperne, og talt med som en sjette farve. */
    const framePreview = framePreviewContract();
    const resultStage = $('[data-result-stage]');
    resultStage.dataset.previewFrameColor = framePreview.previewFrameColor;
    resultStage.dataset.previewFrameSurface = framePreview.previewFrameSurface;
    resultStage.dataset.previewFrameStyle = framePreview.previewFrameStyle;
    resultStage.dataset.previewFrameDesign = framePreview.previewFrameDesign;
    root.__nfCityFramePreview = framePreview;
    saetVaegRamme(resultStage);

    /* Knappen er stedet, kunden kan se, om previewet passer til det, hun
       kigger paa. Derfor tre tilstande og ikke to: aldrig vist, bagud, aktuel. */
    const knap = $('[data-apply-view]');
    const fuldskaermKnap = $('[data-fs-confirm]');
    const fuldskaermStatus = $('[data-fs-cta] p');
    const applyStatus = $('[data-apply-status]');
    const aktuel = erGodkendt();
    const requestMatchesCurrent = previewRequestFingerprint === selectionIntentFingerprint(state);
    const requestFailed = previewRequestState === 'error' && requestMatchesCurrent;
    const previewStale = !!godkendt && !aktuel;
    const progress = {
      fetch: { button: 'Henter kortdata …', status: 'Finder bygninger, veje, vand og grønne områder …', value: 1, label: 'Trin 1 af 7 · Henter kortdata' },
      classify: { button: 'Klassificerer kortet …', status: 'Klassificerer bygninger, veje, vand og grønne områder …', value: 2, label: 'Trin 2 af 7 · Klassificerer overflader' },
      assemble: { button: 'Samler kortdata …', status: 'Samler kortdata for hele det valgte udsnit …', value: 3, label: 'Trin 3 af 7 · Samler udsnittet' },
      model: { button: 'Bygger 3D-model …', status: 'Bygger din 3D-model fra det godkendte udsnit …', value: 4, label: 'Trin 4 af 7 · Bygger 3D-model' },
      materials: { button: 'Tilføjer materialer …', status: 'Tilføjer de valgte materialer til modellen …', value: 5, label: 'Trin 5 af 7 · Materialer' },
      prepare: { button: 'Klargør preview …', status: 'Klargør den fælles visning til 3D, kort og væg …', value: 6, label: 'Trin 6 af 7 · Klargør visninger' },
      ready: { button: 'Preview klar', status: 'Din model er klar.', value: 7, label: '7 af 7 trin færdige' }
    }[previewRequestPhase] || { button: 'Forbereder preview …', status: 'Forbereder forhåndsvisningen …', value: 0, label: 'Starter 3D-preview' };
    if (previewRequestBusy) knap.textContent = progress.button;
    else if (requestFailed) knap.textContent = 'Prøv igen';
    else knap.textContent = !godkendt
      ? 'Vælg kort'
      : (aktuel ? 'Kort valgt' : 'Vælg kort');
    knap.disabled = previewRequestBusy || aktuel;
    knap.setAttribute('aria-busy', String(previewRequestBusy));
    knap.classList.toggle('is-stale', previewStale || requestFailed);
    if (fuldskaermKnap) {
      fuldskaermKnap.textContent = 'Brug dette udsnit';
      fuldskaermKnap.disabled = false;
      fuldskaermKnap.setAttribute('aria-busy', 'false');
      fuldskaermKnap.classList.remove('is-stale');
      if (fuldskaermStatus) fuldskaermStatus.textContent = 'Fortsæt med udtryk og størrelse';
    }
    const applyBar = knap.closest('.nf-city-builder__apply');
    applyBar?.setAttribute('data-stale', String(previewStale || requestFailed));
    applyBar?.setAttribute('data-preview-request', previewRequestBusy ? 'loading' : (requestFailed ? 'error' : 'idle'));
    applyStatus.textContent = previewRequestBusy
      ? progress.status
      : requestFailed
        ? 'Vi kunne ikke hente kortdata lige nu. Prøv igen om et øjeblik.'
        : !godkendt
          ? 'Vælg kortudsnit, udtryk og størrelse, og afslut med “Vælg kort”.'
          : (aktuel
            ? 'Kortet er opdateret. Nye ændringer kræver, at du vælger kortet igen.'
            : 'Du har ændret din konfiguration. Tryk “Vælg kort” igen for at opdatere dit design.');
    root.dataset.previewDirty = String(previewStale || requestFailed);
    const loading = $('[data-product-loading]');
    const loadingText = $('[data-preview-progress-text]');
    const progressBar = $('[data-preview-progress]');
    const progressLabel = $('[data-preview-progress-label]');
    const previewGuide = $('[data-preview-guide]');
    const previewGuideTitle = $('[data-preview-guide-title]');
    const previewGuideCopy = $('[data-preview-guide-copy]');
    $$('.nf-city-builder__product-modes [data-result-view]').forEach(button => {
      button.disabled = !aktuel;
      button.setAttribute('aria-disabled', String(!aktuel));
    });
    loading.hidden = !previewRequestBusy && !requestFailed;
    previewGuide.hidden = previewRequestBusy || requestFailed || aktuel;
    if (!previewGuide.hidden) {
      previewGuideTitle.textContent = previewStale
        ? 'Opdatér kortet for at se den rigtige 3D-model'
        : 'Godkend dit kort for at bygge 3D-modellen';
      previewGuideCopy.textContent = previewStale
        ? 'Du har ændret kortudsnit, udtryk eller størrelse. Tryk “Vælg kort” igen.'
        : 'Tilpas kortudsnit, udtryk og størrelse. Tryk derefter “Vælg kort”.';
    }
    if (previewRequestBusy) {
      resultStage.dataset.productReady = 'false';
      loadingText.textContent = progress.status;
      progressBar.value = progress.value;
      progressLabel.textContent = progress.label;
    } else if (requestFailed) {
      resultStage.dataset.productReady = 'false';
      loadingText.textContent = 'Forhåndsvisningen kunne ikke bygges. Tryk “Prøv igen”.';
      progressBar.value = 0;
      progressLabel.textContent = 'Previewet stoppede før færdiggørelse';
    } else if (previewRequestPhase === 'ready') {
      loadingText.textContent = progress.status;
      progressBar.value = progress.value;
      progressLabel.textContent = progress.label;
    }

    const withFrame = medRamme();
    $('[data-frame-panel]').hidden = !withFrame;
    if (withFrame) renderRamme();
    /* Er rammen fravalgt, vender renderRamme om med det samme, og saa skal
       hjoernevisningen ryddes her -- ellers bliver den staaende. */
    if (!medRamme()) opdaterRammeDetalje();
    /* Bestillingen ligger samme sted uanset ramme. Der er ét produkt paa siden,
       og kunden skal ikke lede efter knappen, fordi hun valgte en ramme. */
    $('[data-direct-order]').hidden = false;
    // Uden ramme: kurven naar den er klar, ellers kontaktformularen.
    $('[data-cart-order]').hidden = !kurvKlar;
    cartButtons().forEach(button => {
      button.textContent = medRamme() ? 'Læg bykort og billederamme i kurv' : 'Læg i kurv';
    });
    root.classList.toggle('has-mobile-purchase', kurvKlar);
    syncMobilePurchaseVisibility();
    $('[data-contact-order]').hidden = kurvKlar;
    syncPurchaseReadiness();
    syncOrderFields(values);
    /* Et rent rammeskift maa ikke resize MapLibre-kortet: resize udloeser
       idle, som ellers bygger hele city-snapshot'et igen. */
    if (!options.frameOnly && !options.selectionOnly) scheduleResultPreview();
  }

  /* Vaeggens rammer viser kun en overflade, naar der findes et autoritativt
     preview-asset. Pro Materials har en frigivet sømløs flise. Dekorative mønstre
     beholder deres valg, pris og ordredata, men vises fail-closed med den
     valgte rammefarve, indtil et korrekt overflade-preview findes:

       Pro Material  -- materialeflisen, som daekker fladen og medbringer sin
                        egen palette. Farven bag traeder til side, praecis som
                        viseren saetter basefarven neutral.
       Moenster      -- neutral rammefarve; ingen simuleret/procedural tekstur.
       Ensfarvet     -- kun farven.

     Katalogets ``billedeFor`` bruges bevidst ikke her: det er salgsfotoet til
     vaelgerens swatch, ikke rammefladen. */
  function saetVaegRamme(stage) {
    const m = motor();
    const proValg = medRamme() && ramme.overflade === 'proMaterial' && !!aktivtMateriale();
    const flade = proValg ? (m?.materialeUrl(overfladeId()) || '') : '';
    const gentagelser = m?.MOENSTER_GENTAGELSER || 4;
    const farve = !medRamme() ? 'transparent'
      : (proValg && flade ? '#ffffff' : (m?.farve(ramme.farve)?.swatch || '#2d2a22'));
    stage.style.setProperty('--nf-vaeg-ramme', farve);
    stage.style.setProperty('--nf-vaeg-flade', flade ? `url("${flade}")` : 'none');
    stage.style.setProperty('--nf-vaeg-flade-gentag', 'repeat');
    stage.style.setProperty('--nf-vaeg-flade-stoerrelse', `${(100 / gentagelser).toFixed(3)}%`);
    stage.dataset.vaegRamme = medRamme() ? 'true' : 'false';
    stage.dataset.vaegFlade = flade ? ramme.overflade : 'solid';
    stage.dataset.vaegPatternFailClosed = medRamme() && ramme.overflade === 'pattern' ? 'true' : 'false';
  }

  /* Kun det færdige format er kundevendt. 44 cm bruger stadig den gennemtestede
     2 x 2-produktionsmodel, men felter og modulantal bliver internt. */
  function renderProductSize() {
    const selectedSize = productSizeFor(state);
    const selectedSummary = panoramaOpsummering(state);
    const previewState = godkendt
      ? { ...godkendt, panorama: previewPanorama() }
      : state;
    const previewSize = productSizeFor(previewState);
    const previewCoverage = areaText(previewState);

    $('[data-size-badge]').textContent = `${selectedSize} × ${selectedSize} cm`;
    $$('[data-product-size]').forEach(input => {
      input.checked = Number(input.value) === selectedSize;
    });
    root.classList.toggle('has-panorama', selectedSummary.count > 1);

    const resultSize = $('[data-result-panorama]');
    resultSize.hidden = false;
    resultSize.replaceChildren(`${previewSize} × ${previewSize} cm`);
    const note = document.createElement('small');
    note.textContent = `Kortudsnit ca. ${previewCoverage}`;
    resultSize.append(note);

    /* Kortet ovenfra, vægvisningen og 3D-modellen deler fortsat det samme
       committed snapshot. En genrendering må aldrig skifte aktiv visning. */
    const modelKnap = $('[data-model-label]');
    if (modelKnap) modelKnap.textContent = '3D-model';

    /* Status ligger på scenen, så 3D-viserens egen tekst ikke overskriver den. */
    const stage = $('[data-result-stage]');
    stage.dataset.panoramaStatus = `${previewSize} × ${previewSize} cm · kortudsnit ca. ${previewCoverage}`;
    /* Produktionsrammerne følger stadig det interne snapshot. */
    byggVaeg();
    if (stage.dataset.resultView === 'vaeg' && !resultSnapshotPending) opdaterVaeg();

    const fsMeta = $('[data-fs-meta]');
    if (fsMeta) fsMeta.textContent = `${selectedSize} × ${selectedSize} cm · ${areaText()}`;
  }

  /* ==========================================================================
     REALISTISK WALL-PRODUKTLAG

     Ét committed kortbillede beskæres hen over alle moduler. Den fælles
     container ejer den ene yderramme og kontaktskyggen; 1 px grid-gap viser
     kun de fysiske interne samlinger. Baggrundsfotoet er et separat fast lag.
     ========================================================================== */

  let vaegBilledeFingerprint = '';

  /* Rammerne bygges kun om, naar antallet aendrer sig. Ved 10 x 10 er det
     hundrede elementer, og at kassere dem ved hver optegning ville koste
     laesbar forsinkelse, hver gang kunden skiftede farve. */
  /* Scenen bruger altid samme kalibrerede kamera. Kun produktets procentmål
     ændres, så 44 x 44 er præcis dobbelt så stort som 22 x 22. */
  function opdaterVaegScene(columns, rows, frameFaceCm = 0) {
    const scene = $('[data-wall-scene]');
    const rum = window.nfVaegScene;
    if (!scene || !rum) return;
    const breddeCm = columns * MODUL_CM;
    const hoejdeCm = rows * MODUL_CM;
    const produkt = rum.product(breddeCm, hoejdeCm, frameFaceCm, frameFaceCm);
    const noegle = `${breddeCm}x${hoejdeCm}|${produkt.exteriorWidthCm}x${produkt.exteriorHeightCm}`;
    if (scene.dataset.scene === noegle) return;
    scene.dataset.scene = noegle;
    const v = rum.vindue(produkt.artworkWidthCm, produkt.artworkHeightCm);
    const background = rum.assetUrl(root);
    scene.style.setProperty('--nf-vaeg-scene', background ? `url("${background}")` : 'none');
    scene.style.setProperty('--nf-vaeg-scene-forhold', `${v.breddeCm} / ${v.hoejdeCm}`);
    scene.style.setProperty('--nf-vaeg-venstre', `${v.motivVenstrePct}%`);
    scene.style.setProperty('--nf-vaeg-top', `${v.motivTopPct}%`);
    scene.style.setProperty('--nf-vaeg-bredde', `${v.motivBreddePct}%`);
    scene.style.setProperty('--nf-vaeg-hoejde', `${v.motivHoejdePct}%`);
    scene.dataset.wallArtworkCm = `${produkt.artworkWidthCm}x${produkt.artworkHeightCm}`;
    scene.dataset.wallExteriorCm = `${produkt.exteriorWidthCm}x${produkt.exteriorHeightCm}`;
  }

  function byggVaeg() {
    const vaeg = $('[data-wall]');
    if (!vaeg) return null;
    const { columns, rows } = previewPanorama();
    const frameFaceCm = ramme.stil === 'minimal' ? 1.9 : 2.5;
    opdaterVaegScene(columns, rows, medRamme() ? frameFaceCm : 0);
    /* Værkets mål er selve 22 cm-modulerne. Den ene yderramme ligger udenom,
       så den aldrig gør 66 x 44-motivet mindre eller ændrer dets 3:2-forhold. */
    vaeg.style.setProperty('--nf-vaeg-ramme-x', `${(frameFaceCm / (columns * MODUL_CM) * 100).toFixed(3)}%`);
    vaeg.style.setProperty('--nf-vaeg-ramme-y', `${(frameFaceCm / (rows * MODUL_CM) * 100).toFixed(3)}%`);
    if (vaeg.dataset.kolonner !== String(columns) || vaeg.dataset.raekker !== String(rows)) {
      vaeg.dataset.kolonner = String(columns);
      vaeg.dataset.raekker = String(rows);
      vaeg.style.setProperty('--nf-vaeg-kolonner', String(columns));
      vaeg.style.setProperty('--nf-vaeg-raekker', String(rows));
      const dele = document.createDocumentFragment();
      const artwork = document.createElement('span');
      artwork.className = 'nf-city-builder__wall-artwork';
      artwork.setAttribute('aria-hidden', 'true');
      dele.append(artwork);
      for (let row = 0; row < rows; row += 1) {
        for (let column = 0; column < columns; column += 1) {
          const felt = document.createElement('div');
          felt.className = 'nf-city-builder__wall-item';
          felt.dataset.raekke = String(row);
          felt.dataset.kolonne = String(column);
          felt.dataset.indeks = String(row * columns + column);
          dele.append(felt);
        }
      }
      vaeg.replaceChildren(dele);
    }
    return vaeg;
  }

  function opdaterVaeg() {
    const vaeg = byggVaeg();
    if (!vaeg) return;
    const snapshot = root.__nfCityProductSnapshot;
    const fingerprint = productLayersFingerprint(godkendtUdsnit(), previewPanorama(), state.style);
    if (!erGodkendt() || !snapshot?.textureDataUrl || productCaptureFingerprint !== fingerprint) {
      vaeg.dataset.harBillede = 'false';
      vaeg.style.removeProperty('--nf-vaeg-billede');
      return;
    }
    if (fingerprint === vaegBilledeFingerprint && vaeg.dataset.harBillede === 'true') return;
    vaegBilledeFingerprint = fingerprint;
    vaeg.dataset.harBillede = 'true';
    vaeg.dataset.billedeLayout = `${previewPanorama().columns}x${previewPanorama().rows}`;
    vaeg.style.setProperty('--nf-vaeg-billede', `url("${snapshot.textureDataUrl}")`);
  }

  function saetResultatVisning(visning) {
    const stage = $('[data-result-stage]');
    const naeste = ['panorama', 'vaeg', 'model'].includes(visning) ? visning : 'model';
    stage.dataset.resultView = naeste;
    $$('.nf-city-builder__product-modes [data-result-view]').forEach(knap => {
      knap.setAttribute('aria-pressed', String(knap.dataset.resultView === naeste));
    });
    $('[data-wall-scene]').hidden = naeste !== 'vaeg';
    root.dispatchEvent(new CustomEvent('nf:city-preview-mode', { detail: { mode: naeste } }));
    if (naeste === 'vaeg') opdaterVaeg();
    opdaterRammeDetalje();
  }

  function setProductSize(value) {
    const choice = PUBLIC_SIZE_CHOICES[Number(value)] || PUBLIC_SIZE_CHOICES[22];
    update({
      areaM: choice.areaM,
      panorama: { columns: choice.columns, rows: choice.rows }
    }, { selection: true, fit: true });
    $('[data-search-status]').textContent = `${choice.sizeCm} × ${choice.sizeCm} cm viser ca. ${areaText()}.`;
  }

  function parseCoordinateQuery(query) {
    const value = String(query || '').trim();
    let parts = null;
    if (value.includes(';')) {
      const semicolonParts = value.split(';').map(part => part.trim()).filter(Boolean);
      if (semicolonParts.length === 2) parts = semicolonParts;
    } else {
      const whitespaceMatch = value.match(/^([+-]?\d{1,2}(?:[.,]\d+)?)\s+([+-]?\d{1,3}(?:[.,]\d+)?)$/);
      const commaMatch = value.match(/^([+-]?\d{1,2}(?:\.\d+)?)\s*,\s*([+-]?\d{1,3}(?:\.\d+)?)$/);
      const match = whitespaceMatch || commaMatch;
      if (match) parts = [match[1], match[2]];
    }
    if (!parts) return null;
    const lat = Number(parts[0].replace(',', '.'));
    const lon = Number(parts[1].replace(',', '.'));
    if (!Number.isFinite(lat) || !Number.isFinite(lon) || lat < -85 || lat > 85 || lon < -180 || lon > 180) return null;
    return { lat, lon };
  }

  const searchZone = $('[data-search-zone]');
  const searchForm = $('[data-search-form]');
  const searchInput = $('[data-search-input]');
  const searchResults = $('[data-search-results]');
  let searchPlaces = [];
  let activeSearchResult = -1;
  let searchZoneFocused = false;

  function syncMobilePurchaseVisibility() {
    const mobilePurchase = $('[data-mobile-purchase]');
    const searchActive = searchZoneFocused || !searchResults.hidden;
    const approvedAndIdle = erGodkendt() && !previewRequestBusy;
    if (mobilePurchase) mobilePurchase.hidden = !root.classList.contains('has-mobile-purchase')
      || searchActive || !approvedAndIdle;
    root.classList.toggle('is-search-active', searchActive);
  }

  function setActiveSearchResult(index) {
    const options = [...searchResults.querySelectorAll('[role="option"]')];
    activeSearchResult = options.length && index >= 0 ? (index + options.length) % options.length : -1;
    options.forEach((option, optionIndex) => {
      const active = optionIndex === activeSearchResult;
      option.setAttribute('aria-selected', String(active));
      if (active) option.scrollIntoView({ block: 'nearest' });
    });
    if (activeSearchResult >= 0) searchInput.setAttribute('aria-activedescendant', options[activeSearchResult].id);
    else searchInput.removeAttribute('aria-activedescendant');
  }

  function closeSearchResults() {
    searchResults.hidden = true;
    searchInput.setAttribute('aria-expanded', 'false');
    setActiveSearchResult(-1);
    syncMobilePurchaseVisibility();
  }

  function openSearchResults() {
    searchResults.hidden = false;
    searchInput.setAttribute('aria-expanded', 'true');
    setActiveSearchResult(-1);
    syncMobilePurchaseVisibility();
  }

  function chooseSearchResult(index) {
    const place = searchPlaces[index];
    if (!place) return;
    const label = place.label.split(',').slice(0, 2).join(',');
    update({ place: label, lat: place.lat, lon: place.lon }, { selection: true, fit: true });
    closeSearchResults();
    $('[data-search-status]').textContent = `${label} er valgt.`;
    publishCropSelected('search_result');
    searchInput.blur();
  }

  async function searchPlace(query) {
    const status = $('[data-search-status]');
    const submit = $('[data-search-form] button[type="submit"]');
    status.textContent = 'Søger…';
    closeSearchResults();
    searchPlaces = [];
    searchResults.replaceChildren();
    submit.disabled = true;
    submit.textContent = 'Søger…';

    async function fetchJson(url) {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 9000);
      try {
        const response = await fetch(url, { headers: { Accept: 'application/json' }, signal: controller.signal });
        if (!response.ok) throw new Error(`Geokodning svarede ${response.status}`);
        return await response.json();
      } finally {
        window.clearTimeout(timeout);
      }
    }

    try {
      let places = [];
      try {
        const nominatim = new URL('https://nominatim.openstreetmap.org/search');
        nominatim.searchParams.set('format', 'jsonv2');
        nominatim.searchParams.set('limit', '5');
        nominatim.searchParams.set('countrycodes', 'dk');
        nominatim.searchParams.set('accept-language', 'da');
        nominatim.searchParams.set('q', query);
        const payload = await fetchJson(nominatim);
        places = payload.map(place => ({
          label: place.display_name,
          lat: Number(place.lat),
          lon: Number(place.lon)
        }));
      } catch (primaryError) {
        places = [];
      }
      if (!places.length) {
        const photon = new URL('https://photon.komoot.io/api/');
        photon.searchParams.set('q', query);
        photon.searchParams.set('limit', '5');
        const payload = await fetchJson(photon);
        places = (payload.features || []).map(feature => {
          const properties = feature.properties || {};
          const coordinates = feature.geometry?.coordinates || [];
          const label = [properties.name, properties.street, properties.city, properties.country]
            .filter((value, index, values) => value && values.indexOf(value) === index)
            .join(', ');
          return { label, lat: Number(coordinates[1]), lon: Number(coordinates[0]) };
        });
      }
      places = places.filter(place => place.label && Number.isFinite(place.lat) && Number.isFinite(place.lon));
      if (!places.length) {
        closeSearchResults();
        status.textContent = 'Vi fandt ingen steder. Prøv en anden søgning.';
        return;
      }
      searchPlaces = places;
      places.forEach((place, index) => {
        const item = document.createElement('li');
        item.id = `${searchResults.id}-option-${index}`;
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');
        item.textContent = place.label;
        item.addEventListener('pointerdown', event => event.preventDefault());
        item.addEventListener('click', () => chooseSearchResult(index));
        searchResults.append(item);
      });
      openSearchResults();
      status.textContent = 'Vælg et resultat fra listen.';
    } catch (error) {
      closeSearchResults();
      status.textContent = 'Søgningen kunne ikke gennemføres. Prøv igen, eller flyt kortet manuelt.';
    } finally {
      submit.disabled = false;
      submit.textContent = 'Søg';
    }
  }

  searchForm.addEventListener('submit', event => {
    event.preventDefault();
    const query = $('[data-search-input]').value.trim();
    const coordinates = parseCoordinateQuery(query);
    if (coordinates) {
      const label = `Koordinater ${coordinates.lat.toFixed(6)}, ${coordinates.lon.toFixed(6)}`;
      update({ place: label, lat: coordinates.lat, lon: coordinates.lon }, { selection: true, fit: true });
      closeSearchResults();
      $('[data-search-status]').textContent = `${label} er valgt.`;
      publishCropSelected('coordinates');
      searchInput.blur();
    } else if (query) searchPlace(query);
    else {
      closeSearchResults();
      $('[data-search-status]').textContent = 'Skriv en by, adresse eller koordinater først.';
    }
  });

  ['pointerdown', 'mousedown', 'touchstart', 'click'].forEach(type => {
    searchZone.addEventListener(type, event => event.stopPropagation());
  });
  searchZone.addEventListener('keydown', event => {
    event.stopPropagation();
    if (event.key === 'Escape' && !searchResults.hidden) {
      event.preventDefault();
      closeSearchResults();
      return;
    }
    if (searchResults.hidden || !searchPlaces.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveSearchResult(activeSearchResult + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveSearchResult(activeSearchResult < 0 ? searchPlaces.length - 1 : activeSearchResult - 1);
    } else if (event.key === 'Enter' && activeSearchResult >= 0) {
      event.preventDefault();
      chooseSearchResult(activeSearchResult);
    }
  });
  searchZone.addEventListener('focusin', () => {
    searchZoneFocused = true;
    map?.keyboard?.disable();
    syncMobilePurchaseVisibility();
  });
  searchZone.addEventListener('focusout', () => {
    window.requestAnimationFrame(() => {
      searchZoneFocused = searchZone.contains(document.activeElement);
      syncMobilePurchaseVisibility();
    });
  });
  $('[data-use-view]')?.addEventListener('click', () => {
    syncSelectionFromMap();
    $('[data-search-status]').textContent = 'Den aktuelle kortvisning er valgt.';
    publishCropSelected('current_view');
  });
  $('[data-selection-center]').addEventListener('click', () => {
    if (!map) return;
    update({ lat: map.getCenter().lat, lon: map.getCenter().lng }, { selection: true, fit: true });
    $('[data-search-status]').textContent = 'Kortområdet er centreret i visningen.';
    publishCropSelected('center');
  });
  $('[data-selection-rotation-slider]')?.addEventListener('input', event => {
    if (selectionLocked) return;
    const rotationDeg = Number(event.currentTarget.value) || 0;
    update({ rotationDeg }, { selection: true });
    layoutCropOverlay();
  });
  $('[data-selection-rotation-slider]')?.addEventListener('change', () => {
    $('[data-search-status]').textContent = `Kortområdet er roteret til ${Math.round(state.rotationDeg)}°.`;
    publishCropSelected('rotate');
  });
  $('[data-selection-lock]').addEventListener('click', event => {
    selectionLocked = !selectionLocked;
    const button = event.currentTarget;
    button.setAttribute('aria-pressed', String(selectionLocked));
    button.setAttribute('aria-label', selectionLocked ? 'Lås kortområdet op' : 'Lås kortområdet');
    button.title = selectionLocked ? 'Lås kortområdet op' : 'Lås kortområdet';
    button.querySelector('[data-lock-open]').hidden = selectionLocked;
    button.querySelector('[data-lock-closed]').hidden = !selectionLocked;
    $('[data-selection-tools]').classList.toggle('is-locked', selectionLocked);
    if (map) {
      if (selectionLocked) {
        map.dragPan.disable();
        map.scrollZoom.disable();
        map.touchZoomRotate.disable();
      } else {
        map.dragPan.enable();
      }
    }
    $('[data-search-status]').textContent = selectionLocked
      ? 'Kortområdet er låst.'
      : 'Flyt kortet under rammen for at vælge udsnit.';
    setSelectionToolMode();
  });
  $$('[data-product-size]').forEach(input => input.addEventListener('change', () => {
    if (input.checked) setProductSize(input.value);
  }));
  $$(`[name^="nf-style-"]`).forEach(input => input.addEventListener('change', () => update({ style: input.value })));
  $$(`[name^="nf-frame-"]`).forEach(input => input.addEventListener('change', () => update({ frame: input.value })));
  $$('[data-map-mode]').forEach(button => button.addEventListener('click', () => applyMapMode(button.dataset.mapMode)));
  $('[data-apply-view]').addEventListener('click', async () => {
    const succeeded = await godkendUdsnit();
    // Paa mobil staar previewet under valgene, saa svaret skal hentes frem.
    if (succeeded && window.innerWidth <= 900) {
      $('[data-result-card]').scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
    }
  });
  const directOrderForm = root.querySelector('#nf-bykort-order-form');
  directOrderForm?.addEventListener('submit', async event => {
    if (approvedMapdataReason()) { event.preventDefault(); return; }
    if (!designApiBase || directOrderForm.dataset.designReady === 'true') return;
    event.preventDefault();
    const submit = directOrderForm.querySelector('button[type="submit"]');
    submit.disabled = true;
    try {
      if (!approvedDesign) throw new Error('Vælg kortet først');
      const snapshot = approvedOrderSnapshot();
      await saveDesign(snapshot);
      if (approvedMapdataReason() || designFingerprint(snapshot) !== designFingerprint(approvedDesign)) {
        throw new Error('Designet blev ændret. Vælg kortet igen.');
      }
      directOrderForm.dataset.designReady = 'true';
      syncOrderFields(summary());
      directOrderForm.submit();
    } catch (error) {
      submit.disabled = false;
    }
  });

  /* Ét sted at tage imod rammens klik. Overfladen skifter tilstand, men roerer
     aldrig ramme.farve -- den er kundens og skal overleve et Pro Material. */
  root.addEventListener('click', event => {
    const knap = event.target.closest('button[data-frame-color],'
      + ' button[data-frame-finish-mode], button[data-frame-surface-mode], button[data-frame-pattern],'
      + ' button[data-frame-pro], button[data-frame-category], button[data-frame-pro-category],'
      + ' button[data-frame-pro-back],'
      + ' button[data-frame-back], button[data-frame-skift], button[data-frame-pro-browse]');
    if (!knap || knap.disabled) return;
    const d = knap.dataset;
    const before = JSON.stringify(framePreviewContract());
    if (d.frameColor) ramme.farve = d.frameColor;
    if (d.frameFinishMode === 'classic') {
      ramme.overflade = 'solid';
      ramme.klassiskOverflade = 'solid';
      ramme.browserAaben = false;
    }
    if (d.frameFinishMode === 'pro') {
      if (!surfaceReadiness.proMaterial.size) return;
      ramme.overflade = 'proMaterial';
      ramme.browserAaben = false;
    }
    if (d.frameSurfaceMode !== undefined) {
      if (d.frameSurfaceMode !== 'solid' && !surfaceReadiness[d.frameSurfaceMode]?.size) return;
      ramme.overflade = d.frameSurfaceMode;
      ramme.klassiskOverflade = d.frameSurfaceMode;
      /* Skifter kunden overflade, aabner browseren paa kategorierne. Intet
         betalt materiale vaelges paa hendes vegne: listen staar aaben og
         uvalgt, og prisen aendrer sig foerst, naar hun selv vaelger. */
      ramme.browserAaben = d.frameSurfaceMode !== 'solid';
      saetKategori('');
    }
    if (d.frameProBrowse !== undefined) {
      if (!surfaceReadiness.proMaterial.size) return;
      ramme.overflade = 'proMaterial';
      ramme.browserAaben = true;
      ramme.proKategori = '';
    }
    if (d.frameProCategory !== undefined) {
      ramme.proKategori = d.frameProCategory;
      render({ frameOnly: true });
      return;
    }
    if (d.frameProBack !== undefined) {
      ramme.proKategori = '';
      render({ frameOnly: true });
      return;
    }
    /* Kategorien er kun navigation -- den aendrer hverken valg eller pris. */
    if (d.frameCategory !== undefined) { saetKategori(d.frameCategory); render({ frameOnly: true }); return; }
    if (d.frameBack !== undefined) { saetKategori(''); render({ frameOnly: true }); return; }
    if (d.frameSkift !== undefined) { ramme.browserAaben = true; render({ frameOnly: true }); return; }
    if (d.framePattern && surfaceChoiceReady('pattern', d.framePattern)) {
      ramme.moenster = d.framePattern; ramme.overflade = 'pattern'; ramme.klassiskOverflade = 'pattern'; ramme.browserAaben = false;
    }
    if (d.framePro && surfaceChoiceReady('proMaterial', d.framePro)) {
      ramme.proMateriale = d.framePro; ramme.overflade = 'proMaterial'; ramme.browserAaben = false;
    }
    const changed = JSON.stringify(framePreviewContract()) !== before;
    if (changed && !refreshApprovedProductChoices()) invalidateDesignApproval();
    render({ frameOnly: true });
    if (changed) publishFrameUpdate();
  });

  $('[data-frame-panel]').addEventListener('input', event => {
    if (!event.target.matches('[data-frame-pro-search]')) return;
    ramme.proSoegning = event.target.value;
    if (ramme.proSoegning.trim()) ramme.proKategori = '';
    const framePrice = NF_CITY_COMMERCE.PRICE_CONTRACT
      .sizes[String(productSizeFor(state))].frame_surcharge_minor;
    renderProMaterials(
      (framePrice.pro - framePrice.klassisk) / 100,
      surfaceReadiness.valid && surfaceReadiness.proMaterial.size > 0
    );
  });

  $$('.nf-city-builder__product-modes [data-result-view]').forEach(knap =>
    knap.addEventListener('click', () => saetResultatVisning(knap.dataset.resultView)));

  /* ==========================================================================
     FULDSKAERMS-KORTEDITOR PAA TELEFON

     Kortet flyttes ikke i DOM'en, og der oprettes ikke et nyt: kortkortet faar
     hele viewporten og lukkes igen paa plads. Derfor er der ét MapLibre-instans
     hele vejen -- ingen dobbelte lyttere, ingen ny runde tiles, og ingen tabt
     tilstand, naar kunden aabner og lukker.
     ========================================================================== */
  const mapCard = $('[data-map-card]');
  const FULDSKAERM_MAKS_PX = 767;
  const kanFuldskaerm = () => window.matchMedia(`(max-width: ${FULDSKAERM_MAKS_PX}px)`).matches;
  let fuldskaermAaben = false;
  let gemtScroll = 0;
  let aabnetAf = null;

  function fokuserbare() {
    return [...mapCard.querySelectorAll('button:not([disabled]), [href], input:not([disabled]),'
      + ' select:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter(node => !node.closest('[hidden]') && node.offsetParent !== null);
  }

  function fastholdFokus(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      lukKortEditor();
      return;
    }
    if (event.key !== 'Tab') return;
    const nodes = fokuserbare();
    if (!nodes.length) return;
    const foerste = nodes[0];
    const sidste = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === foerste) {
      event.preventDefault();
      sidste.focus();
    } else if (!event.shiftKey && document.activeElement === sidste) {
      event.preventDefault();
      foerste.focus();
    }
  }

  /* Baggrunden maa ikke rulle bag editoren, og scrollpositionen skal staa
     praecis hvor den stod, naar den lukkes igen. ``position: fixed`` paa body
     nulstiller ellers rulningen paa iOS. */
  function laasBaggrund(laas) {
    if (laas) {
      document.body.style.top = `-${gemtScroll}px`;
      document.body.classList.add('nf-city-scroll-laast', 'nf-city-editor-aaben');
    } else {
      document.body.classList.remove('nf-city-scroll-laast', 'nf-city-editor-aaben');
      document.body.style.top = '';
      /* Layoutet skal vaere regnet om, foer der rulles. Mens body stod fixed,
         var dokumentet kun én skaerm hoejt, og et ``scrollTo`` foer ombrydningen
         bliver klippet til 0 -- saa lander kunden oeverst paa siden i stedet
         for dér, hvor hun aabnede kortet. */
      void document.body.offsetHeight;
      window.scrollTo(0, gemtScroll);
    }
  }

  function aabnKortEditor() {
    if (fuldskaermAaben || !kanFuldskaerm()) return;
    /* Rulningen laeses foerst af alt. Saa snart kortkortet bliver ``fixed``,
       bliver dokumentet kortere, og browseren klipper rulningen til det nye
       maksimum -- laeser vi bagefter, er positionen allerede vaek. */
    gemtScroll = window.scrollY || window.pageYOffset || 0;
    // Opening, like closing, changes the crop container but not customer intent.
    beginIgnoreMapSync();
    fuldskaermAaben = true;
    aabnetAf = document.activeElement;
    root.classList.add('is-map-fullscreen');
    mapCard.setAttribute('role', 'dialog');
    mapCard.setAttribute('aria-modal', 'true');
    mapCard.setAttribute('aria-label', 'Vælg kortområde');
    $('[data-fs-bar]').hidden = false;
    $('[data-fs-cta]').hidden = false;
    $('[data-map-expand]').setAttribute('aria-expanded', 'true');
    laasBaggrund(true);
    mapCard.addEventListener('keydown', fastholdFokus);
    /* Kortet maalte sin container, foer den fyldte skaermen. Uden en resize
       her tegner MapLibre videre paa det gamle, lave maal. */
    window.requestAnimationFrame(() => {
      map?.resize();
      updateSelection(true);
      endIgnoreMapSync();
      $('[data-map-collapse]').focus({ preventScroll: true });
    });
  }

  function lukKortEditor(focusTarget = null) {
    if (!fuldskaermAaben) return;
    /* Fullscreen og inline-layoutet har forskellige crop-rammer. Naar klassen
       fjernes, kan MapLibre udsende resize/moveend, mens containeren skifter
       maal. Det er kun et layoutskift -- ikke et nyt kundevalg -- og maa
       derfor ikke skrive et nyt center ind i state lige efter en godkendt
       mobil-commit. Hold map-sync lukket gennem resize-frame og den efterfoelgende
       browser-frame, hvor ResizeObserver/moveend kan lande. */
    beginIgnoreMapSync();
    fuldskaermAaben = false;
    mapCard.removeEventListener('keydown', fastholdFokus);
    root.classList.remove('is-map-fullscreen');
    ['role', 'aria-modal', 'aria-label'].forEach(navn => mapCard.removeAttribute(navn));
    $('[data-fs-bar]').hidden = true;
    $('[data-fs-cta]').hidden = true;
    $('[data-map-expand]').setAttribute('aria-expanded', 'false');
    laasBaggrund(false);
    window.requestAnimationFrame(() => {
      map?.resize();
      updateSelection(false);
      endIgnoreMapSync();
      const tilbage = focusTarget || ((aabnetAf && root.contains(aabnetAf)) ? aabnetAf : $('[data-map-expand]'));
      tilbage?.focus?.({ preventScroll: true });
      if (focusTarget) focusTarget.closest('[data-configure-card]')?.scrollIntoView({
        behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start'
      });
    });
  }

  $('[data-map-expand]').addEventListener('click', aabnKortEditor);
  /* Overlayet er skjult for skaermlaesere: expand-knappen i hovedet er den
     tilgaengelige vej ind, og to knapper med samme formaal ville bare stoeje. */
  $('[data-map-open]').addEventListener('click', aabnKortEditor);
  $('[data-map-collapse]').addEventListener('click', () => lukKortEditor());
  $('[data-fs-confirm]').addEventListener('click', () => {
    /* Panorering synkroniseres allerede løbende på ``drag``/``moveend``, og
       søgning samt rotation skriver direkte i state. En ekstra kameralæsning
       her ville derfor kun kunne opsamle den minimale center-afvigelse, som
       MapLibre skaber, når fullscreen-layoutet indpasses. Det gjorde et helt
       uændret genbesøg dirty. Luk editoren uden at omskrive kundens arbejdskopi;
       den endelige “Vælg kort” binder derefter alle valg i ét snapshot. */
    scheduleResultPreview();
    lukKortEditor($('[data-configure-card] h2'));
  });
  /* Drejes telefonen til en bredde, hvor det almindelige layout kan vaere der,
     giver fuldskaerm ikke laengere mening. */
  window.addEventListener('resize', () => {
    if (fuldskaermAaben && !kanFuldskaerm()) lukKortEditor();
  });

  // Read-only bridge. Authentication and job writes belong exclusively to the Worker.
  if (internalProduction) root.readCommittedProductionConfig = () => {
    if (!godkendt || !approvedDesign) throw new Error('Klik Vælg kort først.');
    if (selectionFingerprint(godkendt) !== selectionFingerprint(state)
      || panoramaFingerprint(godkendt) !== panoramaFingerprint(state)) {
      throw new Error('Kortet er ændret. Klik Vælg kort igen.');
    }
    const snapshot = approvedOrderSnapshot();
    const tiles = snapshot.panorama.enabled ? snapshot.panorama.tiles : [null];
    const commerce = {
      contract_version: NF_CITY_COMMERCE.CONTRACT_VERSION,
      price_contract_version: NF_CITY_COMMERCE.PRICE_CONTRACT_VERSION,
      panorama: { schema: PANORAMA_SCHEMA, columns: snapshot.panorama.columns,
        rows: snapshot.panorama.rows, count: snapshot.panorama.count },
      map_count: snapshot.panorama.count
    };
    return { committed: true, designs: tiles.map(tile =>
      designPayload(snapshot, commerce, tile, newRequestId())) };
  };

  cartButtons().forEach(button => button.addEventListener('click', laegIKurv));

  render();
  validateSurfaceReadiness();
  initMap();
  if (!internalProduction) hentProdukt();
  /* Rammens pris-buckets hentes fra butikken, ikke fra temaet. Uden dem kan
     rammen ikke laegges i kurven, og knappen siger det i stedet for at fejle
     ved klik. */
  (async () => {
    if (internalProduction || !motor()) return;
    prisvarianter = await motor().hentPrisvarianter(PRIS_PRODUKTER);
    render();
  })();
  /* Kortnavigation er gratis; produktionsdata hentes først ved kundens
     eksplicitte previewklik. */
  publishFunnelEvent('nf_city_builder_viewed');
})();
